/* Phantom Display 2.1 - interactions + real cart. Self-contained, no deps. */
/* Shared connection-aware media flags. Computed once at top level so the main
   module AND the top-level prism hero IIFE can gate on them. */
window.PD_MEDIA = (function () {
  var REDUCE = !!(window.matchMedia && matchMedia("(prefers-reduced-motion: reduce)").matches);
  var CONN = navigator.connection || navigator.mozConnection || navigator.webkitConnection || null;
  var SLOW = !!(CONN && (CONN.saveData || ["slow-2g", "2g", "3g"].indexOf(CONN.effectiveType) >= 0));
  return { REDUCE: REDUCE, CONN: CONN, SLOW: SLOW };
})();
(function () {
  "use strict";
  // Contract A: mark JS active so "hide until JS" CSS rules apply only when JS runs.
  // Must stay the first executable statement; nothing above relies on the class.
  document.documentElement.classList.add("js");
  var $ = function (id) { return document.getElementById(id); };
  // 2026-07 currency-label fix: PD_CUR is the presentment currency injected by theme.liquid
  // (cart.currency.iso_code). USD keeps the exact legacy "$45"/"$45.00" strings; any other
  // currency formats via Intl so Danish sessions stop seeing "$550" for 550 DKK. Amounts
  // reaching these helpers are already major units (the /cart.js /100 division is correct
  // for JPY/KRW too: Shopify appends two subunit digits even for zero-decimal currencies).
  var PD_CUR = window.PD_CUR || "USD";
  var fmtMoney = function (n, trim) {
    var v = Math.round(n * 100) / 100;
    if (PD_CUR === "USD") return "$" + (trim && v % 1 === 0 ? v : v.toFixed(2));
    try {
      var o = { style: "currency", currency: PD_CUR };
      if (trim && v % 1 === 0) { o.minimumFractionDigits = 0; o.maximumFractionDigits = 0; }
      return new Intl.NumberFormat(undefined, o).format(v);
    } catch (e) { return v.toFixed(2) + " " + PD_CUR; }
  };
  var money = function (n) { return fmtMoney(n, false); };
  var moneyShort = function (n) { return fmtMoney(n, true); };
  // SEC-1: escape store-controlled strings before innerHTML interpolation (search results, cart rows,
  // undo toast). Defense-in-depth against a malicious/imported product title or image URL. Invisible
  // for real data: a literal & / quote in a title or CDN URL renders identically once entity-decoded.
  var ESC_MAP = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };
  var esc = function (s) { return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) { return ESC_MAP[c]; }); };

  /* ---------------- analytics event scaffold (P1.10) ----------------
     Pushes funnel + fit-engine events to a dataLayer and gtag if present.
     Real GA4 / Klaviyo / Meta IDs are wired in the <head> snippet later (decision P-6);
     until then this is a safe no-op that already captures every event. */
  window.dataLayer = window.dataLayer || [];
  /* Simon 2026-07-07: GA4-reserved ecommerce events the Shopify Google channel ALREADY
     sends; forwarding them through gtag here double-counted them (post-launch audit:
     2 add_to_cart hits per physical add). Keep them on the dataLayer only. Trimmed same
     day to ONLY the channel-verified names: GA4 data for 07-06 proved the channel does
     NOT send view_cart / remove_from_cart etc., so those names must pass through or
     theme-emitted cart-funnel events get silently swallowed. */
  var GA4_NATIVE_EVENTS = ["add_to_cart", "view_item", "begin_checkout", "purchase"];
  window.pdEvent = function (name, params) {
    try {
      window.dataLayer.push(Object.assign({ event: name, ts: Date.now() }, params || {}));
      if (GA4_NATIVE_EVENTS.indexOf(name) === -1 && typeof window.gtag === "function") window.gtag("event", name, params || {});
    } catch (e) {}
  };

  /* Buffered emitter (2026-07-31). pd.js is deferred, so it boots BEFORE the Shopify Google channel
     installs window.gtag (measured live: gtag undefined at boot on 7 of 7 loads, arriving 164-264 ms
     later) and gtag.js never turns a plain dataLayer push into a GA4 hit, so boot events are lost today.
     Queue until gtag exists then flush in order; shift-before-send = at most once; the per-item catch
     stops one bad send stranding the rest and falls back to a raw dataLayer push if window.pdEvent is
     overwritten or throws. Every send takes the queued path so that fallback always applies. First 5
     retries at 40 ms (a bounce off a dead end inside 250 ms is exactly the session this must not lose),
     then 250 ms, bounded. If gtag never arrives inside the budget the page LATCHES to dataLayer-only
     (_pdNoGtag) so numerator and denominator events fail together and rates stay unbiased. */
  var _pdQ = [], _pdT = null, _pdTries = 0, PD_Q_MAX = 24, _pdNoGtag = false;
  function pdFlush() {
    try {
      if (!_pdQ.length) return;
      if (typeof window.gtag !== "function" && !_pdNoGtag) {
        if (_pdTries < PD_Q_MAX) {
          if (_pdT == null) _pdT = setTimeout(function () { _pdT = null; _pdTries++; pdFlush(); }, _pdTries < 5 ? 40 : 250);
          return;
        }
        _pdNoGtag = true;
      }
      while (_pdQ.length) {
        var qi = _pdQ.shift();
        try {
          if (_pdNoGtag) { window.dataLayer.push(Object.assign({ event: qi.n, ts: Date.now() }, qi.p || {})); }
          else { window.pdEvent(qi.n, qi.p); }
        } catch (e2) { try { window.dataLayer.push(Object.assign({ event: qi.n, ts: Date.now() }, qi.p || {})); if (!_pdNoGtag && typeof window.gtag === "function") window.gtag("event", qi.n, qi.p || {}); } catch (e3) {} }
      }
    } catch (e) {}
  }
  function pdEventQ(name, params) {
    try { _pdQ.push({ n: name, p: params }); pdFlush(); } catch (e) {}
  }
  // Teardown is the last chance to send a queued event. pdFlush is idempotent and returns instantly when empty.
  try { document.addEventListener("visibilitychange", pdFlush); window.addEventListener("pagehide", pdFlush); } catch (e) {}

  /* ---------------- add-to-cart confirmation (wave3 item3a, Simon) ----------------
     The fly-to-cart flying-image animation was REMOVED everywhere (PDP, quick-add,
     cross-sell, quiz). The add confirmation is now a single subtle pulse on the
     cart-count badge (see pulseBadge, fired only on a real count increase and never
     under prefers-reduced-motion), plus the existing global toast. */

  /* ---------------- shared: scroll-lock (iOS-safe, nestable) + focus-trap ---------------- */
  var _scrollY = 0, _locks = 0;
  function lockScroll(on) {
    var b = document.body;
    if (on) {
      if (_locks++ === 0) {
        _scrollY = window.scrollY || window.pageYOffset || 0;
        b.style.position = "fixed"; b.style.top = (-_scrollY) + "px";
        b.style.left = "0"; b.style.right = "0"; b.style.width = "100%"; b.style.overflow = "hidden";
      }
    } else if (_locks > 0 && --_locks === 0) {
      b.style.position = ""; b.style.top = ""; b.style.left = ""; b.style.right = ""; b.style.width = ""; b.style.overflow = "";
      window.scrollTo(0, _scrollY);
    }
  }
  function trapFocus(container) {
    var SEL = 'a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])';
    function onKey(e) {
      if (e.key !== "Tab") return;
      var f = [].slice.call(container.querySelectorAll(SEL)).filter(function (el) { return el.offsetParent !== null; });
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    container.addEventListener("keydown", onKey);
    return function () { container.removeEventListener("keydown", onKey); };
  }

  /* ---------------- scroll reveal (fail-safe) ---------------- */
  (function () {
    var els = document.querySelectorAll(".pd-reveal");
    if (!els.length) return;
    if (matchMedia("(prefers-reduced-motion: reduce)").matches || !("IntersectionObserver" in window)) return;
    document.documentElement.classList.add("pd-anim");
    var io = new IntersectionObserver(function (ents) {
      ents.forEach(function (x) { if (x.isIntersecting) { x.target.classList.add("in"); io.unobserve(x.target); } });
    }, { threshold: 0.1, rootMargin: "0px 0px -6% 0px" });
    els.forEach(function (e) { io.observe(e); });
    setTimeout(function () { els.forEach(function (e) { e.classList.add("in"); }); }, 1600);
  })();

  /* ---------------- connection-aware media ---------------- */
  var REDUCE = window.PD_MEDIA.REDUCE, CONN = window.PD_MEDIA.CONN, SLOW = window.PD_MEDIA.SLOW;
  // AUDIT-24 W2 F21: pointer-driven press for the card quick-add + filter chips (iOS :active is
  // unreliable without touch listeners). MODULE scope on purpose - the facet IIFE early-returns on
  // pages without #pd-facets, which are exactly the pages where .pd-qadd lives. Capture phase so the
  // non-bubbling pointerleave still arrives. Class toggle only; never preventDefault.
  ["pointerdown", "pointerup", "pointercancel", "pointerleave"].forEach(function (t) {
    document.addEventListener(t, function (e) {
      var b = e.target && e.target.closest && e.target.closest(".pd-fchip, .pd-qadd");
      if (!b) return;
      if (t === "pointerleave" && e.target !== b) return;   /* round-2 fix: a leave of a CHILD (icon/text span) is not a leave of the button */
      b.classList.toggle("is-press", t === "pointerdown");
    }, true);
  });
  // AUDIT-24 W2 F16: FLIP the collection grid so filter/sort mutations glide instead of teleporting.
  // Survivors glide (220ms), arrivals fade in (180ms), leavers stay INSTANT on purpose - deferring the
  // .pd-hide class desyncs syncDividers/recount and races rapid chip taps (verifier ruling). Module
  // scope: the sort handler also runs on non-faceted grids where the facet IIFE early-returns. WAAPI
  // easing cannot read CSS vars; the literal IS --ease-out's value.
  function pdFlipGrid(els, mutate) {
    if ((window.PD_MEDIA && window.PD_MEDIA.REDUCE) || !els || !els.length || !els[0].animate) { mutate(); return; }
    els = els.slice();   // round-1 fix: snapshot - a mutate() that sorts the caller's array in place would desync first[i] from els[i]
    els.forEach(function (el) {   // round-1 fix: a second filter/sort inside 220ms must not measure mid-animation positions
      try { el.getAnimations().forEach(function (a) { a.finish(); }); } catch (err) {}
    });
    var first = els.map(function (el) { return el.getBoundingClientRect(); });
    mutate();
    els.forEach(function (el, i) {
      var a = first[i], b = el.getBoundingClientRect();
      if (!b.width) return;
      if (!a.width) { el.animate([{ opacity: 0, transform: "scale(.97)" }, { opacity: 1, transform: "none" }], { duration: 180, easing: "cubic-bezier(.16,1,.3,1)" }); return; }
      var dx = a.left - b.left, dy = a.top - b.top;
      if (!dx && !dy) return;
      el.animate([{ transform: "translate(" + dx + "px," + dy + "px)" }, { transform: "none" }], { duration: 220, easing: "cubic-bezier(.16,1,.3,1)" });
    });
  }
  var MAY_AUTOPLAY = !REDUCE && !SLOW;

  /* W8 code-audit M10: dead homepage-hero-video + gallery/thumbs/dots/zoom IIFEs removed.
     Their selectors (#pd-hero-vid, #pd-hero-img, .pd-gal__ths) do not exist in v2 (the PDP
     hero is the #pd-prismhero 360 viewer), so the gallery IIFE dead-returned and the #pd-zoom
     lightbox could never open. ~80 lines removed. */

  /* ---------------- accordions ---------------- */
  var _accN = 0;
  document.querySelectorAll(".pd-acc__b").forEach(function (b) {
    var item = b.parentElement, c = item.querySelector(".pd-acc__c");
    // a11y: wire button<->panel and keep collapsed panels out of the tab + a11y tree (WCAG 1.3.1/4.1.2)
    var on = item.classList.contains("on");
    if (c && !c.id) { c.id = "pd-acc-p" + (++_accN); }
    if (c) { b.setAttribute("aria-controls", c.id); if (!on) c.setAttribute("inert", ""); }
    b.setAttribute("aria-expanded", on ? "true" : "false");
    b.addEventListener("click", function () {
      var open = item.classList.contains("on");
      item.parentElement.querySelectorAll(".pd-acc__i").forEach(function (x) {
        x.classList.remove("on");
        var xc = x.querySelector(".pd-acc__c");
        if (xc) { xc.style.maxHeight = null; xc.setAttribute("inert", ""); }
        x.querySelector(".pd-acc__b").setAttribute("aria-expanded", "false");
      });
      if (!open) { item.classList.add("on"); c.style.maxHeight = c.scrollHeight + "px"; c.removeAttribute("inert"); b.setAttribute("aria-expanded", "true"); }
    });
    if (on && c) { c.style.maxHeight = c.scrollHeight + "px"; }
  });

  /* ---------------- hamburger / mobile nav ---------------- */
  (function () {
    var burger = $("pd-burger"), nav = $("pd-mobnav"), x = $("pd-mobnav-x");
    if (!burger || !nav) return;
    burger.setAttribute("aria-expanded", "false");
    var _navUntrap = null;
    var panel = nav.querySelector(".pd-mobnav__panel");
    var curView = "top", _mnavIn = 0, _mnavOut = 0;   /* AUDIT-24 W2 F41: the old `animating` lockout swallowed every tap for ~370ms; transitions are now interruptible (a new tap re-targets) */
    function resetView() {
      var vs = nav.querySelectorAll(".pd-mview");
      for (var i = 0; i < vs.length; i++) { vs[i].classList.remove("pd-mview--active"); vs[i].style.cssText = ""; }
      var t = nav.querySelector("#pd-mview-top"); if (t) t.classList.add("pd-mview--active");
      curView = "top"; if (panel) panel.scrollTop = 0;
    }
    function showView(id, dir) {
      if (id === curView) return;
      var curEl = nav.querySelector("#pd-mview-" + curView), tgt = nav.querySelector("#pd-mview-" + id);
      if (!tgt) return;
      // #18 (Simon 2026-07-02): reduced-motion users get an instant switch - no chained 170ms + 200ms timeouts
      // (which otherwise silently drop taps in the ~370ms animating window). The REDUCE flag was never consulted here.
      if (window.matchMedia && matchMedia("(prefers-reduced-motion: reduce)").matches) {
        if (curEl) { curEl.classList.remove("pd-mview--active"); curEl.style.cssText = ""; }
        tgt.style.cssText = ""; tgt.classList.add("pd-mview--active");
        if (panel) panel.scrollTop = 0;
        curView = id;
        var _ff = (id === "top") ? nav.querySelector("#pd-mview-top button, #pd-mview-top a") : tgt.querySelector("[data-mnav], button, a");
        if (_ff && _ff.focus) { try { _ff.focus(); } catch (e) {} }
        return;
      }
      // AUDIT-24 W2 F41: interruptible sequential push. No lockout flag: a tap mid-transition cancels the
      // pending timers, finalizes whatever was on screen, and re-targets - nothing is ever swallowed.
      // 140ms out + 140ms in (280ms total, was 350ms + a 370ms input lockout). Views stay in normal flow
      // (absolute stacking would collapse the panel's scroll height and flash the logo - verifier ruling).
      clearTimeout(_mnavIn); clearTimeout(_mnavOut);
      [].slice.call(nav.querySelectorAll(".pd-mview")).forEach(function (v) {
        v.style.transition = ""; v.style.transform = ""; v.style.opacity = "";
      });
      var inX = dir === "fwd" ? "22px" : "-22px", outX = dir === "fwd" ? "-22px" : "22px";
      var outEl = nav.querySelector(".pd-mview--active");
      if (outEl && outEl !== tgt) {
        outEl.style.transition = "transform .14s var(--ease-out),opacity .14s var(--ease-out)";
        outEl.style.transform = "translateX(" + outX + ")"; outEl.style.opacity = "0";
      }
      curView = id;
      _mnavIn = setTimeout(function () {
        [].slice.call(nav.querySelectorAll(".pd-mview--active")).forEach(function (v) {
          v.classList.remove("pd-mview--active"); v.style.transition = ""; v.style.transform = ""; v.style.opacity = "";
        });
        tgt.style.transform = "translateX(" + inX + ")"; tgt.style.opacity = "0"; tgt.classList.add("pd-mview--active");
        if (panel) panel.scrollTop = 0;
        void tgt.offsetHeight;
        tgt.style.transition = "transform .14s var(--ease-out),opacity .14s var(--ease-out)"; tgt.style.transform = "translateX(0)"; tgt.style.opacity = "1";
        // a11y: move focus into the now-active view (back button / first focusable in a sub-view;
        // first top item when returning to top) so SR + keyboard users are not orphaned on a hidden node.
        var ff = (id === "top")
          ? nav.querySelector("#pd-mview-top button, #pd-mview-top a")
          : tgt.querySelector("[data-mnav], button, a");
        if (ff && ff.focus) { try { ff.focus(); } catch (e) {} }
        _mnavOut = setTimeout(function () { tgt.style.transition = ""; tgt.style.transform = ""; tgt.style.opacity = ""; }, 160);
      }, 140);
    }
    var open = function () { resetView(); nav.inert = false; nav.classList.add("open"); nav.setAttribute("aria-hidden", "false"); burger.setAttribute("aria-expanded", "true"); lockScroll(true); var _mc = document.getElementById("MainContent"); if (_mc) _mc.inert = true; var f = nav.querySelector("#pd-mview-top button, #pd-mview-top a"); if (f) f.focus(); if (panel) _navUntrap = trapFocus(panel); };
    var close = function () { if (!nav.classList.contains("open")) return; clearTimeout(_mnavIn); clearTimeout(_mnavOut); nav.inert = true; nav.classList.remove("open");   /* round-1 fix: a drill mount firing into a closing drawer stole focus + desynced curView; inert = no focusable content inside aria-hidden during the 300ms exit */ nav.setAttribute("aria-hidden", "true"); burger.setAttribute("aria-expanded", "false"); lockScroll(false); var _mci = document.getElementById("MainContent"); if (_mci) _mci.inert = false; if (_navUntrap) { _navUntrap(); _navUntrap = null; } if (burger.focus) burger.focus(); };
    burger.addEventListener("click", open);
    if (x) x.addEventListener("click", close);
    nav.addEventListener("click", function (e) { if (e.target === nav) close(); });
    document.addEventListener("keydown", function (e) { if (e.key !== "Escape") return; if (curView !== "top" && nav.classList.contains("open")) showView("top", "back"); else close(); });
    // 2-level drill-down: category cards + back buttons (data-mnav), product rows (data-mlink)
    nav.querySelectorAll("[data-mnav]").forEach(function (el) {
      el.addEventListener("click", function (e) { e.preventDefault(); var t = el.getAttribute("data-mnav"); showView(t, t === "top" ? "back" : "fwd"); });
    });
    // #18 (Simon 2026-07-02): the rows are real <a href>, so let native navigation run - immediate, and
    // cmd/ctrl/shift/middle-click opens a new tab (the old preventDefault + 180ms timer added latency and broke
    // new-tab). Just close the drawer alongside on a plain click; leave modifier-clicks entirely to the browser.
    nav.querySelectorAll("[data-mlink]").forEach(function (row) {
      row.addEventListener("click", function (e) { if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return; close(); });
    });
  })();

  /* ---------------- faceted displays filter ---------------- */
  (function () {
    var grid = $("pd-filter-grid"), panel = $("pd-facets"), trendStrip = $("pd-trending");
    if (!grid || !panel) return;
    var cards = [].slice.call(grid.querySelectorAll(".pd-card"));
    // Simon 2026-07-05: the "Trending displays" strip (outside the filter panel) shares the same facet
    // engine via data-facet="trending". Iterate its chip groups alongside the panel's.
    function fgroups() { var g = [].slice.call(panel.querySelectorAll(".pd-fchips")); if (trendStrip) { g = g.concat([].slice.call(trendStrip.querySelectorAll(".pd-fchips"))); } return g; }
    var facetToggle = $("pd-facet-toggle"), facetToggleN = facetToggle ? facetToggle.querySelector(".pd-facet-toggle__n") : null, instock = $("pd-facet-instock");
    var ev = function (n, p) { if (window.pdEvent) window.pdEvent(n, p); };
    var state = { grader: [], bumper: [], mount: [], thick: [], trending: [] };
    function facetPass(c, facet, arr) {
      if (!arr.length) return true;
      if (facet === "grader") { var cg = (c.dataset.graders || "").split(" "); return arr.some(function (g) { return g.split("|").some(function (gg) { return cg.indexOf(gg) >= 0; }); }); }
      if (facet === "bumper") return arr.indexOf(c.dataset.bumper) >= 0;
      if (facet === "mount") return arr.indexOf(c.dataset.mount) >= 0;
      if (facet === "thick") { var ct = c.dataset.thick; return arr.some(function (t) { return ct === t || ct === "both"; }); }
      if (facet === "trending") { var cn = (c.dataset.trending || "").split(" "); return arr.some(function (t) { return cn.indexOf(t) >= 0; }); }
      return true;
    }
    function inStockOk(c) { return !(instock && instock.checked) || c.dataset.avail === "1"; }
    function match(c) {
      if (!inStockOk(c)) return false;
      for (var f in state) { if (!facetPass(c, f, state[f])) return false; }
      return true;
    }
    // P8: how many cards would match if `val` were selected for `facet` (other facets held) -> live counts + disable empty combos
    function countFor(facet, val) {
      return cards.filter(function (c) {
        if (!inStockOk(c)) return false;
        for (var f in state) { if (f !== facet && !facetPass(c, f, state[f])) return false; }
        return facetPass(c, facet, [val]);
      }).length;
    }
    function recount() {
      fgroups().forEach(function (grp) {
        var facet = grp.dataset.facet;
        grp.querySelectorAll(".pd-fchip").forEach(function (b) {
          // A2: capture the base label ONCE before the count span exists, so the accessible name
          // does not read "PSA11". The count span is aria-hidden and we name the chip explicitly.
          if (b.dataset.label == null) b.dataset.label = (b.textContent || "").trim();
          var n = countFor(facet, b.dataset.v), ns = b.querySelector(".pd-fchip__n");
          if (!ns) { ns = document.createElement("span"); ns.className = "pd-fchip__n"; ns.setAttribute("aria-hidden", "true"); b.appendChild(ns); }
          ns.textContent = n;
          b.setAttribute("aria-label", b.dataset.label + ", " + n + (n === 1 ? " result" : " results"));
          var zero = n === 0 && !b.classList.contains("on");
          b.classList.toggle("pd-fchip--zero", zero); b.disabled = zero;
        });
      });
    }
    var rescue = null;
    function showRescue(show) {
      if (show && !rescue) {
        rescue = document.createElement("div");
        rescue.className = "pd-facet-zero";
        rescue.innerHTML = 'Nothing matches every filter yet. <a href="/pages/find-your-display">Match my exact slab</a> or <button type="button" class="pd-facet-zero__clr">clear filters</button>.';
        grid.parentNode.insertBefore(rescue, grid.nextSibling);
        var b = rescue.querySelector("button"); if (b) b.addEventListener("click", function () { var cl = $("pd-facet-clear"); if (cl) cl.click(); });
      }
      if (rescue) rescue.hidden = !show;
    }
    // W8-QA10 item4b: hide a display-option divider when its group has no visible card (filtered out), so a
    // lonely "Display options: Desk + Wall" header never floats above an empty group.
    var dividers = [].slice.call(grid.querySelectorAll(".pd-grp[data-grpdiv]"));
    function syncDividers() {
      dividers.forEach(function (d) {
        var g = d.getAttribute("data-grpdiv");
        var any = cards.some(function (c) { return c.dataset.grp === g && !c.classList.contains("pd-hide"); });
        d.classList.toggle("pd-hide", !any);
      });
    }
    function apply() {
      var n = 0;
      var active = state.grader.length + state.bumper.length + state.mount.length + state.thick.length + state.trending.length + (instock && instock.checked ? 1 : 0);
      pdFlipGrid(cards, function () {   /* AUDIT-24 W2 F16: dividers + rescue flip INSIDE the measured mutation - they reflow the grid, so leaving them outside skewed every survivor's origin */
        cards.forEach(function (c) { var m = match(c); c.classList.toggle("pd-hide", !m); if (m) n++; });
        syncDividers();
        showRescue(active > 0 && n === 0);
      });
      // Simon 2026-07-02 (code-perf audit #11): the mobile grid gave no signal that filters were active (the
      // #pd-facet-scope line was dead - its mount node never existed). Surface the active-filter count on the
      // Filter toggle instead (mobile-only; desktop shows the always-open sidebar).
      if (facetToggleN) facetToggleN.textContent = active > 0 ? active : "";
      if (facetToggle) {
        facetToggle.classList.toggle("pd-facet-toggle--active", active > 0);
        facetToggle.setAttribute("aria-label", active > 0 ? ("Filter displays, " + active + " active") : "Filter displays");
      }
      recount();
    }
    fgroups().forEach(function (grp) {
      var facet = grp.dataset.facet;
      grp.querySelectorAll(".pd-fchip").forEach(function (b) {
        b.setAttribute("aria-pressed", "false");   // A2: chips are toggle buttons; expose pressed state to AT
        b.addEventListener("click", function () {
          var v = b.dataset.v, arr = state[facet], i = arr.indexOf(v);
          if (facet === "trending") {
            // Simon 2026-07-05: Trending pills are single-select - picking one clears any prior pick
            // (re-clicking the active pill clears it and shows everything). They never combine.
            var wasOn = i >= 0;
            state.trending = [];
            grp.querySelectorAll(".pd-fchip.on").forEach(function (o) { o.classList.remove("on"); o.setAttribute("aria-pressed", "false"); });
            if (!wasOn) { state.trending = [v]; b.classList.add("on"); b.setAttribute("aria-pressed", "true"); ev("facet_select", { facet: facet, value: v }); }
            apply();
            return;
          }
          if (i >= 0) { arr.splice(i, 1); b.classList.remove("on"); b.setAttribute("aria-pressed", "false"); }
          else { arr.push(v); b.classList.add("on"); b.setAttribute("aria-pressed", "true"); ev("facet_select", { facet: facet, value: v }); }
          apply();
        });
      });
    });
    if (instock) instock.addEventListener("change", apply);
    var clear = $("pd-facet-clear");
    if (clear) clear.addEventListener("click", function () {
      state = { grader: [], bumper: [], mount: [], thick: [], trending: [] };
      fgroups().forEach(function (grp) { grp.querySelectorAll(".pd-fchip.on").forEach(function (b) { b.classList.remove("on"); b.setAttribute("aria-pressed", "false"); }); });
      if (instock) instock.checked = false;
      apply();
    });
    var toggle = $("pd-facet-toggle"), close = $("pd-facet-close");
    var _facetReturn = null, _facetUntrap = null;
    // A1: the facet panel is a visible sticky sidebar at >760px but a closed slide-up sheet at
    // <=760px. aria-hidden must be true ONLY while the mobile sheet is closed; on desktop (panel
    // visible) or when the sheet is open, the 15 controls must be exposed to AT. Manage it
    // responsively here instead of baking aria-hidden="true" (which hid the whole filter feature
    // from screen readers on the dominant shopping viewport). No visual change: aria-hidden is
    // never a CSS selector in this theme; visibility is driven entirely by the .open class + CSS.
    var facetMQ = window.matchMedia ? matchMedia("(max-width:760px)") : null;
    function syncFacetsAria() {
      var sheetMode = facetMQ ? facetMQ.matches : false;
      var hidden = sheetMode && !panel.classList.contains("open");
      if (hidden) panel.setAttribute("aria-hidden", "true");
      else panel.removeAttribute("aria-hidden");
      panel.inert = hidden;   /* round-2 fix: inert must track aria-hidden from INIT and MQ flips, not only after a close - the closed sheet has no visibility guard, so its 15 controls were tabbable inside aria-hidden on first load */
    }
    // #20 (Simon 2026-07-02): scrim behind the mobile filter sheet. Was a bare sheet - no dimming, and a tap on a
    // card peeking above it clicked through and discarded the client-side filter state (a modality violation).
    // Mirrors the certified cart/nav backdrop: dim + close-on-tap + MainContent inert.
    var _facetScrim = null, _facetHome = null, _facetRestore = null, _facetRestoreT = 0;
    function facetScrim() {
      if (_facetScrim) return _facetScrim;
      _facetScrim = document.createElement("div");
      _facetScrim.className = "pd-facet-scrim";
      _facetScrim.addEventListener("click", closeP);
      (panel.parentNode || document.body).appendChild(_facetScrim);
      return _facetScrim;
    }
    function openP() {
      // Simon 2026-07-05: in sheet mode, portal the panel + scrim to <body> so the taps land. The
      // scroll-lock sets body{position:fixed}, which collapses the <html> box; a fixed panel nested
      // deep inside (.pd-collbody > .pd-wrap > section > main) then becomes un-hit-testable - every
      // tap on a filter chip falls through to <body> and nothing is clickable. The mobile nav + cart
      // drawer work because they are body-level, so we match them and restore the panel on close.
      if (facetMQ && facetMQ.matches) {
        if (!_facetHome) _facetHome = { parent: panel.parentNode, next: panel.nextSibling };
        if (_facetRestore) { clearTimeout(_facetRestoreT); panel.removeEventListener("transitionend", _facetRestore); _facetRestore = null; }   // cancel a pending close-restore: this open owns the panel
        if (panel.parentNode !== document.body) {   /* round-2 fix: re-appending a connected node cancels its transition - an already-portalled panel (reopen mid-exit) reverses in place instead */
          document.body.appendChild(panel);
          void panel.offsetWidth;   /* commit the reparented translateY(100%) start value, or the slide-up never plays */
        }
        var _sc = facetScrim();
        if (_sc.parentNode !== document.body) {   /* round-3 fix: same reopen-mid-exit guard as the panel - re-appending the connected scrim cancelled its fade (proven via CDP probe); own flush so the fresh-open fade never depends on lockScroll's incidental scrollY read */
          document.body.appendChild(_sc);
          void _sc.offsetWidth;
        }
      }
      panel.inert = false;
      panel.classList.add("open"); syncFacetsAria(); if (toggle) toggle.setAttribute("aria-expanded", "true"); lockScroll(true);
      facetScrim().classList.add("open");
      var _mc = document.getElementById("MainContent"); if (_mc) _mc.inert = true;
      // A3: the mobile sheet is a scroll-locked modal; trap focus, remember the opener, and move
      // focus into the sheet, matching the certified cart/search/mobnav drawers.
      _facetReturn = document.activeElement;
      if (close) setTimeout(function () { close.focus(); }, 60);
      _facetUntrap = trapFocus(panel);
    }
    function closeP() {
      if (facetMQ && facetMQ.matches) panel.inert = true;   /* sheet mode only: the desktop sidebar must never be inert (cleared on open + on any MQ flip) */
      panel.classList.remove("open"); syncFacetsAria(); if (toggle) toggle.setAttribute("aria-expanded", "false");
      if (_facetScrim) _facetScrim.classList.remove("open");
      // AUDIT-24 W2 F18: the exit must actually play. Reparenting in the same tick cancelled the slide-down
      // (zero transition events, proven live), so in sheet mode the DOM restore + scroll unlock now wait for
      // the transition (transitionend + 380ms fallback so the panel can never strand orphaned). A reopen
      // inside the window aborts the restore (or an OPEN panel would be reparented back into the grid and
      // the page unlocked under a modal). Accessibility state stays synchronous below.
      // Round-1 blocker fix: lockScroll is REFCOUNTED and openP() always increments, so the unlock must
      // be SYNCHRONOUS (deferring it leaked a count on reopen-mid-exit and froze the page; the sheet is
      // position:fixed, so no reflow is visible under it). Only the DOM restore is deferred, exactly-once,
      // and openP() cancels it.
      lockScroll(false);
      var _rhDone = false;
      var restoreHome = function (e) {
        if (e && (e.target !== panel || e.propertyName !== "transform")) return;
        if (_rhDone) return; _rhDone = true;
        clearTimeout(_facetRestoreT);
        panel.removeEventListener("transitionend", restoreHome);
        _facetRestore = null;
        if (panel.classList.contains("open")) return;   // reopened mid-exit: openP owns the panel again
        if (_facetHome && _facetHome.parent) { _facetHome.parent.insertBefore(panel, _facetHome.next); }
      };
      if (facetMQ && facetMQ.matches && !(window.PD_MEDIA && window.PD_MEDIA.REDUCE)) {
        _facetRestore = restoreHome;
        panel.addEventListener("transitionend", restoreHome);
        _facetRestoreT = setTimeout(restoreHome, 380);
      } else {
        // desktop sidebar / reduced motion: restore immediately (Simon 2026-07-05: the sheet must return
        // to its in-flow home so the desktop sticky sidebar is intact)
        if (_facetHome && _facetHome.parent) { _facetHome.parent.insertBefore(panel, _facetHome.next); }
      }
      var _mci = document.getElementById("MainContent"); if (_mci) _mci.inert = false;
      if (_facetUntrap) { _facetUntrap(); _facetUntrap = null; }   // A3: release trap + restore focus to the opener
      if (_facetReturn && _facetReturn.focus) _facetReturn.focus();
    }
    if (toggle) toggle.addEventListener("click", function () { panel.classList.contains("open") ? closeP() : openP(); });
    if (close) close.addEventListener("click", closeP);
    document.addEventListener("keydown", function (e) { if (e.key === "Escape" && panel.classList.contains("open")) closeP(); });   // A3: Escape dismisses the sheet
    syncFacetsAria();
    /* round-3: no separate mode guard needed - syncFacetsAria owns inert (init + every MQ flip) */
    if (facetMQ) { if (facetMQ.addEventListener) facetMQ.addEventListener("change", syncFacetsAria); else if (facetMQ.addListener) facetMQ.addListener(syncFacetsAria); }
    // P8: ?g=PSA from the desktop mega-menu pre-selects that grading company
    try {
      var pg = new URLSearchParams(location.search).get("g");
      if (pg) { var gchip = panel.querySelector('.pd-fchips[data-facet="grader"] .pd-fchip[data-v*="' + pg + '"]'); if (gchip && !gchip.disabled) gchip.click(); }
    } catch (e) {}
    apply();
  })();

  /* ---------------- collection sort ---------------- */
  (function () {
    var sel = $("pd-sort"); if (!sel) return;
    var grid = $("pd-filter-grid") || document.querySelector(".pd-grid"); if (!grid) return;
    var orig = [].slice.call(grid.children);
    // 2026-09-09 (small truths a): an explicit price sort is ONE globally ordered list. It used to sort WITHIN
    // each display-option group (W8-QA10 item4b: Desk, then Wall + Desk), so "Price: high to low" ran down to
    // $19 and then restarted at $60 at the group boundary - a visibly false ordering. Both grids now do what
    // the plain grid has always done: hide every .pd-grp divider while a price sort is active (a curated group
    // label is meaningless once the order is not curated, and a divider has no price of its own), sort every
    // card together, and let "Featured" restore the curated order AND the dividers. Ties break on the card
    // title, so equal prices order deterministically instead of inheriting the server's grouped DOM order.
    function cardTitle(c) { var a = c.querySelector(".pd-card__link"); return a ? (a.textContent || "").trim().toLowerCase() : ""; }
    function byPrice(v) {
      return function (a, b) {
        var pa = parseFloat(a.getAttribute("data-price")) || 0, pb = parseFloat(b.getAttribute("data-price")) || 0;
        if (pa !== pb) return v === "price-asc" ? pa - pb : pb - pa;
        var ta = cardTitle(a), tb = cardTitle(b);
        return ta < tb ? -1 : ta > tb ? 1 : 0;
      };
    }
    sel.addEventListener("change", function () {
      var v = sel.value;
      // 2026-09-09 instrumentation repair: sorting was the one untracked collection control (facets
      // emit facet_select, this emitted nothing). Fired before every branch returns, so all three
      // sort paths report. `handle` comes from data-handle on the select (main-collection.liquid
      // stamps it on both layouts); "" if a future template forgets it, never undefined.
      pdEventQ("collection_sort", { sort: v, handle: sel.dataset.handle || "" });
      if (v === "featured") {
        // restore the curated order AND re-show the group dividers hidden by a price sort. The facet engine's
        // own .pd-hide on an empty group's divider is a CLASS, not an inline style, so a divider that the
        // active filters emptied stays hidden here.
        pdFlipGrid(orig, function () {   /* AUDIT-24 W2 F16 */
          [].slice.call(grid.querySelectorAll(".pd-grp")).forEach(function (d) { d.style.display = ""; });
          orig.forEach(function (c) { grid.appendChild(c); });
        });
        return;
      }
      var cards = [].slice.call(grid.querySelectorAll(".pd-card"));
      pdFlipGrid(cards, function () {   /* AUDIT-24 W2 F16 */
        [].slice.call(grid.querySelectorAll(".pd-grp")).forEach(function (d) { d.style.display = "none"; });
        cards.slice().sort(byPrice(v)).forEach(function (c) { grid.appendChild(c); });   /* round-1 fix: sort a COPY - in-place sort desyncs the FLIP snapshot */
      });
    });
  })();

  /* ============================================================
     CART (live Shopify cart; in-memory cache, refreshed after each mutation)
     ============================================================ */
  // Source of truth is the live Shopify cart (GET /cart.js). An in-memory cache keeps
  // the render functions synchronous + byte-identical; it is refreshed after every
  // mutation (add / change / remove).
  var _cart = { items: [], item_count: 0 };
  // PERFECT 10 (Simon 2026-10-04, 30-day test): 10% off when the cart holds 10+ DISPLAYS, any mix.
  // The discount itself is a Shopify AUTOMATIC discount titled VOL10.title: the 13 display products
  // below, minimum quantity 10 counted over those products only, combines with NOTHING (a code swaps
  // it and Shopify keeps whichever single discount is bigger). Shopify applies it at cart/checkout;
  // this block only DESCRIBES it so the PDP and drawer can say so. KEEP IN SYNC with the Admin
  // discount: handles = its product list, start/end = its startsAt/endsAt. Outside [start, end)
  // every surface below renders exactly as it did before.
  var VOL10 = {
    start: Date.parse("2026-10-04T21:30:00Z"), end: Date.parse("2026-11-03T21:30:00Z"),
    min: 10, pct: 10,
    title: "Perfect 10: 10% off 10+ displays", label: "Bundle: 10% off",   // label: Simon 2026-10-13 (the cards now say Bundle); title = the Admin discount, unchanged
    handles: ["phantom-ultra", "ultra-color", "ultra-thick-variant", "nano", "magneto", "magneto-color",
      "magneto-thick-cards", "magneto-gradedguard", "magneto-slabmags", "phantom-x-gradedguard",
      "phantomxslabmags", "phantom-x-slabmags-thick", "ungraded"]
  };
  function vol10On() { var n = Date.now(); return n >= VOL10.start && n < VOL10.end; }
  function vol10Item(h) { return VOL10.handles.indexOf(h) >= 0; }
  // true when /cart.js shows Perfect 10 money on this line (line_level_discount_allocations). Only that
  // discount is labelled: any other line-level discount renders exactly as before (and a code's title
  // is never printed, the popup-era leak guardrail).
  function hasVol10(it) {
    var a = it.line_level_discount_allocations || [];
    for (var k = 0; k < a.length; k++) {
      var d = a[k] && a[k].discount_application;
      if (d && d.title === VOL10.title && a[k].amount > 0) return true;
    }
    return false;
  }
  function mapItem(it) {
    return {
      key: it.key,
      id: it.variant_id || it.id,
      handle: it.handle,
      title: it.product_title,
      variant: (it.variant_title && it.variant_title !== "Default Title") ? it.variant_title : "",
      image: (it.featured_image && it.featured_image.url) ? it.featured_image.url : (it.image || ""),
      price: it.final_price / 100,
      qty: it.quantity,
      orig: (typeof it.original_price === "number" ? it.original_price : it.final_price) / 100,
      deal: hasVol10(it),
      // the SERVER's own line totals. A percentage off an odd-cent line (EUR 17.95 x 10 less 10% = 161.55)
      // cannot be rebuilt from the whole-cent final_price, so a settled line shows these exactly; a line
      // with a qty tap in flight (an AUDIT-24 F30 target) keeps the optimistic unit x qty arithmetic.
      lineFinal: typeof it.final_line_price === "number" ? it.final_line_price / 100 : null,
      lineOrig: typeof it.original_line_price === "number" ? it.original_line_price / 100 : null,
      settled: !(_optTargets && _optTargets[it.key])
    };
  }
  function lineAmt(i) { return (i.settled && i.lineFinal != null) ? i.lineFinal : i.price * i.qty; }
  // the line's money cell. A Perfect 10 line leads with its struck pre-discount total; every other
  // line is byte-identical to the markup this replaced.
  function linePriceHtml(i) {
    var was = (i.deal && i.orig > i.price) ? '<s class="pd-ci__was"><span class="pd-vh">Was </span>' + money((i.settled && i.lineOrig != null) ? i.lineOrig : i.orig * i.qty) + '</s>' : "";
    return was + money(lineAmt(i)) + (i.qty > 1 ? '<span class="pd-ci__unit">' + money(i.price) + ' ea</span>' : '');
  }
  function read() {
    var items = (_cart.items || []).map(mapItem), pending = false, k;
    // Perfect 10 mid-flight: a tap that takes the cart BELOW 10 displays still carries the pre-tap discount
    // in /cart.js until its response lands. For that one round trip show those lines at full price with no
    // label, so the row, the nudge and the Checkout total agree and the total is never below the charge.
    for (k in _optTargets) { pending = true; break; }
    if (pending) {
      var n = 0;
      items.forEach(function (i) { if (vol10Item(i.handle)) n += i.qty; });
      if (n < VOL10.min) items.forEach(function (i) { if (i.deal) { i.deal = false; i.price = i.orig; i.settled = false; } });
    }
    return items;
  }
  function count() { return _cart.item_count || 0; }
  function subtotal() { return read().reduce(function (s, i) { return s + lineAmt(i); }, 0); }
  // item6 2026-09-09: AUTHORITATIVE money (ARCH 29). subtotal() is the sum of the visible line rows and
  // equals items_subtotal_price once the queue settles, carrying any pending optimistic qty (AUDIT-24
  // F30) - so it stays the subtotal basis. It cannot know an ORDER-level discount, which is why the
  // charged amount comes off the cart object. NOT total_discount: the Ajax Cart API reference's own
  // example (3399 original / 3149 items_subtotal / 2925 total / 474 total_discount) proves it also
  // counts the 250 LINE-level allocation already baked into items_subtotal_price and into every line
  // row, so rendering it would leave Subtotal - Discount != Total on screen.
  // SETTLED, sub === items_subtotal_price/100 and BOTH branches below collapse to total_price/100
  // exactly: the drawer never invents a settled total. IN FLIGHT (round-2 blocker fix) sub already
  // carries the optimistic quantity while isp/tp are still the PRE-tap server allocation, so a single
  // arithmetic shape has to guess: sub - disc is right for a fixed-amount discount and understates a
  // percentage one on every DECREMENT (measured: $69.75 shown against a real $76.50), while scaling by
  // the rate is right for a percentage and understates a fixed-amount one on every INCREMENT. Take the
  // LARGER of the two, i.e. the smaller discount: for both UNCONDITIONAL shapes (a straight percentage or
  // a fixed amount off the order) the number on screen is then never BELOW what will be charged, it always
  // adds up (disc is derived back from it), and it lands on truth the moment the response does.
  // NOT never-below for a discount gated on a MINIMUM SUBTOTAL: a decrement that crosses a tier downwards
  // drops the true rate while both branches here still carry the pre-tap one, so the total reads low for
  // that one round trip (measured $9.00 and $22.50 under on a $45 line against the store's real 2023
  // laborday ladder). Accepted, not fixed: ARCH 29.7 has the evidence, the pinned magnitude (harness
  // risk.tier.*) and the alternative rule that would remove it. 0 <= total <= sub, so no clamp.
  function cartMoney() {
    var c = _cart || {}, sub = subtotal(), isp = c.items_subtotal_price, tp = c.total_price;
    if (typeof isp !== "number" || typeof tp !== "number" || isp <= tp || isp <= 0) return { sub: sub, disc: 0, total: sub };
    var t = Math.round(Math.max(sub - (isp - tp) / 100, sub * tp / isp) * 100) / 100;
    return { sub: sub, disc: Math.round((sub - t) * 100) / 100, total: t };
  }
  var _cartHydrated = false;   // #8 (Simon 2026-07-02): true once /cart.js has answered at least once. Until then a
  // cold drawer-open shows the skeleton (not a false "empty"), and a FAILED reconcile must never zero the badge.
  function fetchCart() {
    return fetch("/cart.js", { headers: { "Accept": "application/json" }, credentials: "same-origin" })
      // item6 2026-09-09: same r.ok guard as postAdd/changeLine (JS-1). A 4xx/5xx with a JSON error body
      // used to be assigned as the CART, which zeroed read() and painted "Your cart is empty".
      .then(function (r) { if (!r.ok) throw new Error("cart"); return r.json(); })
      .then(function (c) {
        _cart = c || _cart;
        _cartHydrated = true;
        setStale(false);
        // Simon 2026-06-23: fire a lightweight sync event on every cart refresh (page load + each add/change/
        // remove) so the PDP free-ship meter can mirror the REAL cart subtotal. Dedicated name (not the
        // count-bearing pd:cart:updated) to avoid double-counting in that event's listeners.
        try { document.dispatchEvent(new CustomEvent("pd:cartsync")); } catch (e) {}
        return _cart;
      })
      .catch(function () { setStale(true); return _cart; });
  }
  // item6 2026-09-09: a failed /cart.js refresh used to be SILENT - the catch handed the stale cache back
  // and the drawer kept painting a cart that may no longer be true. Show one honest line with a Retry
  // inside the drawer, keep the last known cart rendered, and drop the line on the next success. Only
  // once hydrated: before that, the cold-open path already owns the full renderCartError block.
  function setStale(on) {
    var el = $("pd-cart-stale");
    if (!on || !_cartHydrated) {
      // the Retry control may hold focus when a successful refresh removes the node; removing a focused
      // node drops focus to <body>, which is OUTSIDE the aria-modal panel and past trapFocus (keydown is
      // bound to .pd-cart__panel). Put focus back on a real control inside the panel first.
      if (el && el.parentNode) {
        if (el.contains(document.activeElement)) {
          var f = $("pd-cart-checkout"), ft = $("pd-cart-foot");
          if (!f || (ft && ft.style.display === "none")) f = $("pd-cart-x");
          if (f && f.focus) f.focus();
        }
        el.parentNode.removeChild(el);
      }
      return;
    }
    if (el) { var b0 = el.querySelector("button"); if (b0) b0.removeAttribute("aria-busy"); return; }
    var foot = $("pd-cart-foot"), panel = foot && foot.parentNode; if (!panel) return;
    el = document.createElement("div");
    el.id = "pd-cart-stale"; el.className = "pd-cart__stale"; el.setAttribute("role", "status");
    // sits BETWEEN #pd-cart-items and the footer, so no renderCart innerHTML write can destroy it and it
    // shows on an empty cart too (the footer is display:none there).
    // Inserted EMPTY, filled one task later: a role=status region that arrives with its text already in
    // it is unreliable across NVDA/JAWS/VoiceOver (live regions announce CHANGES to a region that was
    // already there). Same pattern as #pd-cart-prog, which ships empty in cart-drawer.liquid.
    panel.insertBefore(el, foot);
    setTimeout(function () {
      if (!el.parentNode) return;
      el.innerHTML = '<span>Could not refresh your cart</span><button type="button" class="pd-cart__retry">Try again</button>';
      el.querySelector("button").addEventListener("click", function () {
        this.setAttribute("aria-busy", "true");
        fetchCart().then(function () { syncBadge(); renderCart(); });
      });
    }, 50);
  }
  // extra (optional, Simon 2026-10-25 desk stand tick box): more {id, quantity} lines sent in the SAME request, so the
  // Magneto and its stand land together or not at all
  function postAdd(id, quantity, extra) {
    var body = { id: id, quantity: quantity || 1 };
    if (extra && extra.length) body = { items: [{ id: id, quantity: quantity || 1 }].concat(extra) };
    return fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify(body)
    }).then(function (r) {
      // JS-1: /cart/add.js returns HTTP 422 (sold out / not purchasable / over stock) or 404
      // (bad variant id) with an error body {status, message, description} and no `key`. r.json()
      // resolves on those, so without an r.ok check the success path runs and shows a false "Added".
      // Reject on a non-OK response (or an error body) so every caller's existing .catch fires the
      // honest "could not add" toast. The happy path (HTTP 200 cart-line JSON) is unchanged.
      return r.json().then(function (b) {
        if (!r.ok || (b && b.status && b.description)) {
          var e = new Error((b && b.description) || "Sorry, could not add to cart"); e.body = b; throw e;
        }
        return b;
      });
    });
  }
  // renderFloor (optional): a promise the response RENDER waits on (never the POST) - lets the remove-row
  // exit finish before the rebuild destroys the row. Resolves with _cart on success, null on failure.
  function changeLine(line, quantity, renderFloor) {
    return fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ line: line, quantity: quantity })
    }).then(function (r) {
      // AUDIT-24: same JS-1 guard as postAdd. /cart/change.js answers 422/404 with an error body
      // {status, message, description} that r.json() happily resolves; without this guard that body was
      // assigned as the CART, read() returned [], and the drawer painted "Your cart is empty" beside a
      // zeroed badge with no toast. Reject so the catch below (refetch + honest toast) handles it.
      return r.json().then(function (c) {
        if (!r.ok || (c && c.status && c.description)) {
          var e = new Error((c && c.description) || "Could not update cart"); e.body = c; throw e;
        }
        return c;
      });
    })
      .then(function (c) {
        _cart = c || _cart;
        // AUDIT-24 F30 (round-2 blocker fix): this response reflects only the ops completed so far, so
        // before painting it, re-apply every still-pending optimistic quantity target onto the fresh
        // cache. That keeps _cart AND the DOM current on every response - no backwards repaint, no
        // stale cache for the next tap to compute its target from, no stale row indices. The render is
        // therefore unconditional again (the old _cartPending render-skip traded the backslide for
        // swallowed taps; verified by simulation both ways).
        applyOptTargets();
        // item6 round 2: a 200 here IS a successful full-cart read, so it clears the refresh-failure
        // line exactly as fetchCart's success path does. Without this, one failed /cart.js could leave
        // "Could not refresh your cart" sitting over correct, freshly-committed numbers for the session.
        setStale(false);
        // The floor gates the RESOLUTION, not a detached side-chain: callers' after() (e.g. showUndo)
        // must run on the post-render DOM in the fast-response case too, and a throw inside the render
        // must reach the catch below (mandatory F30 amendment) rather than dying unhandled.
        return (renderFloor || Promise.resolve()).then(function () {
          syncBadge(); renderCart();
          // Simon 2026-06-23: in-drawer qty +/- and REMOVE update _cart here directly (not via fetchCart),
          // so fire the same sync event the PDP free-ship meter listens on.
          try { document.dispatchEvent(new CustomEvent("pd:cartsync")); } catch (e) {}
          return _cart;
        });
      })
      .catch(function () {
        toast("Could not update cart", "Couldn't update");   // JS-3: honest heading (not the static "Added to cart")
        // AUDIT-24 F30 amendment: the drawer renders optimistic quantities, so a failed mutation must
        // restore server truth on screen or a false qty/price/subtotal stays up permanently. Refetch (not
        // just re-render: _cart itself carries the optimistic write), and drop every pending optimistic
        // target - after a failure the only honest display is server truth; still-queued ops will
        // re-render as their own responses land. If the refetch also fails we keep the cache as-is; the
        // shopper already has the failure toast. Resolves null so callers can tell failure apart (e.g.
        // never show the "Removed / Undo" bar for a remove that did not happen).
        _optTargets = Object.create(null);
        return fetchCart().then(function () { syncBadge(); renderCart(); return null; });
      });
  }
  // AUDIT-24 F30: pending optimistic quantity targets, keyed by line key. Written by the qty tap handler,
  // re-applied onto every server response in changeLine, entry cleared when the tap's own op settles (seq
  // guard so an older op cannot clear a newer tap's entry), wiped wholesale on any failure.
  var _optTargets = Object.create(null), _optSeq = 0;
  // AUDIT-24 F37: line keys currently playing their removal exit - a rebuild during the 160ms window
  // re-applies the class (renderCart) so an unrelated render cannot resurrect the row at full opacity.
  var _exitingKeys = Object.create(null);
  function applyOptTargets() {
    var its = (_cart && _cart.items) || [], total = 0, i;
    for (i = 0; i < its.length; i++) {
      var t = _optTargets[its[i].key];
      if (t) its[i].quantity = t.q;
      total += its[i].quantity || 0;
    }
    if (_cart) _cart.item_count = total;
  }
  function flashLine(key) {
    var box = $("pd-cart-items"); if (!box) return;
    var idx = read().map(function (x) { return x.key; }).indexOf(key);
    if (idx < 0) idx = read().length - 1;
    var rows = box.querySelectorAll(".pd-ci");
    if (rows[idx]) { var r = rows[idx]; r.classList.add("flash"); setTimeout(function () { r.classList.remove("flash"); }, 800); }
  }
  function refreshAndRender(flashKey) {
    // AUDIT-24: applyOptTargets keeps the invariant "every server-truth render re-applies pending
    // optimistic targets first" on THIS path too (Undo restore + quick-add land here mid-chain);
    // it is a no-op when no targets are pending.
    return fetchCart().then(function () { applyOptTargets(); syncBadge(); renderCart(); if (flashKey) flashLine(flashKey); });
  }

  // W8 code-audit L7: reflect the Displays mega-menu open/closed state for assistive tech.
  (function () {
    var mitem = document.querySelector(".pd-navitem"), mtrig = mitem && mitem.querySelector(":scope > a");
    if (mtrig) {
      var setExp = function (v) { mtrig.setAttribute("aria-expanded", v ? "true" : "false"); };
      mitem.addEventListener("mouseenter", function () { setExp(true); });
      mitem.addEventListener("mouseleave", function () { setExp(false); });
      mitem.addEventListener("focusin", function () { setExp(true); });
      mitem.addEventListener("focusout", function (e) { if (!mitem.contains(e.relatedTarget)) setExp(false); });
    }
  })();
  var badge = $("pd-cn"), _prevCount = null;
  function syncBadge() {
    var n = count();
    if (!badge) return;
    badge.textContent = n;
    badge.classList.toggle("on", n > 0);
    badge.toggleAttribute("aria-hidden", n === 0);   // W8 code-audit L10: do not announce an empty "0" cart
    // wave3 item3a: the count-badge pulse is now the SOLE add confirmation (fly-to-cart removed).
    // Pulse only when the count actually rises (a real add), never on first page load or on a
    // remove/decrease, and never under prefers-reduced-motion.
    // AUDIT-24: also gate on the mutation queue being drained (_cartPending === 0) - optimistic taps move
    // _prevCount ahead of the server now, and a mid-queue reconcile could otherwise read as a "rise" and
    // pulse on a decrease.
    if (_prevCount !== null && n > _prevCount && !REDUCE && _cartPending === 0) pulseBadge();
    _prevCount = n;
  }
  function pulseBadge() {
    // W5 item1: kept SUBTLE. The old pulse popped to 1.5x on a strong overshoot spring (1.56), which read
    // as jumpy. A gentler ~1.3x rise on a mild ease-out reads as a calm confirmation, in keeping with the
    // softer drawer motion (this is the sole visible add-confirmation on the quick-add path, where no
    // drawer opens). Never fires under reduced motion (guarded at the syncBadge call site).
    badge.style.transition = "transform .4s cubic-bezier(.34,1.28,.64,1)";
    badge.style.transform = "scale(1.3)";
    setTimeout(function () { badge.style.transform = "scale(1)"; }, 180);
  }

  // founder-confirmed US program: free U.S. shipping on orders $200-$600. Above $600 standard rates
  // resume, so the meter must never promise free shipping there (Simon-approved 2026-06-10).
  var FREE_SHIP = 200, FREE_SHIP_MAX = 600;
  // Simon 2026-07-14: the program is US-only and the band is USD-defined, but /cart.js reports the session's
  // PRESENTMENT currency and moneyShort hardcodes "$", so non-USD sessions got wrong threshold math and a
  // wrong "free U.S. shipping" promise. Every free-ship meter (cart drawer + PDP #pd-qship + the wizard's
  // mirror in pd-wizard-buybox.liquid) now no-ops unless the cart is USD.
  var usdCart = function () { return ((_cart && _cart.currency) || "USD") === "USD"; };
  function renderProgress() {
    var el = $("pd-cart-prog"), ship = $("pd-cart-ship"); if (!el) return;
    // Simon 2026-07-14: the bar sits in the drawer FOOTER now (old top slot), in place of the always-on
    // "Shipping & taxes calculated at checkout" line (#pd-cart-ship). Exactly one of the two shows: the bar
    // for USD-presentment carts, the neutral ship line otherwise.
    var usd = usdCart();
    if (FREE_SHIP == null || !usd || !read().length) {
      el.hidden = true; el.innerHTML = "";
      if (ship) ship.hidden = false;
      return;
    }
    if (ship) ship.hidden = true;
    var s = subtotal(), pct = Math.min(100, Math.round(s / FREE_SHIP * 100));
    var inProgram = s >= FREE_SHIP && (FREE_SHIP_MAX == null || s <= FREE_SHIP_MAX);   // free shipping only inside the $200-$600 band
    el.hidden = false;
    el.setAttribute("role", "status");   // a11y: announce free-ship progress changes to SR
    el.classList.toggle("unlocked", inProgram);   // lime "unlocked" state only while free shipping actually applies
    var msg = s < FREE_SHIP
      ? '<p>Add <b>' + moneyShort(FREE_SHIP - s) + '</b> more for free U.S. shipping.</p>'
      : inProgram
        ? '<p>You have unlocked <b>free U.S. shipping</b>.</p>'
        : '<p>Orders over <b>' + moneyShort(FREE_SHIP_MAX) + '</b> ship at standard U.S. rates.</p>';   // honor the $600 ceiling honestly
    // AUDIT-24 W2 F36: build the track ONCE, mutate thereafter. The old innerHTML rebuild destroyed the
    // fill node on every render, so its width transition (and the .unlocked glow-once) never fired - a
    // fresh node has no start value. Node persistence alone fixes both: width animates between renders,
    // and the glow animation runs only on the render that actually crosses into the band. (Module is
    // dormant on live while the FREESHIP kill-switch is ON; this makes it correct whenever it returns.)
    var _track = el.querySelector(".pd-cart__progtrack");
    if (!_track) {
      el.innerHTML = '<p></p><div class="pd-cart__progtrack"><div class="pd-cart__progfill" style="width:0%"></div></div>';
    }
    var _p = el.querySelector("p"); if (_p) _p.outerHTML = msg;
    var _fill = el.querySelector(".pd-cart__progfill"); if (_fill) _fill.style.width = pct + "%";
  }

  // Per-product Judge.me ratings, baked site-wide by w1-liquid (snippets/product-ratings-map.liquid):
  // the #pd-ratings-data JSON tag { "<handle>": {"r":<rating>,"c":<count>} } is the single source of
  // truth (the same Judge.me per-product pair as the PDP badge + schema, so the numbers agree). Parsed
  // once and cached. Absent handle -> no echo (no fabrication, matching the PDP badge's "rating only
  // when real" rule).
  function ratingMap() {
    if (ratingMap._m !== undefined) return ratingMap._m;
    var n = $("pd-ratings-data"), m = null;
    if (n) { try { m = JSON.parse(n.textContent); } catch (e) { m = null; } }
    return (ratingMap._m = m);
  }
  function ratingFor(handle) {
    var m = ratingMap(), v = m && m[handle];
    return (v && v.r > 0 && v.c > 0) ? { r: v.r, c: v.c } : null;
  }
  function starsHtml(r) {
    var full = Math.floor(r), frac = r - full, h = "";
    for (var i = 0; i < 5; i++) {
      if (i < full) h += '<span class="pd-star" aria-hidden="true">★</span>';
      else if (i === full && frac >= 0.125) h += '<span class="pd-star p" aria-hidden="true" style="--pf:' + Math.round(frac * 100) + '%"><span class="pd-star__f">★</span>★</span>';
      else h += '<span class="pd-star e" aria-hidden="true">★</span>';
    }
    return h;
  }

  /* ---------------- "Complete the display" cross-sell selection logic (DORMANT) ----------------
     W6 item3 (Simon round 4): the cross-sell module is REMOVED from the cart drawer - the drawer render
     and its events (cross_sell_view / cross_sell_add) no longer fire, and the #pd-cart-xsell slot is gone
     from the markup. The DATA (snippets/cart-xsell-data.liquid -> #pd-xsell-data) and the compatibility
     selection core (xsellData + pickXsell below) are kept INTACT but dormant, ready to drive a future
     surface. The HARD LAW (kept for that future use): a Magneto mount only with a Magneto display, the
     GradedGuard bumper only with a display sized for it, universal accessories with any display; never
     OOS, never already-in-cart. NO discounts, NO urgency copy. */
  var _xsellData;   // parse-once cache: undefined = unparsed, null = absent/bad, object = parsed
  function xsellData() {
    if (_xsellData !== undefined) return _xsellData;
    var n = $("pd-xsell-data"), d = null;
    if (n) { try { d = JSON.parse(n.textContent); } catch (e) { d = null; } }
    return (_xsellData = d);
  }
  function _inList(list, h) { return !!(list && list.indexOf(h) >= 0); }
  // pickXsell: apply the compatibility HARD LAW against the live cart, return <= 2 cards.
  function pickXsell() {
    var data = xsellData(); if (!data || !data.cards || !data.cards.length) return [];
    var handles = read().map(function (i) { return i.handle; });
    if (!handles.length) return [];
    var inCart = {}; handles.forEach(function (h) { inCart[h] = 1; });
    var hasMag = false, hasGG = false, hasDisp = false;
    handles.forEach(function (h) {
      if (_inList(data.magneto, h)) hasMag = true;     // Magneto family present (iron core => mountable)
      if (_inList(data.ggSized, h)) hasGG = true;       // a display sized to hold the GradedGuard bumper
      if (_inList(data.displays, h)) hasDisp = true;    // any display at all
    });
    if (!hasDisp) return [];   // nothing to "complete" without a display in the cart (accessory-only => absent)
    function ok(c) { return c && c.vid && c.available && !inCart[c.handle]; }   // never OOS, never already-in-cart
    var picks = [], i, c;
    // 1) at most ONE Magneto mount, and only when a Magneto-family display is in the cart (LAW: mounts are Magneto-only)
    if (hasMag) { for (i = 0; i < data.cards.length; i++) { c = data.cards[i]; if (c.rule === "mount" && ok(c)) { picks.push(c); break; } } }
    // 2) the GradedGuard bumper, only alongside a display sized to hold it (LAW: fit-matrix relationship)
    if (hasGG) { for (i = 0; i < data.cards.length; i++) { c = data.cards[i]; if (c.rule === "bumper" && ok(c)) { picks.push(c); break; } } }
    // 3) universal accessories (any display) fill the remaining slot(s).
    // W5 item2: cap raised 2 -> 3. The drawer cross-sell is now a horizontal swipe row, so a 3rd card
    // adds no module height (it scrolls), and the per-rule caps are unchanged (<= 1 mount, <= 1 bumper);
    // the extra slot can ONLY be filled by a universal accessory, which is compatible with any display by
    // law. So the hard compatibility law is untouched; 3 is purely the horizontal capacity.
    for (i = 0; i < data.cards.length && picks.length < 3; i++) {
      c = data.cards[i];
      if (c.rule === "universal" && ok(c) && picks.indexOf(c) < 0) picks.push(c);
    }
    return picks.slice(0, 3);
  }
  // W6 item3: renderXsell() (the drawer-specific render) and xsellMaybeView() (the cross_sell_view event)
  // were REMOVED here, along with the in-drawer Add handler + the #pd-cart-xsell markup. pickXsell() above
  // stays as the reusable, side-effect-free selection core for a future surface. No cross-sell renders in
  // the cart drawer and no cross_sell_* events fire.

  // item6 2026-09-09: the Discount + Total rows exist ONLY while an order-level discount is live. With no
  // discount nothing is inserted and the footer is identical to before. Both rows reuse .pd-cart__sub, so
  // they inherit the drawer's existing type ramp (no new sizes, weights or colours).
  function renderCartDisc(m, n) {
    var foot = $("pd-cart-foot"), row = foot && foot.querySelector(".pd-cart__sub"), el = $("pd-cart-disc");
    if (!row) return;
    if (!n || !m.disc) { if (el && el.parentNode) el.parentNode.removeChild(el); return; }
    if (!el) { el = document.createElement("div"); el.id = "pd-cart-disc"; row.parentNode.insertBefore(el, row.nextSibling); }
    el.innerHTML = '<div class="pd-cart__sub pd-cart__disc"><span>Discount</span><span>&minus;' + money(m.disc) + '</span></div>' +
      '<div class="pd-cart__sub"><span>Total</span><span>' + money(m.total) + '</span></div>';
  }

  // Perfect 10 nudge (Simon 2026-10-04, approved copy): with 5-9 displays in the cart, say how many more
  // unlock the 10%. Counts VOL10.handles off the same (optimistic) quantities the rows show, so it moves in
  // the tap's own frame. Absent outside the test window, under 5 displays and at 10+ (the row labels take
  // over there). Inserted above Subtotal only while it applies, so the footer is otherwise unchanged.
  function renderVolNudge(items) {
    var foot = $("pd-cart-foot"), row = foot && foot.querySelector(".pd-cart__sub"), el = $("pd-cart-nudge"), n = 0;
    if (vol10On()) items.forEach(function (i) { if (vol10Item(i.handle)) n += i.qty; });
    var need = VOL10.min - n;
    if (!row || n < 5 || need <= 0) { if (el && el.parentNode) el.parentNode.removeChild(el); return; }
    if (!el) { el = document.createElement("p"); el.id = "pd-cart-nudge"; el.className = "pd-cart__nudge"; row.parentNode.insertBefore(el, row); }
    el.textContent = "Add " + need + " more display" + (need === 1 ? "" : "s") + " and save 10% on all of them";
  }

  function renderCart() {
    var items = read(), box = $("pd-cart-items");
    if (!box) return;
    var hn = $("pd-cart-headn"); if (hn) hn.textContent = items.length ? " (" + count() + ")" : "";
    if (!items.length) {
      box.innerHTML = '<div class="pd-cart__empty"><svg aria-hidden="true"><use href="#i-ct"/></svg>' +
        '<h4>Your cart is empty</h4><p>Protect and display your graded cards in handcrafted acrylic.</p>' +
        '<a class="pd-cta" href="/collections/all">Shop displays</a>' +
        '<a class="pd-cart__emptyquiz" href="/pages/find-your-display">Not sure? Take the 60-second fit finder &rarr;</a>' +
        (usdCart() ? '<p class="pd-cart__emptynote">Free U.S. shipping on orders $200-$600.</p>' : '') + '</div>';
    } else {
      // AUDIT-24 F33: patch, don't replace. When the row SET is unchanged (every qty +/- case), write only
      // the numbers into the existing rows: an innerHTML rebuild clamps the region's scrollTop to 0 (the
      // shopper loses their place on every tap with 2-3 items in the cart) and forces every 192px thumb to
      // re-decode. Rows carry data-key so the match is exact; anything unusual in the region (skeleton,
      // undo bar, empty/error state) falls through to the rebuild path.
      var rows = box.querySelectorAll(".pd-ci[data-key]");
      var canPatch = rows.length === items.length && !box.querySelector(".pd-cart__sk, .pd-undo, .pd-cart__empty, .pd-cart__err, .pd-ci--exiting");
      if (canPatch) { for (var pi = 0; pi < items.length; pi++) { if (rows[pi].getAttribute("data-key") !== items[pi].key) { canPatch = false; break; } } }
      if (canPatch) {
        items.forEach(function (i, idx) {
          var r = rows[idx];
          var q = r.querySelector(".pd-ci__qty span"); if (q) q.textContent = i.qty;
          var p = r.querySelector(".pd-ci__p");
          if (p) p.innerHTML = linePriceHtml(i);
          // Perfect 10: crossing 10 displays adds or drops the row label IN PLACE (a rebuild here would
          // drop a keyboard user's focus from the +/- button to <body>, outside the dialog's focus trap)
          var dl = r.querySelector(".pd-ci__deal");
          if (i.deal && !dl) {
            dl = document.createElement("div"); dl.className = "pd-ci__deal"; dl.textContent = VOL10.label;
            var an = r.querySelector(".pd-ci__v") || r.querySelector(".pd-ci__t");
            if (an) an.parentNode.insertBefore(dl, an.nextSibling);
          } else if (!i.deal && dl) { dl.parentNode.removeChild(dl); }
        });
      } else {
        // Row set changed (add/remove): rebuild, but preserve the shopper's place. The clamp to 0 happens
        // at write time, so a same-task restore is flicker-free.
        var st = box.scrollTop;
        // AUDIT-24: a live Undo bar must survive the rebuild (an optimistic qty tap now renders in the
        // same frame, and it must not erase the accidental-remove recovery affordance). Captured before
        // the innerHTML write, re-inserted after; its 4.5s cleanup timer holds the same node reference.
        var undoBar = box.querySelector(".pd-undo");
        box.innerHTML = items.map(function (i, idx) {
          var rt = ratingFor(i.handle);   // per-product rating echo (only when a real rating exists for this handle)
          var rateRow = rt ? '<div class="pd-ci__rate">' + starsHtml(rt.r) + '<i>' + esc(String(rt.r)) + '</i><span class="pd-ci__ratec">' + esc(String(rt.c)) + ' reviews</span></div>' : '';
          return '<div class="pd-ci" data-key="' + esc(i.key) + '">' +
            '<img class="pd-ci__img" src="' + esc(i.image) + '" alt="">' +
            '<div class="pd-ci__main"><div class="pd-ci__t">' + esc(i.title) + '</div>' +
            (i.variant ? '<div class="pd-ci__v">' + esc(i.variant) + '</div>' : '') +
            (i.deal ? '<div class="pd-ci__deal">' + esc(VOL10.label) + '</div>' : '') +
            rateRow +
            '<div class="pd-ci__row"><span class="pd-ci__qty"><button data-dec="' + idx + '" aria-label="Decrease quantity">&minus;</button><span>' + i.qty + '</span><button data-inc="' + idx + '" aria-label="Increase quantity">+</button></span>' +
            '<span class="pd-ci__p">' + linePriceHtml(i) + '</span></div>' +
            '<button class="pd-ci__rm" data-rm="' + idx + '" aria-label="Remove ' + esc(i.title) + '"><svg aria-hidden="true"><use href="#i-tr"/></svg>Remove</button></div></div>';
        }).join("");
        if (undoBar) box.insertBefore(undoBar, box.firstChild);
        box.scrollTop = st;
        // AUDIT-24 F37: a rebuild mid-exit regenerates the exiting row without its class - re-apply it by
        // key so the exit survives an unrelated render instead of popping back to full opacity.
        var exiting = Object.keys(_exitingKeys);
        if (exiting.length) {
          var fresh = box.querySelectorAll(".pd-ci[data-key]");
          for (var xf = 0; xf < fresh.length; xf++) {
            if (_exitingKeys[fresh[xf].getAttribute("data-key")]) fresh[xf].classList.add("pd-ci--exiting");
          }
        }
      }
    }
    var m = cartMoney();
    var sub = $("pd-cart-sub"); if (sub) sub.textContent = money(m.sub);
    renderCartDisc(m, items.length);
    renderVolNudge(items);
    // running total on the checkout button ("Checkout · $45.00"), live with every cart mutation (Simon-approved 2026-06-10).
    // item6: the button now names what is actually charged (m.total), so an order-level discount can no
    // longer send a shopper to checkout expecting the pre-discount number.
    var co = $("pd-cart-checkout"); if (co) { co.textContent = items.length ? "Checkout · " + money(m.total) : "Checkout"; co.disabled = false; }
    var foot = $("pd-cart-foot"); if (foot) foot.style.display = items.length ? "" : "none";
    renderProgress();
  }

  // public hook: PDP / collections / quick-add markup (rendered by other templates) call this.
  window.PhantomCart = {
    add: function (payload, openAfter) {
      var id = payload && (payload.id || payload.variantId);
      var quantity = (payload && (payload.quantity || payload.qty)) || 1;
      var extra = (payload && payload.extra) || null;
      // Simon 2026-10-25: an add carrying extra lines (the desk stand tick box) must never cost the main item. Shopify
      // adds a multi-line request all or nothing, so if it fails, retry the main line alone and report extraFailed.
      // If the main line itself cannot be added, the retry fails too and the caller's honest toast fires as before.
      var extraFailed = false;
      var send = function () {
        return postAdd(id, quantity, extra).catch(function (err) {
          if (!(extra && extra.length)) throw err;
          extraFailed = true;
          return postAdd(id, quantity);
        });
      };
      // W6 item2 (Simon round 4): kill the dead pause before the drawer opens. When this add opens the
      // drawer (openAfter=true, the PDP ATC), the panel begins its glide IMMEDIATELY with a loading
      // skeleton in the items region; the real content cascades in once the cart refetch lands. The
      // optimistic OPEN is fine; the optimistic CONFIRM is NOT: this promise (and the button's "Added")
      // still resolve only on the real POST result, and a failed POST reverts the open + lets the caller
      // fire the honest toast. The non-opening callers (quick-add, in-drawer cross-sell) are unchanged.
      if (openAfter) {
        openCart({ loading: true });
        return send().then(function (added) {
          return fetchCart().then(function () {
            applyOptTargets();   // AUDIT-24: same invariant as refreshAndRender - never paint truth that lags a pending tap
            syncBadge();
            try { document.dispatchEvent(new CustomEvent("pd:cart:updated", { detail: { count: count() } })); } catch (e) {}
            revealCartAfterLoad();   // refetch landed: swap skeleton -> real content, wave-5 cascade plays
            return { extraFailed: extraFailed };
          });
        }).catch(function (err) {
          closeCart();   // revert the optimistic open; the caller's .catch shows the honest "Couldn't add" toast
          throw err;
        });
      }
      // item6 round 2: the non-opening add (quick-add) is a real server mutation the checkout settle must
      // wait for. cartWork, not cartBusy: _cartPending gates syncBadge's pulse, which IS this path's only
      // add confirmation. The rejection is re-thrown so callers' honest "Couldn't add" toast still fires.
      cartWork(1);
      return send().then(function (added) {
        return refreshAndRender(added && added.key).then(function () {
          try { document.dispatchEvent(new CustomEvent("pd:cart:updated", { detail: { count: count() } })); } catch (e) {}
          return { extraFailed: extraFailed };
        });
      }).then(function (v) { cartWork(-1); return v; }, function (e) { cartWork(-1); throw e; });
    }
  };

  // cart drawer open/close (real modal: scroll-lock + focus-trap + ESC + focus restore)
  var wrap = $("pd-cartwrap"), _cartReturn = null, _cartUntrap = null, _cartInTimer = null, _cartXTimer = null;
  // W5 item1 (wave5, Simon round 3): how long the drawer content is held hidden after .open before it
  // cascades in (so nothing paints during the panel slide). This single number IS the treatment selector
  // for Simon's pick: 230 = "calm cascade" (content arrives as the panel finishes settling); 0 = "instant
  // content, slow panel" (content rides in with the panel, no cascade; both share the same CSS). Default:
  // calm cascade. The panel glide is .38s on --ease-drawer since Simon's 2026-08-18 ruling (was .52s).
  // AUDIT-24 F34 retime (2026-08-17): 230 -> 120, and the CSS stagger delays were retimed (items 0ms,
  // foot 70ms; the old 225ms foot delay was sequenced behind the removed cross-sell module's slot). The
  // content now begins its rise mid-glide and Checkout is fully visible at ~410ms instead of 815ms, inside
  // the 200-500ms drawer budget. The glide-then-content ORDER (Simon's round-3 pick) is preserved; only
  // the dead waiting was cut. 0 still = "instant content".
  var CART_CASCADE_MS = 120;
  // W6 item2: a brand-consistent loading skeleton shown in the items region during the optimistic open,
  // while the POST + cart refetch resolve. Reduced-motion is handled in CSS (shimmer off) + the reveal
  // below (instant swap). Row count = the current cart rows + the one being added (capped), so the
  // placeholder roughly matches what is about to render. NOTHING here is a confirmation: the header count
  // is intentionally blank until the server answers.
  function cartSkeletonRow() {
    return '<div class="pd-ci pd-ci--sk" aria-hidden="true">' +
      '<div class="pd-ci__img pd-sk"></div>' +
      '<div class="pd-ci__main">' +
        '<div class="pd-sk pd-sk--line" style="width:64%"></div>' +
        '<div class="pd-sk pd-sk--line pd-sk--sm" style="width:40%"></div>' +
        '<div class="pd-ci__row"><div class="pd-sk pd-sk--pill"></div><div class="pd-sk pd-sk--line pd-sk--sm" style="width:24%;margin:0"></div></div>' +
      '</div></div>';
  }
  function renderCartSkeleton() {
    var box = $("pd-cart-items"); if (!box) return;
    var rows = Math.min(3, Math.max(1, read().length + 1));
    var html = ""; for (var i = 0; i < rows; i++) html += cartSkeletonRow();
    box.innerHTML = '<div class="pd-cart__sk" role="status" aria-label="Updating your cart">' + html + '</div>';
    var hn = $("pd-cart-headn"); if (hn) hn.textContent = "";   // no count until the server confirms
    var foot = $("pd-cart-foot"); if (foot) foot.style.display = "";   // footer present (an item is on the way)
    // AUDIT-24 F32a: never render an untrue number while the skeleton runs. The footer used to keep its
    // server-rendered subtotal ($0.00 on a first add) and its stale checkout total; strip the label here
    // and let CSS (.pd-cart--loading) hide the subtotal + dim the CTA until the refetch lands. disabled
    // (not just pointer-events) so keyboard/AT cannot activate a checkout mid-add either; renderCart /
    // renderCartError re-enable.
    var co = $("pd-cart-checkout"); if (co) { co.textContent = "Checkout"; co.disabled = true; }
  }
  // reveal real content after the optimistic-open refetch lands: swap the skeleton out without a fade-out
  // flicker, then replay the wave-5 cascade on the real rows. Bails safely if the user already closed it.
  function revealCartAfterLoad() {
    if (!wrap || !wrap.classList.contains("open")) { renderCart(); return; }
    wrap.classList.remove("pd-cart--loading");
    if (REDUCE || !CART_CASCADE_MS) { renderCart(); wrap.classList.add("pd-cart--in"); return; }
    // AUDIT-24 F32b: re-hide ONLY the items region for the skeleton -> content swap. The old path dropped
    // .pd-cart--in wholesale, which snapped the already-painted footer (and the lime Checkout CTA) to
    // opacity 0 and faded it back ~585ms later - the primary CTA vanished at the exact moment of
    // commitment. The footer now stays mounted and its numbers cross-update in place; only the items
    // region replays an entrance.
    wrap.classList.add("pd-cart--swapitems");
    wrap.classList.add("pd-cart--in");
    renderCart();
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { wrap.classList.remove("pd-cart--swapitems"); });
    });
  }
  // #8 (Simon 2026-07-02): honest failure row for a cold open whose /cart.js fetch fails. Keeps the badge.
  function renderCartError() {
    var box = $("pd-cart-items"); if (!box) return;
    box.innerHTML = '<div class="pd-cart__err" role="alert"><p>We could not load your cart.</p>' +
      '<button type="button" class="pd-cta" id="pd-cart-retry">Try again</button></div>';
    var hn = $("pd-cart-headn"); if (hn) hn.textContent = "";
    var foot = $("pd-cart-foot"); if (foot) foot.style.display = "none";
    var co = $("pd-cart-checkout"); if (co) co.disabled = false;   // AUDIT-24: never strand the disabled state (footer is hidden here anyway)
    var rb = $("pd-cart-retry");
    if (rb) rb.addEventListener("click", function () {
      // AUDIT-24: the retry skeleton must carry the same .pd-cart--loading contract as every other
      // skeleton (subtotal hidden, CTA dimmed) - this is the one path where /cart.js already failed once,
      // i.e. exactly where the server-rendered subtotal is most likely stale. Cleared in both branches.
      if (wrap) wrap.classList.add("pd-cart--loading");
      renderCartSkeleton();
      fetchCart().then(function () {
        if (wrap) wrap.classList.remove("pd-cart--loading");
        if (_cartHydrated) { renderCart(); } else { renderCartError(); }
      });
    });
  }
  /* Simon 2026-07-07 (carryover audit): view_cart + remove_from_cart restored at theme
     level. Aurora fired these (phantom-cart-ga4.liquid); the Shopify Google channel does
     NOT send them, so the v2 swap silently dropped the GA4 cart-funnel steps. Shapes per
     the current GA4 events reference: currency + value + items[{item_id, item_name,
     price, quantity}]. */
  function cartItemsForGa4(list) {
    return list.map(function (i) {
      return { item_id: String(i.id), item_name: i.title + (i.variant ? " - " + i.variant : ""), price: i.price, quantity: i.qty };
    });
  }
  function emitViewCart() {
    try {
      if (window.pdEvent) window.pdEvent("view_cart", {
        currency: _cart.currency || "USD",
        value: Math.round(subtotal() * 100) / 100,
        items: cartItemsForGa4(read())
      });
    } catch (e) {}
  }
  function openCart(opts) {
    if (!wrap) return;
    var loading = !!(opts && opts.loading);
    // #8: a plain cart-icon tap before the idle /cart.js has hydrated must NOT render the empty state beside a
    // badge that shows items. Show the skeleton and fetch; on success reveal the real content, on failure show
    // a retry row (the badge keeps its server-rendered count).
    var coldOpen = !loading && !_cartHydrated, skel = loading || coldOpen;
    if (skel) renderCartSkeleton(); else renderCart();
    wrap.classList.toggle("pd-cart--loading", skel);
    wrap.classList.remove("pd-cart--swapitems");   // AUDIT-24: defensive clear (its remover is a rAF pair that a hidden tab can starve)
    wrap.classList.remove("pd-cart--in");   // reset so the content cascade replays on every fresh open
    wrap.inert = false;   // AUDIT-24: re-admit the drawer to the tab order (see closeCart)
    wrap.classList.add("open"); wrap.setAttribute("aria-hidden", "false");
    lockScroll(true); _cartReturn = document.activeElement;
    // While loading, the skeleton rides in with the panel glide (revealed immediately, no cascade): the
    // cascade is saved for the REAL content when the refetch lands. A normal open cascades as before.
    clearTimeout(_cartInTimer);
    if (skel || REDUCE || !CART_CASCADE_MS) { wrap.classList.add("pd-cart--in"); }
    else { _cartInTimer = setTimeout(function () { if (wrap.classList.contains("open")) wrap.classList.add("pd-cart--in"); }, CART_CASCADE_MS); }
    clearTimeout(_cartXTimer);
    var x = $("pd-cart-x"); if (x) _cartXTimer = setTimeout(function () { x.focus(); }, 60);
    var panel = wrap.querySelector(".pd-cart__panel"); if (panel) _cartUntrap = trapFocus(panel);
    if (coldOpen) {
      fetchCart().then(function () {
        if (!wrap.classList.contains("open")) return;
        if (_cartHydrated) { revealCartAfterLoad(); emitViewCart(); } else { wrap.classList.remove("pd-cart--loading"); renderCartError(); }
      });
    } else {
      emitViewCart();
    }
  }
  function closeCart() {
    if (!wrap || !wrap.classList.contains("open")) return;
    clearTimeout(_cartInTimer);
    // AUDIT-24: the drawer now has a real 260ms exit (visibility transitions), so during the exit window
    // it is still visible and would otherwise stay focusable: clear the pending 60ms x.focus() timer (a
    // failed ATC can close within 60ms, and the stale timer used to be a no-op only because the old close
    // was a single-frame hide) and mark the wrap inert (same idiom as the mobile nav's MainContent) so
    // keyboard/AT can never land inside the closing drawer. openCart lifts inert again.
    clearTimeout(_cartXTimer);
    wrap.inert = true;
    wrap.classList.remove("open"); wrap.classList.remove("pd-cart--in");
    wrap.classList.remove("pd-cart--loading"); wrap.classList.remove("pd-cart--swapitems");
    wrap.setAttribute("aria-hidden", "true");
    lockScroll(false); if (_cartUntrap) { _cartUntrap(); _cartUntrap = null; }
    if (_coCancel) _coCancel();   // item6 round 2: a dismissal cancels a pending checkout settle (see the handler below)
    if (_cartReturn && _cartReturn.focus) _cartReturn.focus();
  }
  if ($("pd-cart-btn")) $("pd-cart-btn").addEventListener("click", openCart);
  if ($("pd-cart-x")) $("pd-cart-x").addEventListener("click", closeCart);
  if (wrap) wrap.addEventListener("click", function (e) { if (e.target === wrap) closeCart(); });
  document.addEventListener("keydown", function (e) { if (e.key === "Escape") closeCart(); });
  // item6 2026-09-09: never leave for /checkout with drawer mutations still in flight. /checkout reads the
  // SERVER cart, so a click landing between an optimistic qty tap and its /cart/change.js response used to
  // check out the PRE-tap cart while the drawer showed the post-tap numbers. Wait for the queue to drain,
  // re-read the cart once, then navigate. Hard 4s cap: on a timeout we navigate anyway, because trapping a
  // shopper who asked to pay is worse than checking out a cart that is one tap behind. The LABEL never
  // changes (nothing reflows under the pointer); the busy state is aria-busy plus its CSS dim.
  var CO_SETTLE_MS = 4000;
  if ($("pd-cart-checkout")) $("pd-cart-checkout").addEventListener("click", function () {
    var co = this, gone = false, cap = null;
    if (co.getAttribute("aria-busy") === "true") return;   // the CSS dim is pointer-events only: a keyboard Enter can still re-fire
    // item6 round 2: the wait needs a CANCEL path. Dismissing the drawer (X, backdrop, Escape - all of
    // them closeCart) used to leave the pending go() armed, so a shopper who tapped +, tapped Checkout,
    // then changed their mind was still taken to /checkout up to 4s later, off a drawer that was no
    // longer on screen. That state could not exist before this item (the old handler navigated in the
    // same tick), so closeCart cancels: no navigation, no trapped shopper, no stuck aria-busy.
    function stop() { gone = true; clearTimeout(cap); _coCancel = null; co.removeAttribute("aria-busy"); }
    function go() { if (gone) return; stop(); window.location.href = "/checkout"; }
    if (!cartWorking()) { go(); return; }   // settled: byte-identical to the old handler
    co.setAttribute("aria-busy", "true");
    cap = setTimeout(go, CO_SETTLE_MS);
    _coCancel = stop;
    (function tick() {
      if (gone) return;
      if (cartWorking()) { setTimeout(tick, 50); return; }
      fetchCart().then(function () { syncBadge(); renderCart(); go(); });   // render even if the wait was cancelled: the cache moved, the badge must follow. go() bails on its own guard.
    })();
  });
  // #7 (Simon 2026-07-02): serialize drawer qty/remove mutations. Each tap captures the line KEY and queues an op
  // that recomputes the target from the FRESHEST cart, so three fast "+" taps apply +3 (not +1 off a shared stale
  // qty), and a remove racing an in-flight change can never retarget the wrong line after the server reindexes.
  var _cartChain = Promise.resolve(), _cartPending = 0;
  function cartBusy(d) { _cartPending += d; if (wrap) wrap.classList.toggle("pd-cart--updating", _cartPending > 0); }
  // item6 round 2: server-cart work that is NOT in the mutateLineByKey queue - the Undo restore and the
  // non-opening PhantomCart.add (quick-add). The checkout settle must wait for these too, or Checkout
  // inside an Undo round trip leaves on the pre-Undo cart, which is the exact defect (b) exists to kill.
  // Deliberately a SECOND counter, not cartBusy: _cartPending also gates syncBadge's add pulse (the only
  // add confirmation quick-add has) and .pd-cart--updating, and neither semantic should change.
  var _cartWork = 0, _coCancel = null;
  function cartWork(d) { _cartWork += d; }
  function cartWorking() { return _cartPending + _cartWork; }
  function mutateLineByKey(key, compute, after, renderFloor) {
    cartBusy(1);
    _cartChain = _cartChain.then(function () {
      var cur = read(), idx = -1;
      for (var i = 0; i < cur.length; i++) { if (cur[i].key === key) { idx = i; break; } }
      if (idx < 0) { if (after) after(false); return; }       // line already gone: no-op (report non-success)
      // after(ok): ok=false when changeLine failed (it resolves null after refetching truth), so callers
      // like showUndo never announce a removal that did not happen.
      return changeLine(idx + 1, compute(cur[idx].qty), renderFloor).then(function (res) { if (after) after(res !== null); });
    }).catch(function () {
      // AUDIT-24 F30 amendment (mandatory): this swallow must still restore truth on screen, or an
      // optimistic value survives a throw out of the success path (e.g. renderCart itself failing).
      try { syncBadge(); renderCart(); } catch (e) {}
    }).then(function () { cartBusy(-1); });
    return _cartChain;
  }
  if (box_listen()) {}
  function box_listen() {
    var box = $("pd-cart-items"); if (!box) return false;
    box.addEventListener("click", function (e) {
      var inc = e.target.closest("[data-inc]"), dec = e.target.closest("[data-dec]"), rm = e.target.closest("[data-rm]");
      if (!inc && !dec && !rm) return;
      var items = read();
      // AUDIT-24 (round 2): resolve the tapped LINE BY KEY from the row's data-key, never by the
      // positional data-inc/-dec/-rm index - the DOM can briefly lag the cache around a response render,
      // and a positional lookup then targets the WRONG line (or a vanished index). Key lookup makes a
      // stale row an honest no-op instead.
      var rowEl = e.target.closest(".pd-ci"), rowKey = rowEl && rowEl.getAttribute("data-key");
      var idx = -1; for (var ki = 0; ki < items.length; ki++) { if (items[ki].key === rowKey) { idx = ki; break; } }
      if (idx < 0) return;
      // AUDIT-24 F30: optimistic quantity. The number, line price and subtotal move in the SAME FRAME as
      // the tap (a frozen stepper for a 300-800ms round trip is how shoppers get trained to double-tap).
      // The #7 serialize ruling (2026-07-02) is preserved with ABSOLUTE targets: each tap advances the
      // optimistic cache first and captures its own target, and every server response re-applies the
      // pending targets (applyOptTargets in changeLine) before it repaints, so the cache and the DOM can
      // never fall behind a tap. The overlay entry is seq-guarded and cleared when this tap's own op
      // settles; any failure wipes the overlay and refetches truth.
      function optimisticQty() {
        var line = _cart.items && _cart.items[idx], it = items[idx];
        if (!line || !it) return null;
        var newQ = inc ? it.qty + 1 : Math.max(1, it.qty - 1);
        if (newQ === it.qty) return null;                       // dec at qty 1: nothing to do, no POST
        _optTargets[it.key] = { q: newQ, n: ++_optSeq };
        line.quantity = newQ;
        applyOptTargets();                                      // recompute item_count from quantities
        syncBadge(); renderCart();                              // patch path: scroll + DOM intact
        return newQ;
      }
      if (inc || dec) {
        var key = items[idx].key, tq = optimisticQty();
        if (tq !== null) {
          var mySeq = _optSeq;
          mutateLineByKey(key, function () { return tq; }, function () {
            var entry = _optTargets[key];
            if (entry && entry.n === mySeq) delete _optTargets[key];   // only the newest tap's op may clear its entry
          });
        }
      }
      else if (rm) {
        var removed = items[idx], ri = idx;
        if (removed) {
          try {
            if (window.pdEvent) window.pdEvent("remove_from_cart", {
              currency: _cart.currency || "USD",
              value: Math.round(removed.price * removed.qty * 100) / 100,
              items: cartItemsForGa4([removed])
            });
          } catch (e) {}
          // AUDIT-24 F37 (amended): exit the row before the rebuild deletes it - opacity-only via the
          // .pd-ci--exiting CLASS (translateX would create horizontal overflow in the overflow-y:auto items
          // region; inline styles would survive renderCart's patch path and strand an invisible row on a
          // failed remove - the class is instead a canPatch bail-out, so any render while a row is
          // mid-exit takes the rebuild path and produces a clean DOM either way). The POST starts
          // IMMEDIATELY; the 160ms floor gates only the response RENDER (changeLine's renderFloor), same
          // floor idiom as the PDP ATC spinner, so the exit always completes without slowing the remove.
          var row = rm.closest(".pd-ci"), floor = null;
          if (row && !REDUCE) {
            row.classList.add("pd-ci--exiting");
            _exitingKeys[removed.key] = 1;   // survives a mid-exit rebuild: renderCart re-applies the class by key
            floor = new Promise(function (res) { setTimeout(res, 160); });
          }
          mutateLineByKey(removed.key, function () { return 0; }, function (ok) {
            delete _exitingKeys[removed.key];
            if (ok) { showUndo(removed, ri); }
            else {
              // failed/no-op: bring the row back. Look it up fresh - a render during the exit window may
              // have replaced the node the tap captured.
              var rows2 = box.querySelectorAll(".pd-ci[data-key]");
              for (var x2 = 0; x2 < rows2.length; x2++) { if (rows2[x2].getAttribute("data-key") === removed.key) { rows2[x2].classList.remove("pd-ci--exiting"); break; } }
              if (row) row.classList.remove("pd-ci--exiting");
            }
          }, floor);
        }
      }
    });
    return true;
  }
  // W6 item3: the in-drawer cross-sell "Add" handler (and its cross_sell_add event) was REMOVED with the
  // module. The PDP / quick-add add paths (PhantomCart.add) are untouched.
  var _undoTimer = null;
  function showUndo(item, idx) {
    var box = $("pd-cart-items"); if (!box || !item) return;
    var bar = document.createElement("div");
    bar.className = "pd-undo";
    bar.innerHTML = '<span>Removed ' + esc(item.title) + '</span><button type="button">Undo</button>';
    box.insertBefore(bar, box.firstChild);
    bar.querySelector("button").addEventListener("click", function () {
      // item6 round 2: cartWork brackets the restore so the checkout settle waits for it (see cartWorking).
      if (item && item.id) { cartWork(1); postAdd(item.id, item.qty).then(function () { return refreshAndRender(); })
        .catch(function () { toast("Sorry, could not add to cart", "Couldn't add"); })   // JS-1: honest failure (postAdd now rejects on HTTP error)
        .then(function () { cartWork(-1); }); }
    });
    clearTimeout(_undoTimer);
    // AUDIT-24 W2 F37p2: exit mirrors the entrance (.out shares the -8px path); removal stays
    // TIMER-driven, never transitionend-driven, so a browser without @starting-style support (or a
    // swallowed event) can never strand the bar.
    _undoTimer = setTimeout(function () {
      bar.classList.add("out");
      setTimeout(function () { if (bar.parentNode) bar.parentNode.removeChild(bar); }, 150);
    }, 4500);
  }
  // Simon 2026-06-25 (perf audit #10): the cart-count badge is already server-rendered (header #pd-cn = cart.item_count),
  // so defer the /cart.js reconciliation off the load critical path. The badge is correct immediately; the drawer
  // line-items reconcile on idle (before the user can click to open it).
  (window.requestIdleCallback || function (f) { setTimeout(f, 600); })(function () { fetchCart().then(function () { if (_cartHydrated) { syncBadge(); renderCart(); } }); });   // #8: reconcile only on a real answer; a failed idle fetch keeps the correct server-rendered badge

  // toast(label[, heading]). The heading defaults to the component's success "Added to cart" line;
  // the add-failure paths pass "Couldn't add" so the heading never lies about a failed add (D2 honest-
  // failure, Simon-approved 2026-06-10). Set via .pd-toast__t so no Liquid markup change is needed.
  function toast(label, heading) {
    var el = $("pd-toast"); if (!el) return;
    var t = el.querySelector(".pd-toast__t"); if (t) t.textContent = heading || "Added to cart";
    var d = $("pd-toast-d"); if (d) d.textContent = label;
    el.classList.add("show"); setTimeout(function () { el.classList.remove("show"); }, 3000);
  }

  /* ---------------- "Add a desk stand" row (Simon 2026-10-25) ----------------
     Markup + live stand data: snippets/pd-stand-row.liquid (Magneto pages, under the delivery line). Design:
     docs/superpowers/specs/2026-10-25-cart-desk-stand-upsell-design.md. Round 3: a TICK BOX. Ticked, the picked stand
     rides along with the Magneto on the main Add to cart: the PDP's render() adds standRowExtra() to the Add to cart,
     sticky bar and Afterpay figures, and doAdd() sends both in one /cart/add.js call, then calls standRowAfterAdd().
     The row sits UNDER Add to cart (Simon's call), so once the Magneto has been added from this page, ticking adds the
     stand straight away (quick add, no drawer). Unticked by default and reset after every add. A sold-out combination
     is aria-disabled; a pick that sells out moves to the first available one. Visibility (Ungraded Type, display sold
     out) is owned by setBuyState via standRowVisible(). Any change fires "pd:stand" so the PDP re-renders its totals. */
  function standRowData(row) {
    if (row._sr) return row._sr;
    var d = [], n = row.querySelector(".pd-dsr__data");
    if (n) { try { d = JSON.parse(n.textContent) || []; } catch (e) { d = []; } }
    row._sr = { v: d, type: "Single", color: "Obsidian Black", checked: false };
    return row._sr;
  }
  function standRowFind(s, type, color) {
    for (var i = 0; i < s.v.length; i++) if (s.v[i].type === type && s.v[i].color === color) return s.v[i];
    return null;
  }
  function standRowSync(row) {
    var s = standRowData(row), cur = standRowFind(s, s.type, s.color);
    if (!cur || !cur.available) {
      for (var i = 0; i < s.v.length; i++) if (s.v[i].available) { cur = s.v[i]; s.type = cur.type; s.color = cur.color; break; }
    }
    if (!cur) return null;
    row.querySelectorAll("[data-sr-type]").forEach(function (b) {
      var t = b.getAttribute("data-sr-type"), m = standRowFind(s, t, s.color);
      b.setAttribute("aria-pressed", t === s.type ? "true" : "false");
      if (m && m.available) b.removeAttribute("aria-disabled"); else b.setAttribute("aria-disabled", "true");
      var p = null; for (var j = 0; j < s.v.length; j++) if (s.v[j].type === t) { p = s.v[j].price; break; }
      var pe = b.querySelector(".pd-dsr__p"); if (p && pe) pe.textContent = p;
    });
    row.querySelectorAll("[data-sr-color]").forEach(function (b) {
      var c = b.getAttribute("data-sr-color"), m = standRowFind(s, s.type, c);
      b.setAttribute("aria-pressed", c === s.color ? "true" : "false");
      if (m && m.available) b.removeAttribute("aria-disabled"); else b.setAttribute("aria-disabled", "true");
    });
    var img = row.querySelector(".pd-dsr__img");
    if (img) { if (cur.img && img.getAttribute("src") !== cur.img) img.src = cur.img; img.alt = "Magneto Desk Stand " + cur.type + " in " + cur.color + ", holding a graded card display"; }
    var pr = row.querySelector(".pd-dsr__price"); if (pr) pr.textContent = "+" + cur.price;
    var cb = row.querySelector("[data-sr-check]"); if (cb) cb.checked = s.checked;
    row.classList.toggle("is-on", s.checked);
    return cur;
  }
  // what the PDP adds to its totals and its add: the picked stand when the row is shown, ticked and in stock
  function standRowExtra(row) {
    if (!row || row.hidden) return null;
    var s = standRowData(row); if (!s.checked) return null;
    var cur = standRowFind(s, s.type, s.color);
    return cur && cur.available ? cur : null;
  }
  function standRowChanged(row) { try { row.dispatchEvent(new CustomEvent("pd:stand", { bubbles: true })); } catch (e) {} }
  function standRowNote(row, text, ms) {
    var n = row.querySelector(".pd-dsr__note"); if (!n) return;
    n.textContent = text; clearTimeout(row._srN);
    row._srN = setTimeout(function () { n.textContent = ""; }, ms || 3500);
  }
  function standRowEvent(row, cur, via) {
    pdEventQ("stand_row_add", {
      surface: row.getAttribute("data-surface") || "pdp", product: row.getAttribute("data-product") || "", via: via,
      variant_id: cur.vid, stand_type: cur.type, stand_color: cur.color, value: cur.cents / 100,
      currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || "USD"
    });
  }
  // the Magneto went in but the stand could not (sold out since the page loaded, etc.): untick and say so plainly
  function standRowFailed(row) {
    var s = standRowData(row); s.checked = false; standRowSync(row);
    standRowNote(row, "The desk stand couldn't be added, but your Magneto is in your cart.", 8000);
    standRowChanged(row);
  }
  // called by the PDP's doAdd() after a successful Add to cart that carried the ticked stand
  function standRowAfterAdd(row, cur, via) {
    var s = standRowData(row); s.checked = false; standRowSync(row);
    standRowNote(row, "Desk stand added to your cart.");
    standRowEvent(row, cur, via);
    standRowChanged(row);
  }
  // called by setBuyState on every variant change: Ungraded shows the row only for its Magneto type, and the row
  // follows the delivery line (hidden while the selected display variant is sold out).
  // stand_row_view = the first time the un-hidden row is actually ON SCREEN (it boots below the fold on both layouts),
  // sent through pdEventQ because pd.js boots before gtag exists (a plain pdEvent at boot never reaches GA4).
  function standRowVisible(row, avail, variant) {
    var need = row.getAttribute("data-sr-need");
    var on = !!avail && (!need || !!(variant && variant.opts && variant.opts.indexOf(need) >= 0));
    var was = row.hidden;
    row.hidden = !on;
    if (was !== row.hidden) {
      // a row that hides drops its tick, and the PDP re-renders its totals once this render has finished
      if (!on && row._sr && row._sr.checked) { row._sr.checked = false; standRowSync(row); }
      setTimeout(function () { standRowChanged(row); }, 0);
    }
    if (!on || row._srSeen || row._srIO) return;
    var send = function () {
      if (row._srSeen) return; row._srSeen = true;
      pdEventQ("stand_row_view", { surface: row.getAttribute("data-surface") || "pdp", product: row.getAttribute("data-product") || "" });
    };
    if (!("IntersectionObserver" in window)) { send(); return; }
    row._srIO = new IntersectionObserver(function (es) {
      for (var k = 0; k < es.length; k++) if (es[k].isIntersecting && !row.hidden) { send(); row._srIO.disconnect(); return; }
    }, { threshold: 0.5 });
    row._srIO.observe(row);
  }
  (function () {
    var rows = document.querySelectorAll("[data-stand-row]");
    if (!rows.length) return;
    rows.forEach(function (r) { standRowSync(r); });
    document.addEventListener("change", function (e) {
      var cb = e.target && e.target.matches && e.target.matches("[data-sr-check]") ? e.target : null;
      var row = cb && cb.closest("[data-stand-row]"); if (!row) return;
      var s = standRowData(row);
      s.checked = cb.checked; standRowSync(row); standRowChanged(row);
      // the Magneto is already in the cart from this page: ticking now adds the stand straight away
      if (s.checked && row._displayAdded && window.PhantomCart) {
        var cur = standRowFind(s, s.type, s.color); if (!cur || !cur.available) return;
        cb.disabled = true;
        window.PhantomCart.add({ id: cur.vid, quantity: 1 }, false).then(function () {
          cb.disabled = false; toast("Magneto Desk Stand " + cur.type + " in " + cur.color);
          standRowAfterAdd(row, cur, "tick");
        }, function () {
          cb.disabled = false; s.checked = false; standRowSync(row); standRowChanged(row);
          toast("Sorry, could not add to cart", "Couldn't add");
        });
      }
    });
    document.addEventListener("click", function (e) {
      var t = e.target && e.target.closest ? e.target.closest("[data-sr-type],[data-sr-color]") : null;
      var row = t && t.closest("[data-stand-row]"); if (!row) return;
      if (t.getAttribute("aria-disabled") === "true") return;
      var s = standRowData(row);
      if (t.hasAttribute("data-sr-type")) s.type = t.getAttribute("data-sr-type");
      else s.color = t.getAttribute("data-sr-color");
      standRowSync(row); standRowChanged(row);
    });
  })();

  /* ---------------- search overlay ---------------- */
  (function () {
    var ov = $("pd-search"); if (!ov) return;
    var index = []; try { index = JSON.parse(ov.dataset.pdIndex) || []; } catch (e) {}
    var input = $("pd-search-in"), results = $("pd-search-results");
    var _searchReturn = null, _searchUntrap = null;
    function open() {
      ov.inert = false;
      ov.classList.add("open"); ov.setAttribute("aria-hidden", "false");
      lockScroll(true); _searchReturn = document.activeElement;
      // AUDIT-24 F24: focus SYNCHRONOUSLY inside the user-gesture task. iOS Safari only raises the
      // software keyboard for a focus() in the tap's call stack; the old setTimeout(..., 50) escaped it,
      // so iPhone shoppers had to tap the field a second time. The offsetHeight read flushes style so the
      // input is no longer visibility:hidden when focus() runs; preventScroll stops the focus scroll from
      // fighting lockScroll's body{position:fixed} pin (older Safari ignores the option harmlessly).
      void ov.offsetHeight;
      if (input) input.focus({ preventScroll: true });
      var panel = ov.querySelector(".pd-search__panel"); if (panel) _searchUntrap = trapFocus(panel);
    }
    function close() {
      if (!ov.classList.contains("open")) return;
      ov.inert = true;   /* AUDIT-24 W2: focusable content must not sit inside aria-hidden during the 220ms visible exit */
      ov.classList.remove("open"); ov.setAttribute("aria-hidden", "true");
      lockScroll(false); if (_searchUntrap) { _searchUntrap(); _searchUntrap = null; }
      if (_searchReturn && _searchReturn.focus) _searchReturn.focus();
    }
    if ($("pd-search-btn")) $("pd-search-btn").addEventListener("click", open);
    if ($("pd-mnav-search")) $("pd-mnav-search").addEventListener("click", function () { var x = $("pd-mobnav-x"); if (x) x.click(); open(); });
    if ($("pd-search-x")) $("pd-search-x").addEventListener("click", close);
    ov.addEventListener("click", function (e) { if (e.target === ov) close(); });
    document.addEventListener("keydown", function (e) { if (e.key === "Escape") close(); });
    // single-edit (Levenshtein <= 1) tolerance for typos like "magnetoo" / "akrylic"
    function editLE1(a, b) {
      if (a === b) return true;
      var la = a.length, lb = b.length; if (Math.abs(la - lb) > 1) return false;
      var i = 0, j = 0, edits = 0;
      while (i < la && j < lb) {
        if (a.charAt(i) === b.charAt(j)) { i++; j++; }
        else { if (++edits > 1) return false; if (la > lb) i++; else if (lb > la) j++; else { i++; j++; } }
      }
      if (i < la || j < lb) edits++;
      return edits <= 1;
    }
    function searchUrl(raw) { return "/search?q=" + encodeURIComponent(raw) + "&type=product"; }
    function render(q) {
      if (!results) return;   // Simon 2026-06-26 (audit F6): defensive guard if the results node is absent
      var raw = (input && input.value || "").trim();
      q = (q || "").trim().toLowerCase();
      if (!q) { results.innerHTML = '<div class="pd-search__empty">Start typing to find a display, stand or accessory.</div>'; return; }
      var toks = q.split(/\s+/).filter(Boolean);
      var all = index.filter(function (i) {
        var hay = (i.t + " " + (i.d || "") + " " + (i.k || "")).toLowerCase();
        var words = hay.split(/[^a-z0-9]+/);
        return toks.every(function (tok) {
          if (hay.indexOf(tok) >= 0) return true;                       // substring / synonym hit
          return tok.length >= 4 && words.some(function (w) { return w.length >= 3 && editLE1(tok, w); }); // typo tolerance
        });
      });
      var hits = all.slice(0, 8);
      // Simon 2026-06-25 (design audit #8/C5): always offer a path to the full /search results (Enter also
      // routes there). 2026-09-09 (small truths b): the COUNT is gone. Both sides are PRODUCTS ONLY (searchUrl()
      // above pins &type=product), so the divergence is not the resource type: it is the index and the matching.
      // `all` counts this theme's own baked index (snippets/search-drawer.liquid = title + type + tags + four
      // synonyms, with imageless products, magneto-color and type Ghost dropped: 20 rows of 21 storefront
      // products) under our substring + Levenshtein-1 rules; the link lands on Shopify's index, which matches
      // the full product text of all 21. Verified 2026-09-09: "psa" is 12 here and 17 there. Two indexes cannot
      // be promised to agree, so state no number rather than a wrong one.
      var seeAll = '<a class="pd-sr pd-sr--all" href="' + searchUrl(raw) + '">See all results for &ldquo;' + esc(raw) + '&rdquo; &rarr;</a>';
      results.innerHTML = hits.length ? (hits.map(function (i) {
        return '<a class="pd-sr" href="' + esc(i.u) + '"><img src="' + esc(i.img) + '" alt=""><span class="pd-sr__t">' + esc(i.t) +
          '<span class="pd-sr__d" style="display:block">' + esc(i.d || "") + '</span></span><span class="pd-sr__p">' + esc(i.p) + '</span></a>';
      }).join("") + seeAll) : '<div class="pd-search__empty">No matches for "' + esc(q) + '" here. <a href="' + searchUrl(raw) + '" style="color:var(--lime)">Search the full catalog &rarr;</a> or <a href="/pages/find-your-display" style="color:var(--lime)">take the quiz</a></div>';
    }
    if (input) {
      input.addEventListener("input", function () { render(input.value); });
      input.addEventListener("keydown", function (e) { if (e.key === "Enter") { var v = input.value.trim(); if (v) { e.preventDefault(); location.href = searchUrl(v); } } });
    }
  })();

  /* ---- Simon 2026-06-25 (perf audit #2): in-view play for the Magneto landing videos. They ship
     preload=none + a poster and only load/play when scrolled near (200px rootMargin), pausing when out of
     view. Replaces the old autoplay that streamed ~24MB of 1080p up front on a mobile-first page. ---- */
  (function () {
    var vids = document.querySelectorAll(".pd-magvid"); if (!vids.length) return;
    // Simon 2026-06-26 (a11y / WCAG 2.2.2): respect prefers-reduced-motion AND prefers-reduced-data —
    // never auto-play the looping story videos; expose native controls so the user can opt in. Mirrors
    // the hero-video guard below.
    // Simon 2026-07-02 (code-perf audit #1/#12): gate on the REAL slow/data-saver signal (PD_MEDIA.SLOW =
    // saveData + effectiveType), not prefers-reduced-data (a flag-only feature no shipping browser matches,
    // so Android Data Saver users still downloaded the full 24MB).
    var MAGSLOW = !!(window.PD_MEDIA && window.PD_MEDIA.SLOW);
    // Mobile (<=760px): these clips have no SD rendition (~9.8MB of 1080p each), and the first story video sits
    // inside a tall phone viewport, so autoplay-in-view would still stream ~9.5MB before any real scroll. Show
    // the poster + native controls (tap to play) on phones, matching the hero's poster-only mobile decision.
    // Desktop keeps autoplay-in-view. Reduced-motion + slow/data-saver connections are gated the same way.
    if (matchMedia("(prefers-reduced-motion: reduce)").matches || MAGSLOW || matchMedia("(max-width: 760px)").matches) {
      for (var r = 0; r < vids.length; r++) vids[r].setAttribute("controls", "");
      return;
    }
    if (!("IntersectionObserver" in window)) { for (var k = 0; k < vids.length; k++) vids[k].setAttribute("controls", ""); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        var v = en.target;
        // rootMargin 0px so a story video only starts when actually visible (the old 200px prefetched the ~9.8MB
        // ring before any scroll). No forced preload='auto'; play() buffers progressively from the preload='none' base.
        if (en.isIntersecting) { var p = v.play(); if (p && p.catch) p.catch(function () {}); }
        else { try { v.pause(); } catch (e) {} }
      });
    }, { rootMargin: "0px", threshold: 0.25 });
    for (var i = 0; i < vids.length; i++) io.observe(vids[i]);
  })();

  /* ---- Simon 2026-06-26: Magneto landing hero video. The webp poster (.pd-hero__bg) paints first for a fast
     LCP; the beauty-shot video (.pd-hero__vid) loads and fades in (.on) once it can play. Honors reduced-motion
     and data-saver, where the poster simply stays and the video never loads. ---- */
  (function () {
    var v = document.querySelector(".pd-maghero .pd-hero__vid"); if (!v) return;
    // Simon 2026-07-02 (code-perf audit #1): poster-only on reduced-motion, slow/data-saver connections, and on
    // phones (<=760px). The ~14MB HD hero video (no mobile rendition) never loads there; the WebP poster stays
    // (Simon's mobile design call). Desktop on a normal connection keeps the fade-in beauty shot.
    if (matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    if (window.PD_MEDIA && window.PD_MEDIA.SLOW) return;
    if (matchMedia("(max-width: 760px)").matches) return;
    v.addEventListener("playing", function () { v.classList.add("on"); }, { once: true });
    var start = function () { v.preload = "auto"; var p = v.play(); if (p && p.catch) p.catch(function () {}); };
    if (document.readyState === "complete") start();
    else window.addEventListener("load", start, { once: true });
  })();

  /* ---------------- email capture (footer + slide-up bar) ---------------- */
  (function () {
    // W8-QA26 item1 (Simon): the footer signup is now a real Shopify {% form 'customer' %}
    // (server-side POST -> creates a customer with accepts_marketing=true, which Klaviyo syncs).
    // No JS interception: let the form submit natively so the email is actually captured.
    // blog sticky protect-your-cards CTA (audit 9/36: reveal on scroll, dismissible)
    var bs = $("pd-blogsticky");
    if (bs) {
      var bsx = $("pd-blogsticky-x"), bsDismissed = false;
      var onBsScroll = function () { if (!bsDismissed) bs.hidden = !(window.pageYOffset > 700); };
      window.addEventListener("scroll", onBsScroll, { passive: true });
      if (bsx) bsx.addEventListener("click", function () { bsDismissed = true; bs.hidden = true; });
    }
    // Simon 2026-07-02 (code-perf audit #10): the blog lead-magnet capture was REMOVED. It faked success but
    // transmitted nothing, and delivering the checklist needs a Klaviyo flow (store-side, deferred). Markup gone
    // from article-extras.liquid. Slide-up email-capture popup (pd-ebar) removed earlier at founder request.
    // Simon 2026-07-18: footer signup band re-added, direct-to-Klaviyo client subscribe
    // (Newsletter list WbLVYp -> Welcome Flow). Native Shopify customer form POST is the
    // no-JS fallback. Success state ONLY on 2xx (hard rule after the 07-02 blog-capture
    // fake-success postmortem). Payload shape verified against Klaviyo OpenAPI 2026-07-18.
    var fnl = $("pd-fnl-form");
    if (fnl) {
      var fnlEmail = $("pd-fnl-email"), fnlBtn = $("pd-fnl-btn"), fnlDone = $("pd-fnl-done"), fnlErr = $("pd-fnl-msg");
      // AUDIT-24 W2 F26 (round-1 corrected): pre-size the button at init to fit its widest label, so the
      // JOIN -> JOINING... swap can never move a pixel (min-width at submit time could not stop a flex
      // item growing - the growth WAS the jump). Probe measured off-screen, one-time, ~0 cost.
      var _pinJoinWidth = function () {
        if (!fnlBtn || fnl.hidden) return;   /* round-3 fix: a late fonts.ready after a successful signup would measure the display:none form as 0 */
        try {
          fnlBtn.style.width = "";   // re-runnable: clear an earlier fallback-font pin before re-reading
          var _probe = fnlBtn.cloneNode(false); _probe.removeAttribute("id"); _probe.textContent = "Joining...";
          _probe.style.cssText = "position:absolute;left:-9999px;top:auto;width:auto;visibility:hidden";
          fnlBtn.parentNode.appendChild(_probe);
          fnlBtn.style.width = Math.max(fnlBtn.offsetWidth, _probe.offsetWidth) + "px";
          _probe.parentNode.removeChild(_probe);
        } catch (e2) {}
      };
      // round-2 fix: pd.js is deferred and Neue Haas is font-display:swap, so an immediate measurement
      // reads fallback-font metrics; measure once the faces settle (and that call corrects any early pin).
      _pinJoinWidth();
      try { if (document.fonts && document.fonts.ready) document.fonts.ready.then(_pinJoinWidth); } catch (e3) {}
      fnl.addEventListener("submit", function (e) {
        e.preventDefault();
        if (!fnlEmail.checkValidity()) { fnlEmail.reportValidity(); return; }
        var hp = fnl.querySelector('input[name="pd-fnl-extra"]');
        if (hp && hp.value) { fnl.hidden = true; fnlDone.hidden = false; return; }  // honeypot: swallow bots, send nothing
        // AUDIT-24 W2 F26: the button is pre-sized at init (see wire-up); pin only the row height here
        // so the form -> success swap cannot collapse the band.
        if (fnl.parentNode && !fnl.parentNode.style.minHeight) fnl.parentNode.style.minHeight = fnl.parentNode.offsetHeight + "px";
        fnlBtn.disabled = true; fnlBtn.textContent = "Joining..."; fnlErr.hidden = true;
        fetch("https://a.klaviyo.com/client/subscriptions/?company_id=XLk4Bd", {
          method: "POST",
          headers: { "Content-Type": "application/json", "revision": "2026-07-15" },
          body: JSON.stringify({ data: { type: "subscription",
            attributes: { custom_source: "footer-signup-v2",
              profile: { data: { type: "profile", attributes: { email: fnlEmail.value,
                subscriptions: { email: { marketing: { consent: "SUBSCRIBED" } } } } } } },
            relationships: { list: { data: { type: "list", id: "WbLVYp" } } } } })
        }).then(function (r) {
          if (!r.ok) throw new Error("klaviyo " + r.status);
          // AUDIT-24 W2 F26: form fades out, success line fades in (CSS @starting-style entrance).
          // Branch on PD_MEDIA.REDUCE rather than a CSS guard: the inline transition shorthand would
          // beat any non-important reduced-motion rule (verifier ruling).
          if (window.PD_MEDIA && window.PD_MEDIA.REDUCE) { fnl.hidden = true; fnlDone.hidden = false; }
          else {
            fnl.style.transition = "opacity 140ms var(--ease-out), transform 140ms var(--ease-out)";
            fnl.style.opacity = "0"; fnl.style.transform = "translateY(-4px)";
            setTimeout(function () { fnl.hidden = true; fnlDone.hidden = false; }, 140);
          }
          window.pdEvent("newsletter_signup", { source: "footer-signup-v2" });
          // 2026-08-15 blog-popup suppression: any successful signup marks this browser as subscribed
          // so the blog popup (blog-popup module below) never asks an already-joined reader again;
          // also retire a docked teaser pill on the spot (review fix: state was only read at init)
          try { localStorage.setItem("pd_nl_sub", "1"); } catch (e) {}
          var bpt = document.getElementById("pd-bpop-teaser");
          if (bpt) { bpt.classList.remove("is-in"); bpt.hidden = true; }
        }).catch(function () {
          fnlBtn.disabled = false; fnlBtn.textContent = "Join";
          fnlErr.hidden = false;
        });
      });
    }
  })();

  /* ---------------- quick-add on product cards ---------------- */
  document.querySelectorAll(".pd-qadd").forEach(function (b) {
    b.addEventListener("click", function (e) {
      e.preventDefault(); e.stopPropagation();
      // multi-variant products can't be blind-added (wrong/unfulfillable SKU): send to the PDP to choose
      if (b.dataset.opts) { window.location.href = b.dataset.href; return; }
      // R3 (Shopify): single-variant in-stock cards carry the real Shopify variant id. Add it to the
      // live cart through the same PhantomCart.add path the PDP ATC uses (POST -> refetch -> badge pop
      // + toast). This path is id-only: a card with neither data-opts nor data-variant-id is a template
      // bug, not a reason to blind-add an item the live cart can't resolve.
      // openAfter=false: collection/home quick-add is a BROWSING action, so it confirms with the toast +
      // badge pop and does NOT take over with the drawer (Baymard mini-cart guidance; Simon-approved
      // 2026-06-10). Drawer auto-open stays on the high-intent PDP + quiz adds.
      var variantId = b.dataset.variantId;
      if (!variantId || b.dataset.busy === "1") return;   // no id = nothing to add; busy = POST in flight
      b.dataset.busy = "1";
      var orig = b.innerHTML;
      window.PhantomCart.add({ id: Number(variantId), quantity: 1 }, false)
        .then(function () {
          // R7-queue: emit the master section-5 add_to_cart event on a CONFIRMED add (the PDP + quiz
          // already fire it; the quick-add path was the gap). Fires only on success now that postAdd rejects on failure.
          if (window.pdEvent) window.pdEvent("add_to_cart", { handle: b.dataset.h, variantId: Number(variantId), price: parseFloat(b.dataset.p) || 0, qty: 1, currency: (_cart && _cart.currency) || PD_CUR, source: "quick_add" });
          b.classList.add("ok"); b.innerHTML = "Added";
          toast((b.dataset.t || "Item") + " added");
          setTimeout(function () { b.classList.remove("ok"); b.innerHTML = orig; b.dataset.busy = "0"; }, 1400);
        })
        .catch(function () {
          b.innerHTML = orig; b.dataset.busy = "0";
          toast("Sorry, could not add to cart", "Couldn't add");
        });
    });
  });

  /* ---------------- Find-My-Display fit engine (data-driven, grader-first) ---------------- */
  (function () {
    var root = document.getElementById("pd-quiz");
    if (!root) return;
    var D = {}; try { D = JSON.parse(root.dataset.pdFit); } catch (e) { return; }
    var R = D.results || {}, RAW = D.raw;
    var graderById = {}; (D.graders || []).forEach(function (g) { graderById[g.id] = g; });   // Simon 2026-07-05: for the TAG single-size skip
    var mount = document.getElementById("pd-quiz-mount");
    var dots = document.getElementById("pd-quiz-dots");
    var back = document.getElementById("pd-quiz-back");
    var intro = document.getElementById("pd-quiz-intro");
    // 2026-09-09 instrumentation repair: this module calls step(0) synchronously at parse time, so
    // fitfinder_start fired while window.gtag was still undefined (pd.js is deferred; the Shopify
    // Google channel installs gtag 164-264 ms later). window.pdEvent pushes to the dataLayer, which
    // gtag.js never reads as an event, so the start was lost while every later quiz event landed
    // (GA4 90d: 207 starts vs 2,004 completes). Route every quiz event through the buffered emitter
    // instead. Event names and params are byte-identical; pdFlush sends synchronously once gtag is a
    // function, so on a normal session post-boot events behave as before. NOT so on a slow one: past
    // pdFlush's ~4950 ms budget the shared _pdNoGtag latch has engaged and every quiz event stays
    // dataLayer-only, where master's re-checking emitter recovered. Kept on purpose so start and
    // complete fail together and the rate stays unbiased. Do not un-latch: see ARCH 29.1.
    var ev = pdEventQ;

    var STEPS = [
      { id: "grader", q: "Which company graded your card?",
        opts: (D.graders || []).map(function (g) { return { v: g.id, b: g.label, s: g.note || "" }; })
              .concat([{ v: "raw", b: "Raw / ungraded card", s: "", result: "raw" }]) },
      { id: "bumper", q: "Is your card using a protective case?",
        opts: (D.bumpers || []).map(function (b) { return { v: b.id, b: b.label, s: b.sub || "" }; }) },
      { id: "thickness", q: "Is it a standard slab, or thick?",
        opts: (D.thickness || []).map(function (t) { return { v: t.id, b: t.label, s: t.sub || "" }; }) },
      { id: "mounting", q: "How do you want to display it?",
        opts: (D.mounting || []).map(function (m) { return { v: m.id, b: m.label, s: m.sub || "" }; }) }
    ];
    var ans = {}, idx = 0, started = false, history = [], auto = {};

    /* ---------------- wave3 item6: outcome-aware skip logic ----------------
       A pending question is SKIPPABLE when, holding the answers given so far, no
       assignment of the other still-pending questions makes this question's answer
       change the final verdict. A skippable question is auto-advanced (a representative
       value is filled, since every value resolves identically here) and never shown.
       Verified against the live-baked fit data (skip-path-table.md): GradedGuard
       collapses the thickness step for every grading company, matching Simon's case. */
    function dimVals(i) {
      return STEPS[i].opts.filter(function (o) { return o.v !== "raw"; }).map(function (o) { return o.v; });
    }
    function sigFor(a) {
      // the verdict the user actually sees: product + stock state + preselected variant; gap = no known match
      var r = R[[a.grader, a.bumper, a.thickness, a.mounting].join("|")];
      if (!r || r.coverage === "gap") return "gap";
      return (r.handle || "") + "|" + (r.coverage || "") + "|" + ((r.opts && r.opts.join("~")) || "");
    }
    function isSkippable(i) {
      if (i <= 0 || ans[STEPS[i].id] != null) return false;   // grader is always asked; answered dims never skipped
      var others = [], j;
      for (j = 1; j < STEPS.length; j++) { if (j !== i && ans[STEPS[j].id] == null) others.push(j); }
      var myVals = dimVals(i);
      if (myVals.length <= 1) return true;
      var combos = [{}];                                      // cartesian product over the OTHER pending dims
      others.forEach(function (oj) {
        var next = [];
        dimVals(oj).forEach(function (v) { combos.forEach(function (c) { var cc = {}; for (var k in c) cc[k] = c[k]; cc[STEPS[oj].id] = v; next.push(cc); }); });
        combos = next;
      });
      for (var ci = 0; ci < combos.length; ci++) {
        var base = { grader: ans.grader, bumper: ans.bumper, thickness: ans.thickness, mounting: ans.mounting }, kk;
        for (kk in combos[ci]) base[kk] = combos[ci][kk];
        var sig0 = null;
        for (var vi = 0; vi < myVals.length; vi++) {
          base[STEPS[i].id] = myVals[vi];
          var s = sigFor(base);
          if (sig0 === null) sig0 = s; else if (s !== sig0) return false;   // this question can change the verdict -> keep it
        }
      }
      return true;
    }
    /* ---- 2026-06-11 catalog correction: dead-BUMPER pruning ----
       A bumper option is OFFERED only if at least one assignment of the still-pending
       questions reaches a product (non-gap). Data-driven from R, no per-grader special
       cases: TAG stops offering the Slabmag/GradedGuard bumpers because no TAG bumper
       product exists (Simon-confirmed: those holders are not made for TAG), while CGC
       keeps both. ONLY the bumper step prunes: thickness and mounting describe the
       collector's slab and preference, which exist regardless of the catalog, so their
       dead values must stay selectable and resolve to the honest gap + capture screen
       (collapsing them would silently assume an answer and recommend a wrong-fit
       display, e.g. SGC thick -> standard Ultra). The grader step is likewise exempt.
       If exactly one bumper survives, step() collapses the question like a wave3 skip,
       so the path length and dots stay honest. */
    function liveVals(i) {
      var others = [], j;
      for (j = 1; j < STEPS.length; j++) { if (j !== i && ans[STEPS[j].id] == null) others.push(j); }
      var combos = [{}];
      others.forEach(function (oj) {
        var next = [];
        dimVals(oj).forEach(function (v) { combos.forEach(function (c) { var cc = {}; for (var k in c) cc[k] = c[k]; cc[STEPS[oj].id] = v; next.push(cc); }); });
        combos = next;
      });
      return dimVals(i).filter(function (v) {
        for (var ci = 0; ci < combos.length; ci++) {
          var base = { grader: ans.grader, bumper: ans.bumper, thickness: ans.thickness, mounting: ans.mounting }, kk;
          for (kk in combos[ci]) base[kk] = combos[ci][kk];
          base[STEPS[i].id] = v;
          if (sigFor(base) !== "gap") return true;
        }
        return false;
      });
    }
    // the honest dynamic path = every step EXCEPT the ones actually auto-skipped so far. The count
    // shrinks exactly when step() collapses a question, and never pre-judges an answer the user has
    // not given yet (pre-judging would also mis-read the all-"gap" prefix at the grader step and
    // wrongly hide every later step). "Answer up to four questions" -> the denominator starts at the
    // max and drops as the chosen answers prove steps unnecessary.
    function plannedSteps() {
      var seq = [];
      for (var i = 0; i < STEPS.length; i++) { if (!auto[STEPS[i].id]) seq.push(i); }
      return seq;
    }

    function renderDots(active) {
      var seq = plannedSteps(), pos = seq.indexOf(active);
      if (pos < 0) pos = seq.length - 1;
      dots.innerHTML = "";
      // a11y: name the progress with the DYNAMIC length so the dots never overstate the path (wave3 item6)
      dots.setAttribute("role", "group");
      dots.setAttribute("aria-label", "Step " + (pos + 1) + " of " + seq.length);
      for (var i = 0; i < seq.length; i++) {
        var d = document.createElement("i");
        if (i <= pos) d.className = "on";
        if (i === pos) d.setAttribute("aria-current", "step");
        dots.appendChild(d);
      }
    }
    function step(i) {
      // wave3 item6: auto-advance past any pending question whose answer cannot change the verdict;
      // catalog-correction pruning: also collapse a question once only one offerable option remains.
      var lv = null;
      while (i > 0 && i < STEPS.length) {
        // Simon 2026-07-05: single-size graders (TAG has no thick slab) skip the thickness step, defaulting to standard.
        if (STEPS[i].id === "thickness" && graderById[ans.grader] && graderById[ans.grader].oneSize) {
          ans[STEPS[i].id] = dimVals(i)[0];   // standard (first thickness value)
          auto[STEPS[i].id] = true;
          ev("fitfinder_step_skip", { step: STEPS[i].id });
          i++; lv = null; continue;
        }
        if (isSkippable(i)) {
          ans[STEPS[i].id] = dimVals(i)[0];   // representative value (all values resolve to the same verdict here)
          auto[STEPS[i].id] = true;
          ev("fitfinder_step_skip", { step: STEPS[i].id });
          i++; lv = null; continue;
        }
        if (STEPS[i].id === "bumper") {
          lv = liveVals(i);
          if (lv.length === 1) {
            ans[STEPS[i].id] = lv[0];         // the only bumper that can reach a product
            auto[STEPS[i].id] = true;
            ev("fitfinder_step_prune", { step: STEPS[i].id, value: lv[0] });
            i++; lv = null; continue;
          }
        }
        break;
      }
      if (i >= STEPS.length) { resolve(); return; }   // every remaining question was answered or skipped
      idx = i;
      if (!started) { started = true; ev("fitfinder_start", {}); }
      if (intro) intro.style.display = i > 0 ? "none" : "";
      back.hidden = history.length === 0;
      renderDots(i);
      var s = STEPS[i];
      // dead-bumper pruning: on the bumper step, offer only values that can reach a product
      var offered = (s.id !== "bumper") ? s.opts : s.opts.filter(function (o) { return o.result || (lv || liveVals(i)).indexOf(o.v) !== -1; });
      var opts = offered.map(function (o) {
        return '<button class="pd-quiz__opt" data-v="' + o.v + '"' + (o.result ? ' data-result="' + o.result + '"' : '') +
          '><b>' + o.b + '</b>' + (o.s ? '<span>' + o.s + '</span>' : '') + '</button>';
      }).join("");
      // wave3 item5 (w3-liquid x W3-JS): one quiet "expanding compatibility" help line under the
      // grader options, rendered from the baked editorial D.grader_help (trusted HTML, same source as the option copy).
      var help = (s.id === "grader" && D.grader_help) ? '<div class="pd-quiz__help">' + D.grader_help + '</div>' : '';
      // #13 (Simon 2026-07-02): mount.innerHTML destroys the focused option, dropping focus to <body> with no
      // announcement. Move focus to the new question heading, but ONLY on a real answer transition (focus was
      // inside the quiz), never on the first render, so we don't scroll/steal focus on page load.
      var wasInQuiz = mount.contains(document.activeElement);
      mount.innerHTML = '<div class="pd-quiz__step"><h2 class="pd-quiz__q" tabindex="-1">' + s.q + '</h2><div class="pd-quiz__opts">' + opts + '</div>' + help + '</div>';
      if (wasInQuiz) { var _qh = mount.querySelector(".pd-quiz__q"); if (_qh) _qh.focus(); }
      mount.querySelectorAll(".pd-quiz__opt").forEach(function (btn) {
        btn.addEventListener("click", function () {
          if (btn.dataset.result === "raw") { ans = { grader: "raw" }; return showRaw(); }
          ans[s.id] = btn.dataset.v;
          history.push(i);
          ev("fitfinder_step_complete", { step: s.id, value: btn.dataset.v });
          step(i + 1);   // step() resolves once every remaining question is answered or skipped
        });
      });
    }
    // wave3 item7 (Simon, consciously reversing part of R6): the verdict card's "Add to cart"
    // button was removed. VIEW (carrying the ?sel preselection) is now the single primary CTA,
    // so the dormant addVariant() add path was deleted with it.
    function viewUrl(r) { return r.url + (r.opts && r.opts.length ? "?sel=" + encodeURIComponent(r.opts.join("~")) : ""); }
    function altCards(alts) {
      // 2026-08-28: alts carry their own ?sel= preselection now, the same way the primary CTA does.
      // Before this the card linked to the bare product URL, so a BGS shopper following the Ultra
      // Color alt landed on whichever variant Shopify defaults to. Liquid only emits alts that have
      // an in-stock fitting variant, so there is never a dead alt card to badge.
      if (!alts || !alts.length) return "";
      return '<div class="pd-qresult__alts"><h4>You might also like</h4><div class="pd-qresult__altrow">' +
        alts.map(function (a) {
          return '<a class="pd-card" href="' + viewUrl(a) + '"><div class="pd-card__media"><img src="' + a.img + '" alt="' + a.title + '" loading="lazy"></div>' +
            '<div class="pd-card__body"><div class="pd-card__title">' + a.title + '</div><div class="pd-card__price">From ' + a.price + '</div>' +
            (a.note ? '<div class="pd-card__desc">' + a.note + '</div>' : '') + '</div></a>';
        }).join("") + '</div></div>';
    }
    function finishUI(inner) {
      back.hidden = true; dots.innerHTML = ""; dots.removeAttribute("role"); dots.removeAttribute("aria-label"); if (intro) intro.style.display = "none";
      var wasInQuiz = mount.contains(document.activeElement);   // #13: move focus to the verdict (the tool's entire output) on a real transition
      mount.innerHTML = '<div class="pd-quiz__step pd-qresult">' + inner +
        '<button class="pd-quiz__restart" id="pd-quiz-restart">&#8634; Start over</button></div>';
      if (wasInQuiz) { var _rh = mount.querySelector(".pd-qresult h2, .pd-qresult__tag"); if (_rh) { _rh.setAttribute("tabindex", "-1"); _rh.focus(); } }
      var rs = document.getElementById("pd-quiz-restart");
      if (rs) rs.addEventListener("click", function () { ans = {}; started = false; history = []; auto = {}; step(0); });
    }
    function showRaw() {
      // Simon 2026-06-26: the raw/ungraded answer has exactly one match (Ungraded One-Touch), so the verdict
      // card was an unnecessary extra step. Go straight to the product page. Analytics still fire first.
      ev("fitfinder_complete", { coverage: "raw" });
      ev("fitfinder_result_view", { handle: "ungraded" });
      window.location.href = (RAW && RAW.url) ? RAW.url : "/collections/all";
    }
    function resolve() {
      var key = [ans.grader, ans.bumper, ans.thickness, ans.mounting].join("|");
      var r = R[key];
      ev("fitfinder_complete", { coverage: r ? r.coverage : "none", key: key });
      if (!r || r.coverage === "gap") {
        // Simon 2026-07-02 (code-perf audit #10): the gap email capture was removed (faked success, sent nothing).
        // The gap outcome now points to the full range instead of promising a follow-up we never sent.
        finishUI(
          '<div class="pd-qresult__tag pd-qresult__tag--gap">No exact match yet</div>' +
          '<h2>Browse the full range</h2>' +
          '<p class="pd-qresult__why">' + ((r && r.reason) || "We could not find an exact in-stock match for that combination.") + '</p>' +
          '<div class="pd-qresult__cta" style="margin-top:14px"><a class="pd-cta pd-cta--view" href="/collections/all">Browse all displays</a></div>');
        return;
      }
      ev("fitfinder_result_view", { handle: r.handle, coverage: r.coverage });
      var oos = r.coverage === "oos";
      /* 2026-08-28 (Simon): the dead-bumper prune STAYS, because a question whose answers all dead-end
         is a worse flow than no question. But it silently answers "just the graded card" on the
         shopper's behalf for TAG and SGC, the only graders with no GradedGuard or Slabmags product, so
         a collector whose slab is already in a case was shown a bare-slab display as a confident match.
         Disclose the assumption instead of asking. Fires ONLY when the step was auto-answered AND the
         assumed value was "none", so it disappears by itself the day a TAG or SGC bumper product ships,
         with no code change. Wording is deliberately a fit statement, not an apology. */
      var assumed = (auto.bumper && ans.bumper === "none")
        ? '<p class="pd-qresult__why">Sized for a bare slab. GradedGuard and Slabmags cases are not made for '
          + esc((graderById[ans.grader] || {}).label || "") + ' slabs, so we have no display for that combination.</p>'
        : "";
      // Simon 2026-07-02 (code-perf audit #10): the OOS "notify me" quiz form was removed (faked success, sent
      // nothing). Send the shopper to the product page instead, where the STOQ back-in-stock widget lives.
      var cta = '<div class="pd-qresult__cta"><a class="pd-cta pd-cta--view" href="' + viewUrl(r) + '">View ' + r.title + '</a></div>';   // wave3 item7: VIEW is the single primary CTA (Add to cart removed)
      finishUI(
        '<div class="pd-qresult__tag">' + (oos ? "Your fit (out of stock)" : "Your match") + '</div>' +
        '<div class="pd-qresult__media"><img src="' + r.img + '" alt="' + r.title + '"></div>' +
        '<h2>' + r.title + '</h2>' +
        ((r.opts && r.opts.length) ? '<div class="pd-qresult__variant">' + r.opts.join(" / ") + '</div>' : '') +
        '<div class="pd-qresult__price">' + r.price + (oos ? "  ·  out of stock" : "") + '</div>' +
        '<p class="pd-qresult__why">' + (r.reason || "") + '</p>' + assumed +
        cta + altCards(r.alternatives));
    }
    back.addEventListener("click", function () {
      // wave3 item6: return to the previous REAL (shown) question, clearing it plus any auto-skipped
      // dims after it so they recompute against the revised answers.
      if (!history.length) return;
      var prevI = history.pop();
      for (var k = prevI; k < STEPS.length; k++) { delete ans[STEPS[k].id]; delete auto[STEPS[k].id]; }
      step(prevI);
    });
    step(0);
  })();

  /* ---------------- PDP buy engine (variant + availability aware) ---------------- */
  var root = document.querySelector(".pd-pdp[data-pd-product]");
  if (root) {
    var data = {}; try { data = JSON.parse(root.dataset.pdProduct); } catch (e) { data = { options: [], variants: [], base: "0" }; }
    var opts = data.options || [];
    var variants = data.variants || [];
    // Simon 2026-07-06: "dorder" = the selector DISPLAY hierarchy from the template (Type, Grader,
    // Card Size, Style, Color). The availability cascade below follows it instead of the raw Shopify
    // option order: Authenticator is the LAST raw option on every grader product, so cascading by raw
    // index judged a grader against the selected style and struck PSA/CGC when only one style sold out.
    // dpos[real option index] = position in the display hierarchy. Fallback = raw order.
    var dorder = (data.dorder && data.dorder.length === opts.length) ? data.dorder : opts.map(function (_, i) { return i; });
    var dpos = [];
    dorder.forEach(function (oi, p) { dpos[oi] = p; });
    // ITEM 3 (2026-09-09): the GRADER option index, by the same name test snippets/collection-card.liquid uses
    // for its facet data ("authentic" or "grad" in the lowercased option name). -1 on the 8 products with no
    // grader axis (gradedguard, ungraded, the magneto stands, the bags), which turns grader protection off for
    // them entirely. acrylic-display-stand DOES have an Authenticator option and IS protected.
    var gidx = -1;
    opts.forEach(function (o, i) {
      var n = String((o && o.name) || "").toLowerCase();
      if (gidx < 0 && (n.indexOf("authentic") >= 0 || n.indexOf("grad") >= 0)) gidx = i;
    });
    var qty = 1;
    (function () { var dt = root.querySelector(".pd-qcard.on"); if (dt) qty = +dt.dataset.qty || 1; })();
    var ev = function (n, p) { if (window.pdEvent) window.pdEvent(n, p); };
    /* oos_dead_end (2026-07-31): the shopper's current selection is a build they cannot buy. At most
       ONCE per page view, both call sites sharing one flag. Read-only: no DOM, URL or cart write. combo
       joins with "~", this file's own ?sel= separator, because option values contain "/" (PSA/CGC). */
    var _deadFired = false;
    function hnd() { return String(data.handle || "").slice(0, 100); }
    function landVia() {
      /* qp / _vid are the raw ?sel= and ?variant= strings from lines 1425 and 1432. ?sel= is written
         only by the fit finder (1296) and by this file's own click handler (1735), so land_sel means
         quiz-routed or self-inflicted, never a bare shared link. land_nolink must stay ZERO: a product
         WITH buyable variants landing dead un-deep-linked means the 07-26 landing fix has regressed. */
      if (!variants.some(function (v) { return v.available; })) return "land_all_oos";
      return qp ? "land_sel" : (_vid ? "land" : "land_nolink");
    }
    function fireDeadEnd(via) {
      try {
        if (_deadFired || isAvail()) return;
        _deadFired = true;
        var cv = currentVariant();
        pdEventQ("oos_dead_end", { handle: hnd(), vid: (cv && cv.id) ? String(cv.id) : "none", combo: sel.join("~").slice(0, 100), via: via || landVia() });
      } catch (e) {}
    }

    // W8-QA item3: sticky-bar observer that tracks the ACTIVE primary CTA, so the bar's show/hide on scroll
    // is identical whether the selected variant is in stock (the in-page Add to cart) or out of stock (the
    // notify block). setBuyState() calls observeCTA() whenever the availability state flips.
    var _sbar = $("pd-sbar"), _io = null, _ioAvail = null;
    function observeCTA(avail) {
      if (!_sbar || !("IntersectionObserver" in window)) return;
      if (_io) { _io.disconnect(); _io = null; }
      var target = avail
        ? [].slice.call(root.querySelectorAll(".pd-atc")).filter(function (b) { return !b.closest(".pd-sbar"); })[0]
        : $("pd-oos-area");
      if (!target) { _sbar.classList.remove("vis"); return; }
      // AUDIT-24 W2 F14: shrink the observer root by bar + target + clearance, measured (never hardcoded),
      // so the in-page CTA is fully clear of the bar before the bar retracts and fully hidden before it
      // returns. No time-lockout hysteresis - IntersectionObserver does not re-deliver a discarded entry,
      // so a time gate would strand the class (verifier ruling).
      var _rm = ((_sbar.offsetHeight || 69) + (target.offsetHeight || 68) + 24);
      _io = new IntersectionObserver(function (ents) {
        ents.forEach(function (x) { _sbar.classList.toggle("vis", !x.isIntersecting); });
      }, { threshold: 0, rootMargin: "0px 0px -" + _rm + "px 0px" });
      _io.observe(target);
    }

    function vKey(a) { return (a || []).join("␟"); }
    var byKey = {};
    variants.forEach(function (v) { byKey[vKey(v.opts)] = v; });

    // default selection = first IN-STOCK variant (never land on a sold-out default)
    var firstAvail = variants.filter(function (v) { return v.available; })[0]
                  || variants[0] || { opts: opts.map(function (o) { return o.values[0]; }) };
    var sel = (firstAvail.opts || []).slice();
    // Simon 2026-07-06: the Grader/Authenticator option defaults to its PSA value on EVERY product,
    // regardless of stock; within that grader the first in-stock combination wins (else its first
    // combination, shown as OOS). The ?sel= and ?variant= deep links below still take precedence.
    // Mirrors the identical rule in main-product.liquid so the server-rendered state matches.
    (function () {
      var gi = -1;
      opts.forEach(function (o, i) { if (gi < 0 && (o.name === "Grader" || o.name === "Authenticator")) gi = i; });
      if (gi < 0) return;
      var psa = (opts[gi].values || []).filter(function (v) { return String(v).indexOf("PSA") !== -1; })[0];
      if (!psa) return;
      var pool = variants.filter(function (v) { return v.opts[gi] === psa; });
      // Simon 2026-07-26: the "|| pool[0]" fallback landed the shopper on a SOLD-OUT PSA variant
      // whenever no PSA combination was buyable. Removed. With no buyable PSA option `pick` is now
      // undefined and `sel` stays on firstAvail above, i.e. the first variant that can actually be
      // bought. The PSA default is untouched wherever a PSA variant IS buyable. Mirrors the identical
      // change in main-product.liquid so the server-rendered state and the JS still agree.
      var pick = pool.filter(function (v) { return v.available; })[0];
      if (pick) sel = pick.opts.slice();
    })();
    // quiz -> PDP pre-selection: ?sel=Single~Rounded~BGS (P3.4)
    try {
      var qp = new URLSearchParams(location.search).get("sel");
      if (qp) { var want = qp.split("~"); if (byKey[vKey(want)]) { sel = want.slice(); } }
      // W8-QA6 item1: the "Matched to your slab" fit-verdict banner was removed (Simon).
    } catch (e) {}
    // W8 item4: honor the Shopify-standard ?variant=<id> (what STOQ and copied product links use) so loading an
    // OOS variant URL actually selects that variant and shows the OOS capture. Takes precedence over ?sel.
    try {
      var _vid = new URLSearchParams(location.search).get("variant");
      if (_vid) { var _vm = variants.filter(function (v) { return String(v.id) === String(_vid); })[0]; if (_vm) sel = _vm.opts.slice(); }
    } catch (e) {}

    function currentVariant() { return byKey[vKey(sel)] || null; }
    // 360 viewer hook (Simon 2026-06-21): broadcast the selected option values so the variant-aware
    // prism hero can swap to the matching spin set. Fires on every selection change and once at load.
    function notifyVariant() { try { root.dispatchEvent(new CustomEvent("pd:variant", { bubbles: true, detail: { sel: sel.slice() } })); } catch (e) {} }
    function variantPrice() { var v = currentVariant(); return v ? (parseFloat(v.price) || 0) : (variants.length ? 0 : (parseFloat(data.base) || 0)); }
    function isAvail() { var v = currentVariant(); return v ? !!v.available : (variants.length ? false : true); }
    function unit() { return variantPrice(); }
    // PERFECT 10: the line price this page can PROMISE. 10% only when THIS display alone reaches the
    // minimum, so the page never shows a saving the cart will not give. A cart that qualifies through a
    // MIX of displays is real too; the drawer's row labels and nudge cover that case, not this page.
    var p10 = vol10Item(data.handle);
    function lineTotal(u, q) {
      if (!(p10 && vol10On() && q >= VOL10.min)) return u * q;
      var c = Math.round(u * q * 100);
      return (c - Math.round(c * VOL10.pct / 100)) / 100;
    }
    function variantLabel() {
      var parts = opts.length ? [sel.join(" / ")] : [];
      return parts.join("  ");
    }
    // classify a value for option oi. W8-QA6 items 4+5: CASCADING availability. A value is judged only against
    // the options ABOVE it, not the ones below. Simon 2026-07-06: "above" now means the DISPLAY hierarchy
    // (dpos, from the template's dorder), not the raw option index, so what the shopper sees cascades
    // top-to-bottom exactly as rendered: a Grader is struck only when EVERY style/colour under it (given the
    // selected Type) is sold out, and the sold-out style itself takes the strike instead. "none" = no SKU for
    // this value given the higher picks (caller hides it); "oos" = exists but all sold out (shown, struck,
    // still selectable for STOQ capture); "ok" = buyable.
    function classify(oi, val) {
      var ms = variants.filter(function (v) {
        if (v.opts[oi] !== val) return false;
        for (var oj = 0; oj < opts.length; oj++) { if (oj !== oi && dpos[oj] < dpos[oi] && v.opts[oj] !== sel[oj]) return false; }
        return true;
      });
      if (!ms.length) return "none";
      return ms.some(function (v) { return v.available; }) ? "ok" : "oos";
    }
    // ITEM 3 (2026-09-09): does ANY variant carry val at oi under the grader the shopper has selected?
    // The single test behind both halves of grader protection: the click guard in snap() and the disabled
    // rendering in syncActive(). gidx < 0 (no grader option) makes it constantly true, so every gradeless
    // product keeps its pre-item-3 behaviour byte for byte.
    function graderHas(oi, val) {
      if (gidx < 0) return true;
      for (var i = 0; i < variants.length; i++) {
        if (variants[i].opts[oi] === val && variants[i].opts[gidx] === sel[gidx]) return true;
      }
      return false;
    }
    // if a pick makes the combo non-existent, snap to the best real variant containing val@oi
    // ITEM 3 (2026-09-09): snap may never move the GRADER. It used to sort the whole variant list for val@oi,
    // so clicking Triple on a Single-only grader (phantom-ultra SGC) silently re-graded the shopper to
    // PSA/CGC/TAG and doubled the price. A Type/Style/Colour pick is now resolved INSIDE the selected grader
    // and, when that grader has no such SKU, refused: returns false and the caller restores the previous
    // selection (the pill is rendered disabled by syncActive, so a real pointer never gets here).
    // A GRADER pick (oi === gidx) is unconstrained, so ruling 2026-08-31 stands: Triple then SGC still lands
    // on Single / Rounded / SGC. Returns true when sel now holds a real combo.
    function snap(oi, val) {
      if (byKey[vKey(sel)]) return true;
      var keep = (gidx >= 0 && oi !== gidx) ? sel[gidx] : null;
      var cand = variants.filter(function (v) { return v.opts[oi] === val && (keep === null || v.opts[gidx] === keep); });
      if (!cand.length) return false;
      cand.sort(function (a, b) {
        var am = 0, bm = 0, oj;
        for (oj = 0; oj < opts.length; oj++) { if (oj !== oi) { if (a.opts[oj] === sel[oj]) am++; if (b.opts[oj] === sel[oj]) bm++; } }
        if (a.available !== b.available) return a.available ? -1 : 1;
        if (bm !== am) return bm - am;
        return (parseFloat(a.price) || 0) - (parseFloat(b.price) || 0);
      });
      sel = cand[0].opts.slice();
      return true;
    }
    function syncActive() {
      var gv = (gidx >= 0) ? sel[gidx] : null, gsel = [];
      root.querySelectorAll(".pd-pill,.pd-swatch,.pd-style").forEach(function (el) {
        var oi = +el.dataset.opt, val = el.dataset.val, cls = opts.length ? classify(oi, val) : "ok";
        var on = sel[oi] === val;
        // ITEM 3 (2026-09-09): unavailable ONLY because of the selected grader. Shown disabled instead of
        // hidden (the shopper must be able to see what the grader costs them), never struck (pd-oos means
        // "sold out", this one is "not made"), never a tab stop, and refused by snap() if clicked anyway.
        // The selected value always has a SKU under its own grader, so `on` can never be disabled.
        var gna = (oi !== gidx) && !graderHas(oi, val);
        el.classList.toggle("on", on);
        el.setAttribute("aria-checked", on ? "true" : "false");
        el.tabIndex = on ? 0 : -1;   // A4: roving tabindex - only the checked radio is a tab stop (the selected value is never pd-na)
        el.classList.toggle("pd-oos", !gna && cls === "oos");
        var na = !gna && (cls === "none");
        // W8-QA6 items 4+5: a "none" value has no SKU for the higher picks (e.g. a Metallic-only colour while
        // Type=Standard, or Jewel+CGC). HIDE it so each option reflects the selection above it, instead of
        // showing a long row of struck options. STOQ capture is unaffected: those combos have no variant id to
        // notify on anyway; the real OOS variants stay visible (struck) and selectable. The selected value is
        // always a real variant (snap guarantees it), so it is never hidden.
        el.classList.toggle("pd-na", na);
        el.style.display = (na && !on) ? "none" : "";
        if (gna) { el.setAttribute("aria-disabled", "true"); var g = el.closest(".pd-sel"); if (g && gsel.indexOf(g) < 0) gsel.push(g); }
        else el.removeAttribute("aria-disabled");
      });
      // ITEM 3 (2026-09-09): one short note per group that has a disabled value, so the dimming is explained
      // rather than mysterious. Reuses .pd-sel__note styling; [data-gna] is the handle for updating/removing it.
      // aria-describedby ties it to the radiogroup: roving skips the disabled pill, so without the description
      // a keyboard / screen-reader user would arrow past the missing value and never be told it exists.
      root.querySelectorAll(".pd-sel").forEach(function (g) {
        var n = g.querySelector("[data-gna]"), rg = g.querySelector('[role="radiogroup"]');
        if (gsel.indexOf(g) < 0) { if (n) { n.remove(); if (rg) rg.removeAttribute("aria-describedby"); } return; }
        if (!n) {
          n = document.createElement("div"); n.className = "pd-sel__note"; n.setAttribute("data-gna", "");
          n.id = "pd-gna-" + g.querySelector("[data-opt]").dataset.opt;
          g.appendChild(n); if (rg) rg.setAttribute("aria-describedby", n.id);
        }
        n.textContent = "Not available for " + gv;
      });
      opts.forEach(function (o, oi) {
        var lbl = root.querySelector('[data-valfor="' + oi + '"]'); if (lbl) lbl.textContent = sel[oi];
      });
    }
    // ITEM 3 (2026-09-09): screen-reader announcement of the build the shopper just selected. Lives in the buy
    // box (.pd-vh = the theme's visually-hidden class), so it costs no pixel and no layout; polite, so it never
    // interrupts. liveRegion() is called EMPTY AT BOOT, never lazily from announceSel: a live region that is
    // injected and written in the same task is routinely dropped by NVDA / JAWS / VoiceOver, which would
    // silence the FIRST selection change of every page view (the grader pick that matters most). The region
    // must already be parked in the accessibility tree and idle before anything is written into it.
    var _live = null;
    function liveRegion() {
      if (_live) return _live;
      _live = root.querySelector("[data-pd-live]");
      if (!_live) {
        _live = document.createElement("div");
        _live.className = "pd-vh"; _live.setAttribute("data-pd-live", "");
        _live.setAttribute("role", "status"); _live.setAttribute("aria-live", "polite");
        (root.querySelector(".pd-bbox") || root).appendChild(_live);
      }
      return _live;
    }
    // na = the value a refused click asked for. Announcing the unchanged build again would just repeat the old
    // string with no explanation, so say why nothing moved instead.
    function announceSel(na) {
      liveRegion().textContent = (na && gidx >= 0) ? na + " not available for " + sel[gidx]
        : sel.join(" / ") + ", " + moneyShort(unit());
    }
    // W8 item4: STOQ (Restock Rocket) injects its real notify form into / after #pd-stoq-mount once it detects
    // the selected variant is OOS (via the ?variant= URL param pd.js maintains). When that widget is present,
    // hide the v2 fallback form so only the real, syncing STOQ form shows; until then the fallback is the capture.
    function syncStoq() {
      var fb = $("pd-notify-wrap"); if (!fb) return;
      // STOQ injects nodes whose class/id contains "restock-rocket" (e.g. #restock-rocket-inline-container,
      // .restock-rocket-wrapper-inline) INTO #pd-stoq-mount. The two anchors there do not, so this is a clean test.
      var found = !!document.querySelector('#pd-stoq-mount [class*="restock-rocket"], #pd-stoq-mount [id*="restock-rocket"]');
      fb.hidden = found;
    }
    function setBuyState() {
      var avail = isAvail();
      // the in-page Add to cart is disabled when OOS (it is hidden in OOS anyway). The STICKY button stays
      // ENABLED in both states: in stock it adds to cart, out of stock it becomes a clickable "Out of stock"
      // control that scrolls up to the notify form (W8-QA6 item3).
      var ipAtc = $("pd-atc"); if (ipAtc) { ipAtc.disabled = !avail; ipAtc.classList.toggle("pd-btn--off", !avail); }
      var sb = $("pd-atc2");
      if (sb) {
        sb.disabled = false; sb.classList.remove("pd-btn--off"); sb.classList.toggle("pd-sbar__btn--oos", !avail);
        var buyL = sb.querySelector(".pd-sbar__buy"), oosL = sb.querySelector(".pd-sbar__oos");
        if (buyL) buyL.hidden = !avail; if (oosL) oosL.hidden = avail;
      }
      // W8 item4: the OOS capture area (#pd-oos-area, in the buy box where Quantity sits) shows when the selected
      // variant is out of stock. Entering the OOS state, poll for the STOQ widget so the v2 fallback yields to it.
      var oos = $("pd-oos-area"); if (oos) oos.hidden = avail;
      if (!avail) { syncStoq(); setTimeout(syncStoq, 400); setTimeout(syncStoq, 1200); setTimeout(syncStoq, 2500); }
      // W5 item3: lead with Notify-me when the selected variant is out of stock. The ENTIRE quantity module is
      // one wrapper (#pd-qmod: the "Quantity" head, the tier cards, the "Or enter an exact amount" stepper and
      // the free-ship bar), so hiding it is atomic; no sub-element (the old orphaned stepper label) can be left
      // behind. The buy row + installment line + sticky buy button follow the same state. On a fully-unbuyable
      // product the server omits #pd-qmod and .pd-buyR entirely, so these lookups are null and safely skipped.
      var qmod = $("pd-qmod"); if (qmod) qmod.style.display = avail ? "" : "none";
      var br = root.querySelector(".pd-buyR"); if (br) br.style.display = avail ? "" : "none";
      // Simon 2026-06-30: hide the delivery/shipping estimate when the selected variant is out of stock.
      var dv = root.querySelector(".pd-deliv"); if (dv) dv.style.display = avail ? "" : "none";
      // Simon 2026-10-25: the desk stand row follows the delivery line; on Ungraded it also needs the Magneto Type.
      var srow = root.querySelector(".pd-dsr"); if (srow) standRowVisible(srow, avail, currentVariant());
      var instRow = $("pd-inst-row"); if (instRow && !avail) instRow.style.display = "none";  // render() owns the avail case (>= $40 gate)
      // W8-QA item3: re-point the sticky-bar observer when the in-stock/out-of-stock state flips, so the bar
      // appears/disappears on scroll IDENTICALLY in both states (previously, OOS hid .pd-buyR -> the observed
      // ATC had no box -> the bar was stuck permanently visible). Only re-observe when the state actually changes.
      if (_ioAvail !== avail) { _ioAvail = avail; observeCTA(avail); }
    }
    // Simon 2026-06-30: on static-gallery products (no 360 viewer), switch the gallery to the selected variant's
    // image, e.g. the colour swatch, by checking the matching slide's radio. The 360 products keep their viewer.
    // Simon 2026-07-26: on 360 products the photo slides are a fixed slice of product media
    // (img_cap = 3 in main-product.liquid), so the selected variant's own photo is usually NOT
    // among the rendered slides and this function used to bail on line 1, leaving the gallery
    // stuck on media order while Style/Authenticator changed. Point the FIRST photo slide (and
    // its thumbnail) at the selected variant's image instead. No-ops when already correct, so
    // the initial render costs nothing when the server-rendered slide already matches.
    function swapVariantPhoto(fname, ialt) {
      if (!fname) return;
      var base_i = document.querySelector(".pd-prismhero") ? 1 : 0;
      var hero = document.querySelector('.mgal__slide[data-i="' + base_i + '"] .mgal__img');
      if (!hero) return;
      var cur = hero.getAttribute("src") || "";
      var cut = cur.lastIndexOf("/");
      if (cut < 0) return;
      var dir = cur.slice(0, cut + 1);
      if ((cur.slice(cut + 1).split("?")[0]) === fname) return;  // already showing it
      var u = dir + fname;
      hero.setAttribute("src", u + "?width=900");
      hero.setAttribute("srcset", u + "?width=450 450w, " + u + "?width=900 900w");
      if (ialt) hero.setAttribute("alt", ialt);
      var th = document.querySelector('.mgal__thumb[for="mgp' + base_i + '"] img');
      if (th) { th.setAttribute("src", u + "?width=200"); if (ialt) th.setAttribute("alt", ialt); }
    }
    // Simon 2026-07-26: PER-VARIANT PHOTO SETS. Shopify allows exactly ONE media per variant, so the
    // gallery rendered the first `img_cap` images in MEDIA ORDER for every variant: selecting Nano still
    // showed two Rounded photos. main-product.liquid emits every image (filename + alt) as data-gg-imgs
    // on the .mgal, gated to phantom-x-gradedguard, and we pick the set whose FILENAME TOKENS match the
    // selected Style / Type / Grader. Filenames were standardised 2026-07-26 as
    // phantom-x-gradedguard-[triple-]<style>-display-<grader>-...  -- a new upload that breaks that
    // pattern simply will not match, so we FALL BACK to the server-rendered slides rather than blank the
    // gallery. Counts: Nano 1/1, Rounded 4(PSA/CGC)/3(BGS), Prism 2/3, each Triple 1. Total 18.
    function ggMatch(opts) {
      var gal = document.querySelector(".mgal[data-gg-imgs]");
      if (!gal || !opts || !opts.length) return null;
      var all; try { all = JSON.parse(gal.getAttribute("data-gg-imgs")) || []; } catch (e) { return null; }
      if (!all.length) return null;
      var style = "", triple = false, bgs = false;
      for (var i = 0; i < opts.length; i++) {
        var v = (opts[i] || "").toLowerCase();
        if (v === "prism" || v === "rounded" || v === "nano") style = v;
        else if (v === "triple") triple = true;
        else if (v.indexOf("bgs") >= 0) bgs = true;
      }
      if (!style) return null;
      var out = [];
      for (var j = 0; j < all.length; j++) {
        var f = (all[j].f || "").toLowerCase();
        if (f.indexOf(style + "-display") < 0) continue;          // style must match exactly
        if ((f.indexOf("triple-") >= 0) !== triple) continue;      // single vs triple
        if ((f.indexOf("-bgs-") >= 0) !== bgs) continue;           // grader group (psa/cgc vs bgs)
        out.push(all[j]);
      }
      return out.length ? out : null;
    }
    function applyVariantGallery(opts) {
      var set = ggMatch(opts);
      if (!set) return false;
      var probe = document.querySelector(".mgal__slide .mgal__img");
      if (!probe) return false;
      var s0 = probe.getAttribute("src") || "";
      var dir = s0.slice(0, s0.lastIndexOf("/") + 1);
      if (!dir) return false;
      var base_i = document.querySelector(".pd-prismhero") ? 1 : 0;
      var slides = document.querySelectorAll(".mgal__slide"), shown = 0;
      for (var i = 0; i < slides.length; i++) {
        var sl = slides[i];
        if (sl.className.indexOf("mgal__slide--video") >= 0) continue;   // keep the video slide
        var im = sl.querySelector(".mgal__img");
        if (!im) continue;                                               // the 360 slide has no .mgal__img
        var di = sl.getAttribute("data-i");
        var th = document.querySelector('.mgal__thumb[for="mgp' + di + '"]');
        var rec = set[shown];
        if (rec) {
          var u = dir + rec.f;
          im.setAttribute("src", u + "?width=900");
          im.setAttribute("srcset", u + "?width=450 450w, " + u + "?width=900 900w");
          if (rec.a) im.setAttribute("alt", rec.a);
          sl.style.display = "";                                         // let the :checked switcher own it
          if (th) {
            th.style.display = "";
            var ti = th.querySelector("img");
            if (ti) { ti.setAttribute("src", u + "?width=200"); if (rec.a) ti.setAttribute("alt", rec.a); }
          }
          shown++;
        } else {
          sl.style.display = "none";                                     // surplus slide for this variant
          if (th) th.style.display = "none";
        }
      }
      // If the checked radio now points at a hidden slide the stage would go blank: fall back to slide 1.
      var ck = document.querySelector(".mgal__r:checked");
      if (ck) {
        var cs = document.querySelector('.mgal__slide[data-i="' + ck.id.replace("mgp", "") + '"]');
        if (cs && cs.style.display === "none") {
          var first = document.getElementById("mgp" + base_i);
          if (first) first.checked = true;
        }
      }
      return true;
    }
    // Simon 2026-08-24: GRADER-FILTERED GALLERY. magneto-color ships one photo per colour x style x
    // grader = 24 images, so the rail was a 24-thumb, 3-row wall and half of it was always the wrong
    // grader for whatever the shopper had selected. On a static-gallery product, hide the slides and
    // thumbnails that belong to a grader other than the selected one.
    //
    // Driven off the variant -> image map the section already emits (data.variants[].img), NOT off
    // filename tokens (ggMatch's approach cannot work here: magneto-color filenames are meaningless,
    // e.g. 404.png, 3_72178ea6-....png) and NOT off hardcoded index ranges (re-ordering media in
    // Shopify would silently break those). Adding a colour or re-ordering media needs no code change.
    //
    // NO-OPS unless the grader genuinely partitions the gallery, so the products whose graders share
    // the same photos (magneto, magneto-thick-cards, magneto-gradedguard, magneto-slabmags,
    // acrylic-display-stand) are untouched. An image attached to NO variant (lifestyle shot) always
    // stays. Hard floor: if the filter would leave the stage empty it reverts every slide, so it can
    // never reproduce the blank-hero bug it shipped alongside.
    function filterGalleryByGrader() {
      var gi = -1;
      (data.options || []).forEach(function (o, i) { if (/authenticator|grader/i.test(o.name || "")) gi = i; });
      if (gi < 0 || !sel[gi] || !variants.length) return;
      var mine = {}, all = {}, nAll = 0, nMine = 0;
      for (var k = 0; k < variants.length; k++) {
        var v = variants[k], f = v.img;
        if (!f) return;                                          // incomplete map: leave the gallery alone
        if (!all[f]) { all[f] = 1; nAll++; }
        if ((v.opts || [])[gi] === sel[gi] && !mine[f]) { mine[f] = 1; nMine++; }
      }
      if (!nMine || nMine >= nAll) return;                       // grader does not partition the gallery
      var slides = document.querySelectorAll(".mgal__slide"), touched = [], shownAny = false;
      for (var i = 0; i < slides.length; i++) {
        var sl = slides[i];
        if (sl.className.indexOf("mgal__slide--video") >= 0) continue;   // the video is grader-agnostic
        var im = sl.querySelector(".mgal__img");
        if (!im) continue;                                       // the 360 slide has no .mgal__img
        var fn = (im.getAttribute("src") || "").split("?")[0].split("/").pop();
        var hide = !!all[fn] && !mine[fn];                       // another grader's photo; unassigned stays
        var th = document.querySelector('.mgal__thumb[for="mgp' + sl.getAttribute("data-i") + '"]');
        touched.push([sl, th]);
        sl.style.display = hide ? "none" : "";                   // "" hands the slide back to the :checked switcher
        if (th) th.style.display = hide ? "none" : "";
        if (!hide) shownAny = true;
      }
      if (!shownAny) {                                           // never blank the stage
        for (var j = 0; j < touched.length; j++) {
          touched[j][0].style.display = "";
          if (touched[j][1]) touched[j][1].style.display = "";
        }
        return;
      }
      // If the checked radio now points at a hidden slide the stage would go blank: fall back to the
      // first slide still visible. syncGalleryToVariant re-points it at the exact variant right after.
      var ck = document.querySelector(".mgal__r:checked");
      var cs = ck && document.querySelector('.mgal__slide[data-i="' + ck.id.replace("mgp", "") + '"]');
      if (cs && cs.style.display === "none") {
        for (var m = 0; m < slides.length; m++) {
          if (slides[m].style.display === "none") continue;
          var r = document.getElementById("mgp" + slides[m].getAttribute("data-i"));
          if (r) { r.checked = true; break; }
        }
      }
    }
    // Simon 2026-08-28: VARIANT-FILTERED GALLERY (products with no grader option). gradedguard sells one
    // photo per Finish x Fits x Colour -- 37 variant images inside 51 media -- and its options are
    // Type / Size / Color, so filterGalleryByGrader never fired (it keys off an "Authenticator"/"Grader"
    // option name) and main-product.liquid's 4-image cap left 33 of the 37 variants with no slide to switch
    // to: picking Purple kept the hero on Royal. With the cap now lifted for any static-gallery product with
    // more than 4 variants, filter the rail down to the images belonging to the selected NON-COLOUR options,
    // so Standard + PSA shows its 10 colours, Jewel + PSA its 3 and Metallic + PSA its 4.
    //
    // Colour is the free dimension -- the swatch row is how a shopper moves through the rail, so it must not
    // filter the rail down to a single thumb. Requires a colour option AND at least one other option, and
    // no grader option: on a grader product the 2026-08-24 filterGalleryByGrader owns the gallery and this
    // no-ops, so every PDP verified that day (magneto-color, nano, ultra-color, acrylic-display-stand, ...)
    // is byte-identical. Driven off the variant -> image map in data-pd-product, never off filenames or
    // index ranges, so adding a colour or re-ordering media in Shopify needs no code change.
    //
    // ONE DELIBERATE DIFFERENCE from the grader filter: an image attached to NO variant is HIDDEN here
    // rather than kept. On a product whose media IS the variant matrix an unattached image is a dead
    // colourway, not a lifestyle shot -- gradedguard carries 14 of them (Frosted Clear, Mint, standard
    // Sapphire, Emerald, six metallic-on-BGS renders), and showing an unbuyable colour in the rail is the
    // same defect as showing the wrong one. Same hard floor as the grader filter: if the result would be an
    // empty stage, every slide is restored.
    function filterGalleryByVariant() {
      var opts = data.options || [];
      if (!variants.length || opts.length < 2) return;
      var ci = -1, gi = -1;
      opts.forEach(function (o, i) {
        if (/colou?r/i.test(o.name || "")) ci = i;
        if (/authenticator|grader/i.test(o.name || "")) gi = i;
      });
      if (ci < 0 || gi >= 0) return;                             // needs a colour axis; grader products keep the 08-24 filter
      var keys = [];
      for (var q = 0; q < opts.length; q++) { if (q !== ci) keys.push(q); }
      if (!keys.length) return;                                  // colour is the only option: nothing to filter on
      var mine = {}, all = {}, nAll = 0, nMine = 0;
      for (var k = 0; k < variants.length; k++) {
        var v = variants[k], f = v.img;
        if (!f) return;                                          // incomplete map: leave the gallery alone
        if (!all[f]) { all[f] = 1; nAll++; }
        var hit = true;
        for (var n = 0; n < keys.length; n++) { if ((v.opts || [])[keys[n]] !== sel[keys[n]]) { hit = false; break; } }
        if (hit && !mine[f]) { mine[f] = 1; nMine++; }
      }
      if (!nMine || nMine >= nAll) return;                       // these options do not partition the gallery
      var slides = document.querySelectorAll(".mgal__slide"), touched = [], shownAny = false;
      for (var i = 0; i < slides.length; i++) {
        var sl = slides[i];
        if (sl.className.indexOf("mgal__slide--video") >= 0) continue;   // the video belongs to every variant
        var im = sl.querySelector(".mgal__img");
        if (!im) continue;                                       // the 360 slide has no .mgal__img
        var fn = (im.getAttribute("src") || "").split("?")[0].split("/").pop();
        var hide = !mine[fn];                                    // another combo's photo, or attached to nothing
        var th = document.querySelector('.mgal__thumb[for="mgp' + sl.getAttribute("data-i") + '"]');
        touched.push([sl, th]);
        sl.style.display = hide ? "none" : "";                   // "" hands the slide back to the :checked switcher
        if (th) th.style.display = hide ? "none" : "";
        if (!hide) shownAny = true;
      }
      if (!shownAny) {                                           // never blank the stage
        for (var j = 0; j < touched.length; j++) {
          touched[j][0].style.display = "";
          if (touched[j][1]) touched[j][1].style.display = "";
        }
        return;
      }
      // If the checked radio now points at a hidden slide the stage would go blank: fall back to the first
      // slide still visible. syncGalleryToVariant re-points it at the exact variant immediately after.
      var ck = document.querySelector(".mgal__r:checked");
      var cs = ck && document.querySelector('.mgal__slide[data-i="' + ck.id.replace("mgp", "") + '"]');
      if (cs && cs.style.display === "none") {
        for (var m = 0; m < slides.length; m++) {
          if (slides[m].style.display === "none") continue;
          var r = document.getElementById("mgp" + slides[m].getAttribute("data-i"));
          if (r) { r.checked = true; break; }
        }
      }
    }
    // Simon 2026-08-31: VARIANT-FILTERED GALLERY FOR 360 PRODUCTS. syncGalleryToVariant() hands every page
    // carrying a .pd-prismhero to applyVariantGallery() (phantom-x-gradedguard only, filename-driven) and
    // otherwise to swapVariantPhoto(), which by design repoints exactly ONE slide -- data-i = base_i. With
    // main-product.liquid capping a 360 product at 3 photo slides, 2 of those 3 stayed welded to media order on
    // every SKU. That is not a cosmetic freeze: on phantom-ultra slide data-i=3 was permanently
    // 13_7ad6bc63... which is the Single/Prism/BGS variant's OWN photo, so a shopper on Rounded/PSA saw a Prism
    // BGS display presented as part of what they were buying, and Single/Prism/BGS showed it twice.
    // phantomxslabmags showed the wrong style or the wrong capacity on 6 of its 8 SKUs.
    //
    // Driven off the variant -> image map in data-pd-product (variants[].img), the same source
    // filterGalleryByGrader() has used since 2026-08-24. NOT filenames: ggMatch() only works because
    // gradedguard's files were deliberately renamed on 2026-07-26, and Ultra's are 13_7ad6bc63... NOT index
    // ranges: re-ordering media in Shopify would silently break those.
    //
    // KEEPS UNASSIGNED IMAGES, like the grader filter and unlike the 08-28 filterGalleryByVariant(). On these
    // products an image attached to no variant is a real lifestyle shot, not a dead colourway. It caps them at
    // LIFE_KEEP so the rail still honours Simon's 2026-06-18 four-view rule: ultra-thick-variant carries 13
    // unassigned images (several of them phantom-ultra's own renders, shared media), and showing them all would
    // take that rail from 4 thumbs to 15.
    //
    // Returns false when it cannot act, and the caller then falls back to the legacy swapVariantPhoto(), so a
    // product with no variant map, one shared photo, or no rendered slide for its variant behaves as before.
    function filterGallery360(pv) {
      var LIFE_KEEP = 2;                                          // + the variant's own photo = the 3-photo cap
      if (!pv || !pv.img || !variants.length) return false;
      var assigned = {}, nAssigned = 0;
      for (var k = 0; k < variants.length; k++) {
        var vf = variants[k].img;
        if (vf && !assigned[vf]) { assigned[vf] = 1; nAssigned++; }
      }
      // Simon 2026-10-03: magneto-slabmags has one photo across all 8 variants but still carries a
      // data-gal-extra map (both Fit Charts), so it is filtered by the map. A one-photo product with no map
      // (ungraded) still returns false and keeps the legacy swapVariantPhoto() path.
      if (nAssigned < 2 && !document.querySelector(".mgal[data-gal-extra]")) return false;   // one photo for the whole product and no map: nothing to filter
      // Simon 2026-08-31 (second pass): an unassigned image is NOT automatically a neutral lifestyle shot.
      // All three of phantom-ultra's are photos of a SINGLE display, so a Triple shopper was still shown two
      // Singles. main-product.liquid emits data-gal-extra as `file=<option value>` pairs, `*` meaning
      // SKU-neutral, one entry per image someone has actually looked at. Anything not listed stays hidden, so
      // an unlisted image can only be missing, never wrong. Alt text is NOT usable here: phantom-ultra media 1
      // is alt-texted "Ultra Thick Prism display case" and is a standard Rounded Single.
      var extra = {}, galEl = document.querySelector(".mgal[data-gal-extra]");
      if (galEl) {
        (galEl.getAttribute("data-gal-extra") || "").split("|").forEach(function (pair) {
          var eq = pair.lastIndexOf("=");
          if (eq > 0) extra[pair.slice(0, eq)] = pair.slice(eq + 1);
        });
      }
      var slides = document.querySelectorAll(".mgal__slide"), touched = [], target = null, life = 0;
      for (var i = 0; i < slides.length; i++) {
        var sl = slides[i];
        if (sl.className.indexOf("mgal__slide--video") >= 0) continue;   // the video belongs to every variant
        var im = sl.querySelector(".mgal__img");
        if (!im) continue;                                               // the 360 slide has no .mgal__img
        var fn = (im.getAttribute("src") || "").split("?")[0].split("/").pop();
        var hide;
        if (fn === pv.img) { hide = false; target = sl; }                // this SKU's own photo, always shown
        else if (assigned[fn]) hide = true;                              // another SKU's photo
        else {
          var req = extra[fn];                                           // unassigned: only if someone cleared it
          // tokens are AND-joined: an extra shows only when EVERY token is a selected option value, so a
          // Prism CGC shot never appears on a Rounded BGS SKU (Simon, third pass).
          var okExtra = req === "*" || (!!req && req.split("+").every(function (t) { return sel.indexOf(t) >= 0; }));
          hide = !okExtra || life >= LIFE_KEEP;
          if (!hide) life++;
        }
        var th = document.querySelector('.mgal__thumb[for="mgp' + sl.getAttribute("data-i") + '"]');
        touched.push([sl, th]);
        sl.style.display = hide ? "none" : "";                           // "" hands the slide back to the :checked switcher
        if (th) th.style.display = hide ? "none" : "";
      }
      if (!target) {                                                     // no slide for this variant: restore, fall back
        for (var j = 0; j < touched.length; j++) {
          touched[j][0].style.display = "";
          if (touched[j][1]) touched[j][1].style.display = "";
        }
        return false;
      }
      // Lead the rail with THIS SKU's own photo. Slides render in media order, so without this the display the
      // shopper actually selected can sit behind the lifestyle shots: on phantom-ultra it lands last on 8 of the
      // 12 combos, where before this change swapVariantPhoto() always left it in the first photo slot.
      // Thumbs only, never slides. mockup-pdp.css matches the lit thumb with
      // `#mgpN:checked ~ .mgal__rail label[for="mgpN"]`, an attribute selector, so rail order is free; the slide
      // switcher keys off data-i and its DOM order must not move. The video thumb stays last either way.
      var rail = document.querySelector(".mgal__rail");
      if (rail) {
        var tth = rail.querySelector('.mgal__thumb[for="mgp' + target.getAttribute("data-i") + '"]');
        var t360 = rail.querySelector(".mgal__thumb--360");
        if (tth && tth.previousElementSibling !== t360) {
          rail.insertBefore(tth, t360 ? t360.nextElementSibling : rail.firstElementChild);
        }
      }
      // If the checked radio now points at a hidden slide the stage would go blank, so move it to this variant's
      // own photo. A shopper parked on the 360 hero, the video or a kept lifestyle shot is left where they are.
      var ck = document.querySelector(".mgal__r:checked");
      var cs = ck && document.querySelector('.mgal__slide[data-i="' + ck.id.replace("mgp", "") + '"]');
      if (cs && cs.style.display === "none") {
        var r = document.getElementById("mgp" + target.getAttribute("data-i"));
        if (r) r.checked = true;
      }
      return true;
    }
    function syncGalleryToVariant() {
      if (document.querySelector(".pd-prismhero")) {
        var pv = currentVariant();
        // Simon 2026-10-25: a variant with no photo of its own (the Magneto Desk Stand's Double; no double stand photo
        // exists yet) borrows the selected colour's first imaged variant, the rule the static gallery below has used since
        // 2026-07-05. Without it filterGallery360() bailed on the empty img and the rail kept the previous colour's photos.
        // Inert on the other 13 spin pages: every one of their variants carries an image (checked 2026-10-25).
        if (pv && !pv.img) {
          var ci360 = -1;
          (data.options || []).forEach(function (o, i) { if (/colou?r/i.test(o.name || "")) ci360 = i; });
          for (var k360 = 0; ci360 >= 0 && k360 < variants.length; k360++) {
            var vk = variants[k360];
            if ((vk.opts || [])[ci360] === sel[ci360] && vk.img) { pv = { opts: pv.opts, img: vk.img, ialt: vk.ialt }; break; }
          }
        }
        // Per-variant set first (gradedguard), then the variant filter, then swapVariantPhoto as the
        // single-image fallback for products with no usable map.
        if (pv && !applyVariantGallery(pv.opts) && !filterGallery360(pv)) swapVariantPhoto(pv.img, pv.ialt);
        return;
      }
      filterGalleryByGrader();
      filterGalleryByVariant();   // no-ops on grader products; owns the rail on gradedguard
      // Simon 2026-07-05: reflect the FULL selected variant (Colour + Style + Grader), not just the colour.
      // Prefer the exact current variant's own image; only if it has none, fall back to the selected colour's
      // first imaged variant (covers a snap() landing on a sibling variant that carries no image of its own).
      var targetImg = null;
      var cv = currentVariant();
      if (cv && cv.img) targetImg = cv.img;
      if (!targetImg) {
        var colorIdx = -1;
        (data.options || []).forEach(function (o, i) { if (/colou?r/i.test(o.name || "")) colorIdx = i; });
        if (colorIdx >= 0 && sel[colorIdx]) {
          for (var k = 0; k < variants.length; k++) {
            if ((variants[k].opts || [])[colorIdx] === sel[colorIdx] && variants[k].img) { targetImg = variants[k].img; break; }
          }
        }
      }
      if (!targetImg) return;
      var imgs = document.querySelectorAll(".mgal__slide .mgal__img");
      for (var i = 0; i < imgs.length; i++) {
        if ((imgs[i].getAttribute("src") || "").indexOf("/" + targetImg) >= 0) {
          var slide = imgs[i].closest(".mgal__slide");
          var di = slide && slide.getAttribute("data-i");
          // Simon 2026-08-28: this slide IS the selected variant's photo, so it must never be one of the
          // hidden ones. main-product.liquid pre-hides the other combos' slides inline for the first paint,
          // and an inline display:none would beat the :checked switcher's display:block and blank the stage.
          // Belt and braces: reveal it (and its thumb) before checking the radio.
          if (slide && slide.style.display === "none") {
            slide.style.display = "";
            var th2 = document.querySelector('.mgal__thumb[for="mgp' + di + '"]');
            if (th2) th2.style.display = "";
          }
          var r = (di != null) && document.getElementById("mgp" + di);
          if (r && !r.checked) r.checked = true;
          return;
        }
      }
    }
    // Simon 2026-07-03: syncFitcueSize removed - the size guide is now static and never swaps with the selected Card Size.
    function render() {
      syncGalleryToVariant();
      var u = unit(), total = lineTotal(u, qty), avail = isAvail();
      if ($("pd-price")) $("pd-price").textContent = moneyShort(u);
      // Simon 2026-10-25: a ticked desk stand rides along with this Add to cart, so the button, the sticky bar and the
      // Afterpay instalment show the combined total (the quantity cards above stay display-only prices)
      var srx = standRowExtra(root.querySelector(".pd-dsr")), shown = total + (srx ? srx.cents / 100 : 0);
      if ($("pd-atc-p")) $("pd-atc-p").textContent = moneyShort(shown);
      // Simon 2026-07-26: was Math.ceil(total / 4), which rounded each instalment UP to a whole dollar
      // and so overstated the order: a $45 Display advertised "4 payments of $12", i.e. $48. It also
      // disagreed with the server, which renders inst_cents = unit_cents | divided_by: 4 ($11.25) into
      // the same node, so the figure visibly changed on boot. Afterpay's merchant messaging rules
      // require the advertised instalment to be accurate. fmtMoney already rounds to 2dp and trims a
      // whole-dollar value, so this prints $11.25 and matches the server exactly.
      if ($("pd-inst")) $("pd-inst").textContent = moneyShort(shown / 4);
      var instRow = $("pd-inst-row"); if (instRow) instRow.style.display = (avail && u >= 40 && PD_CUR === "USD") ? "" : "none";  // no "4 payments of $3" on sub-$40; USD sessions only (the $40 floor is USD-defined and Afterpay is a US offer)
      if ($("pd-sbar-p")) $("pd-sbar-p").textContent = moneyShort(shown);
      // each preset card shows its own live line price (n x current unit price). Perfect 10: a card whose
      // quantity reaches the discount on THIS display alone shows the struck full price, the discounted
      // price and its "Save 10%" chip ([data-qsave]); every other card is unchanged.
      root.querySelectorAll(".pd-qcard[data-qty]").forEach(function (c) {
        var q = +c.dataset.qty, sub = c.querySelector("[data-qprice]"), t = lineTotal(u, q), deal = t < u * q;
        if (sub) {
          if (deal) sub.innerHTML = '<s class="pd-qcard__was"><span class="pd-vh">Was </span>' + moneyShort(u * q) + '</s> ' + moneyShort(t);
          else sub.textContent = moneyShort(u * q);
        }
        // Simon 2026-10-13: the chip reads "Save $45 (10%)" like the grid arm's sheet (amount from the same numbers)
        var sv = c.querySelector("[data-qsave]");
        if (sv) { if (deal) sv.textContent = "Save " + moneyShort(u * q - t) + " (" + Math.round((u * q - t) / (u * q) * 100) + "%)"; sv.hidden = !deal; }
      });
      // visual free-shipping progress slider (fills toward the $200 threshold)
      // Simon 2026-07-14: USD sessions only - the band is USD-defined and the copy is a US-only promise;
      // non-USD presentment carts got wrong math and "$"-labelled foreign amounts (see usdCart above).
      var qbar = $("pd-qship"), fill = $("pd-qbar-fill"), cap = $("pd-qbar-cap");
      if (qbar) {
        if (FREE_SHIP != null && avail && usdCart()) {
          qbar.hidden = false;
          // Simon 2026-06-23: the meter tracks the REAL cart subtotal, not this page's not-yet-added
          // selection. An empty cart now reads "Add $200 more" (was "$145 more" because it pre-counted the
          // $55 item on screen, which read as if only $145 total was needed). It refreshes on pd:cartsync
          // (page load + every add/change/remove), so once the item is actually added the bar advances.
          var shipT = subtotal();
          // free shipping only inside the $200-$600 band; above $600 standard rates resume and the meter
          // must not promise free shipping (Simon-approved 2026-06-10).
          var inBand = shipT >= FREE_SHIP && (FREE_SHIP_MAX == null || shipT <= FREE_SHIP_MAX);
          var pct = Math.max(0, Math.min(100, shipT / FREE_SHIP * 100));
          qbar.className = "pd-qbar" + (inBand ? " pd-qbar--ok" : "");
          if (fill) fill.style.width = pct + "%";
          if (cap) cap.innerHTML = shipT < FREE_SHIP
            ? 'Add <b>' + moneyShort(FREE_SHIP - shipT) + '</b> more for free U.S. shipping'
            : inBand
              ? '<svg aria-hidden="true"><use href="#i-ck"/></svg> You have unlocked <b>free U.S. shipping</b>'
              : 'Orders over <b>' + moneyShort(FREE_SHIP_MAX) + '</b> ship at standard U.S. rates';
        } else { qbar.hidden = true; }
      }
      // Simon 2026-07-05: grader helper note removed (markup gone from main-product.liquid); no per-grader hint to sync.
      // free-ship messaging now lives at the quantity (#pd-qship), so the duplicate ATC shipnote is gone
      setBuyState();
    }
    root.querySelectorAll(".pd-pill,.pd-swatch,.pd-style").forEach(function (pill) {
      pill.addEventListener("click", function () {
        // W8-QA item3: every value is selectable, including "none" combos. snap() resolves the click to the
        // nearest real variant carrying this value (preferring in-stock, else an OOS variant -> notify flow).
        // ITEM 3 (2026-09-09): except one - a Type/Style/Colour value with no SKU under the SELECTED grader is
        // refused (snap returns false) and the previous selection is restored, so the picker can never re-grade
        // the shopper behind their back. The tail below still runs on a refused click: render() is idempotent,
        // and the ?variant= replaceState (STOQ's only variant signal) must never be conditional.
        var oi = +pill.dataset.opt, prev = sel.slice(), no = false;
        sel[oi] = pill.dataset.val;
        if (!snap(oi, pill.dataset.val)) { sel = prev; no = true; }
        // Must stay AFTER snap() (snap rescues a non-existent combo to a live variant, which is not a
        // dead end) and BEFORE render(), so no render-path side effect can be blamed for the fire.
        fireDeadEnd("select");
        syncActive(); render(); notifyVariant(); announceSel(no ? pill.dataset.val : null);
        // P7: write the selection to the URL so a copied link deep-links to this exact variant.
        // W8 item4: also write ?variant=<id> so the STOQ app (URL variant detection) tracks the selected variant.
        try { var u = new URL(location.href); u.searchParams.set("sel", sel.join("~")); var cv = currentVariant(); if (cv && cv.id) u.searchParams.set("variant", cv.id); history.replaceState(null, "", u); } catch (e) {}
      });
    });
    function syncQ() {
      // a preset card is selected when its number matches the current quantity; an arbitrary amount
      // typed or stepped in the manual control simply highlights none of the three cards.
      var anyQOn = false, qcards = root.querySelectorAll(".pd-qcard");
      qcards.forEach(function (c) {
        var on = (+c.dataset.qty === qty);
        c.classList.toggle("on", on); c.setAttribute("aria-checked", on ? "true" : "false");
        c.tabIndex = on ? 0 : -1;   // A4: roving tabindex
        if (on) anyQOn = true;
      });
      if (!anyQOn && qcards[0]) qcards[0].tabIndex = 0;   // A4: arbitrary qty selects no card; keep the group Tab-reachable
      var qv = $("pd-qv"); if (qv && document.activeElement !== qv) qv.value = qty;  // don't fight the cursor while typing
    }
    root.querySelectorAll(".pd-qcard").forEach(function (c) {
      c.addEventListener("click", function () {
        qty = +c.dataset.qty || 1; syncQ();
        var qv = $("pd-qv"); if (qv) qv.value = qty;
        render();
      });
    });
    // +/- always writes the value explicitly (a button press is an explicit action, so it must update the
    // shown number even if the field happens to hold focus, unlike the typing guard inside syncQ).
    if ($("pd-qd")) $("pd-qd").addEventListener("click", function () { if (qty > 1) { qty--; syncQ(); var qv = $("pd-qv"); if (qv) qv.value = qty; render(); } });
    if ($("pd-qu")) $("pd-qu").addEventListener("click", function () { if (qty < 25) { qty++; syncQ(); var qv = $("pd-qv"); if (qv) qv.value = qty; render(); } });
    if ($("pd-qv")) {
      // editable quantity: type any number (a buyer who wants 8 should not tap "+" seven times)
      $("pd-qv").addEventListener("input", function () {
        var n = parseInt((this.value || "").replace(/\D/g, ""), 10);
        if (!n) return;                 // allow a transient empty field; clamp on blur
        if (n > 25) n = 25;
        qty = n; syncQ(); render();
      });
      $("pd-qv").addEventListener("blur", function () {
        var n = parseInt((this.value || "").replace(/\D/g, ""), 10);
        qty = (!n || n < 1) ? 1 : (n > 25 ? 25 : n);
        this.value = qty; syncQ(); render();
      });
    }

    function doAdd(btn) {
      if (!isAvail() || btn.disabled || btn.classList.contains("ld")) return;
      btn.classList.add("ld");
      var c = btn.querySelector(".c"); var html = c ? c.innerHTML : "";
      // R2: resolve the currently-selected variant -> its Shopify numeric id (emitted on the
      // data-pd-product variants by the template) and add to the live R1 cart. The second arg
      // `true` opens the cart drawer. W6 item2: the drawer now opens OPTIMISTICALLY the instant this
      // runs (panel glides + loading skeleton), and the real content cascades in when the refetch lands;
      // the button "Added" below still waits for the real POST result (D2), so the open and the confirm
      // are decoupled (no dead pause before the glide; no premature "Added").
      var cv = currentVariant(), variantId = cv && cv.id;
      if (!variantId) { btn.classList.remove("ld"); return; }
      // D2 (2026-06-10): the confirm state, event, and toast resolve from the REAL POST result,
      // not the old 480ms timer (which showed "Added" before the server answered). The 480ms
      // floor is kept only as minimum spinner time so the confirm does not flash on fast
      // networks. JS-1's reject-on-HTTP-error routes failures to the honest toast below.
      var floor = new Promise(function (res) { setTimeout(res, 480); });
      // Simon 2026-10-25: a ticked desk stand goes in the same request (both or neither)
      var srow = root.querySelector(".pd-dsr"), srx = standRowExtra(srow);
      Promise.all([window.PhantomCart.add({ id: variantId, quantity: qty, extra: srx ? [{ id: srx.vid, quantity: 1 }] : null }, true), floor])
        .then(function (res) {
          var standFailed = !!(res && res[0] && res[0].extraFailed);
          if (srow) { srow._displayAdded = true; if (srx) { if (standFailed) standRowFailed(srow); else standRowAfterAdd(srow, srx, "atc"); } }
          btn.classList.remove("ld"); btn.classList.add("ok");
          if (c) c.innerHTML = '<svg aria-hidden="true"><use href="#i-ck"/></svg><span>Added</span>';
          ev("add_to_cart", { handle: data.handle, variant: sel.join("/"), price: unit(), qty: qty, currency: (_cart && _cart.currency) || PD_CUR });
          // W5 item1: success toast SUPPRESSED here. This add opens the cart drawer (PhantomCart.add(...,true)),
          // and the drawer IS the confirmation; firing a toast at the same instant was a redundant, competing
          // signal (part of the "everything moves at once" roughness Simon flagged). The button's "Added" state
          // plus the drawer opening confirm the add. The toast still fires for quick-add (no drawer) and for the
          // failure path below (the drawer does not open on a failed add).
          // re-render after restoring: the saved label can carry a desk stand total the reset tick box no longer has
          setTimeout(function () { btn.classList.remove("ok"); if (c) c.innerHTML = html; render(); }, 1800);
        })
        .catch(function () { btn.classList.remove("ok", "ld"); if (c) c.innerHTML = html; toast("Sorry, could not add to cart", "Couldn't add"); });
    }
    if ($("pd-atc")) $("pd-atc").addEventListener("click", function () { doAdd(this); });
    // the desk stand row (tick, type, colour) changes the Add to cart total
    document.addEventListener("pd:stand", function () { render(); });
    if ($("pd-atc2")) $("pd-atc2").addEventListener("click", function () {
      // W8-QA6 item3: when OOS the sticky button is an "Out of stock" control that scrolls up to the notify
      // form and focuses its first field, instead of trying (and failing) to add to cart.
      if (!isAvail()) {
        var oos = $("pd-oos-area");
        if (oos) { oos.scrollIntoView({ behavior: "smooth", block: "center" });
          setTimeout(function () { var f = oos.querySelector("input, button, select"); if (f && f.focus) { try { f.focus({ preventScroll: true }); } catch (e) { f.focus(); } } }, 450); }
        return;
      }
      doAdd(this);
    });

    // Simon 2026-07-02 (code-perf audit #10): the v2 fallback notify form was removed (faked success, sent
    // nothing). STOQ (Restock Rocket) remains the real back-in-stock capture, injected into #pd-stoq-mount;
    // when it is absent the OOS area now shows an honest "out of stock" line (no fake form). syncStoq() below
    // still hides that line once STOQ injects.

    // Sticky buy bar: shown whenever the active primary CTA is off-screen (above OR below the fold). On mobile
    // the CTA starts below the fold, so the bar is present from load, carrying price + button into the first
    // viewport for the ~86% who never scroll. On desktop the CTA is in view at rest, so the bar stays hidden.
    // The observer is owned by observeCTA() (defined above) and re-pointed by setBuyState() on stock-state
    // changes, so OOS variants get the same show/hide-on-scroll behaviour as in-stock ones. The initial
    // syncActive/syncQ/render() below runs setBuyState(), which sets up the observer for the load state.
    // A4: the variant + quantity groups declare role="radiogroup"/role="radio" and set aria-checked,
    // but only had a click listener (every radio was a Tab stop, arrow keys dead). Implement the
    // WAI-ARIA roving pattern the markup promises: Arrow/Home/End move focus among the ENABLED radios
    // (skipping pd-na impossible combos) and selection follows focus by reusing the existing click
    // handlers (snap, price, qty logic all unchanged). Pure behaviour addition; no layout/visual change.
    function wireRoving(group) {
      group.addEventListener("keydown", function (e) {
        var k = e.key;
        if (k !== "ArrowRight" && k !== "ArrowDown" && k !== "ArrowLeft" && k !== "ArrowUp" && k !== "Home" && k !== "End") return;
        var radios = [].slice.call(group.querySelectorAll('[role="radio"]')).filter(function (r) {
          // W8-QA item3: pd-na (no-SKU) values are now selectable, so keyboard roving includes them too.
          // ITEM 3 (2026-09-09): this filter was DEAD - syncActive() cleared aria-disabled on every radio on
          // every render, so ArrowRight happily focused and CLICKED a display:none pd-na pill. Skip anything a
          // pointer cannot reach: aria-disabled (grader-blocked), hidden, or display:none by inline style or
          // stylesheet. Keyboard now lands only where a mouse could.
          if (r.disabled || r.hidden || r.getAttribute("aria-disabled") === "true" || r.style.display === "none") return false;
          var cs = window.getComputedStyle && window.getComputedStyle(r);
          return !cs || (cs.display !== "none" && cs.visibility !== "hidden");
        });
        if (!radios.length) return;
        e.preventDefault();
        // start from the focused radio, else from the checked one (focus may sit on a radio that just became
        // unreachable), else the first selectable radio.
        var cur = radios.indexOf(document.activeElement);
        if (cur < 0) cur = radios.indexOf(group.querySelector('[aria-checked="true"]'));
        if (cur < 0) cur = 0;
        var next = (k === "Home") ? 0 : (k === "End") ? radios.length - 1
          : (k === "ArrowRight" || k === "ArrowDown") ? (cur + 1) % radios.length
          : (cur - 1 + radios.length) % radios.length;
        var t = radios[next]; if (t) { t.focus(); t.click(); }
      });
    }
    root.querySelectorAll('[role="radiogroup"]').forEach(wireRoving);
    ev("view_item", { handle: data.handle, price: data.base });
    // W8 item4: seed ?variant=<id> at load so the STOQ app detects the initial variant for OOS capture.
    try { var _u = new URL(location.href); var _cv = currentVariant(); if (_cv && _cv.id) { _u.searchParams.set("variant", _cv.id); history.replaceState(null, "", _u); } } catch (e) {}
    // pd_pdp_view is the denominator and the canary: the only page-view count produced by this exact
    // code path, so the rate cannot silently read "no dead ends" when the emitter is what broke.
    try { pdEventQ("pd_pdp_view", { handle: hnd() }); fireDeadEnd(); } catch (e) {}
    liveRegion(); syncActive(); syncQ(); render();   // ITEM 3: park the empty polite region BEFORE any write
    // Simon 2026-06-23: re-render when the live cart changes (page-load fetch + every add/change/remove) so
    // the free-ship meter mirrors the real cart subtotal rather than the on-page selection.
    document.addEventListener("pd:cartsync", render);
    // fire once at load so the 360 viewer syncs to the initial variant (covers ?sel deep-links);
    // async so the viewer IIFE below has attached its pd:variant listener first.
    setTimeout(notifyVariant, 0);
  }

  /* ---------------- stand fit popup (Clear Display Stands, spec 2026-07-18) ----------------
     The matched in-stock result card offers a direct Add to Cart of the exact matched variant via the
     shared PhantomCart API (drawer opens over the page). This is arm-identical on both c0c38bf6 test
     arms and touches no wizard/stacked picker, so it stays arm-safe (Simon-approved 2026-07-18).
     Markup + variant map server-rendered in snippets/pd-fitpop.liquid. */
  (function () {
    var root = document.getElementById("pd-fitpop"); if (!root) return;
    var trig = document.getElementById("pd-fitpop-trig");
    var mount = document.getElementById("pd-fitpop-mount");
    var dots = document.getElementById("pd-fitpop-dots");
    var back = document.getElementById("pd-fitpop-back");
    var panel = root.querySelector(".pd-fitpop__panel");
    if (!trig || !mount || !dots || !back || !panel) return;
    var MAP; try { MAP = JSON.parse(root.dataset.pdStandfit || "{}"); } catch (e) { return; }
    // Product handle for analytics, server-rendered on the root (data-handle); falls back to the known
    // Clear Display Stands handle so a stale template never breaks the add_to_cart event.
    var HANDLE = root.dataset.handle || "acrylic-display-stand";
    var ev = function (n, p) { if (window.pdEvent) window.pdEvent(n, p); };
    /* Task-3 review carry-forward: main has position:relative + z-index:3, creating a stacking
       context, so a fixed overlay nested in main paints under the root-level header and cart
       drawer. Re-home the dialog to <body> so the 1200 overlay tier actually holds. */
    if (root.parentElement !== document.body) document.body.appendChild(root);
    var FIT_URL = root.dataset.fitUrl || "/pages/find-your-display";
    var STEPS = [
      { id: "display", q: "Which display is your card in?", opts: [
        { v: "Nano", b: "Nano", s: "Slim standalone display (not the GradedGuard Nano)" },
        { v: "Ultra", b: "Ultra", s: "Our 10mm crystal acrylic display" },
        { v: "GradedGuard", b: "Phantom x GradedGuard", s: "For cards kept in a GradedGuard case" },
        { v: "Slabmags", b: "Phantom x Slabmags", s: "For cards kept in Slabmags" } ] },
      { id: "ggstyle", q: "Which style is your Phantom x GradedGuard?", opts: [
        { v: "Standard", b: "Prism", s: "Diamond-cut faceted edges, full 10mm" },
        { v: "Standard", b: "Rounded", s: "Smooth rounded edges, full 10mm" },
        { v: "Nano", b: "Nano", s: "Half the thickness" } ] },
      { id: "size", q: "Is your display Standard or Thick?", opts: [
        { v: "Standard", b: "Standard", s: "For standard slabs. Most graded cards are this size." },
        { v: "Thick", b: "Thick", s: "For thick slabs, common with autograph and patch cards." } ] },
      { id: "auth", q: "Who graded your card?", opts: [
        { v: "PSA/CGC/TAG", b: "PSA" }, { v: "PSA/CGC/TAG", b: "CGC" },
        { v: "PSA/CGC/TAG", b: "TAG" }, { v: "BGS", b: "BGS" } ] }
    ];
    var ans = {}, history = [], isOpen = false, _untrap = null, _ret = null, _lastPick = 0;
    function skippable(i) { return STEPS[i].id === "size" && (ans.display === "GradedGuard" || ans.display === "Slabmags"); }
    function notAsked(i) { return STEPS[i].id === "ggstyle" && ans.display !== "GradedGuard"; }
    function renderDots() {
      var total = (ans.display === "Slabmags" || (ans.display === "GradedGuard" && ans.ggstyle === "Nano")) ? 2 : 3;
      var h = "";
      for (var d = 0; d < total; d++) h += "<i" + (d <= history.length ? ' class="on"' : "") + "></i>";
      dots.innerHTML = h;
    }
    function step(i) {
      // No stand we make fits the GradedGuard Nano, so that answer ends the quiz with no product.
      if (ans.display === "GradedGuard" && ans.ggstyle === "Nano") return ggNanoResult();
      while (i < STEPS.length && (notAsked(i) || skippable(i))) {
        if (skippable(i)) {
          ans[STEPS[i].id] = "Standard";
          ev("standfit_step", { step: STEPS[i].id, value: "Standard", auto: true });
        }
        i++;
      }
      if (i >= STEPS.length) return result();
      back.hidden = history.length === 0;
      renderDots();
      var s = STEPS[i];
      var stepOpts = s.opts;
      // GradedGuard cases are made for PSA, CGC and BGS slabs only, no TAG (Simon 2026-07-18)
      if (s.id === "auth" && ans.display === "GradedGuard") stepOpts = stepOpts.filter(function (o) { return o.b !== "TAG"; });
      var opts = stepOpts.map(function (o) {
        return '<button type="button" class="pd-quiz__opt" data-v="' + o.v + '"><b>' + o.b + "</b>" + (o.s ? "<span>" + o.s + "</span>" : "") + "</button>";
      }).join("");
      var help = s.id === "display" ? '<div class="pd-quiz__help">Don’t have a Phantom display yet? <a href="' + FIT_URL + '">Find your fit</a></div>' : "";
      mount.innerHTML = '<div class="pd-quiz__step"><h2 class="pd-quiz__q" tabindex="-1">' + s.q + '</h2><div class="pd-quiz__opts">' + opts + "</div>" + help + "</div>";
      var h = mount.querySelector(".pd-quiz__q"); if (h && isOpen) h.focus();
      mount.querySelectorAll(".pd-quiz__opt").forEach(function (b) {
        b.addEventListener("click", function () {
          // Debounce: a double-tap's second hit lands on the next step's options and silently answers it.
          var now = Date.now(); if (now - _lastPick < 350) return; _lastPick = now;
          ans[s.id] = b.dataset.v; history.push(i);
          ev("standfit_step", { step: s.id, value: b.dataset.v, auto: false });
          step(i + 1);
        });
      });
    }
    function ggNanoResult() {
      back.hidden = false;
      renderDots();
      var key = "GradedGuard|Nano|-";
      ev("standfit_complete", { key: key, found: false, available: false });
      mount.innerHTML = '<div class="pd-quiz__step"><div class="pd-qresult"><p class="pd-qresult__tag">No stand yet</p><h2 class="pd-quiz__q" tabindex="-1">We don’t currently make a stand for the GradedGuard Nano</h2><p class="pd-qresult__why">The GradedGuard stand is sized for the standard 10mm displays, and the Nano stands are sized for the standalone Nano display. Neither fits the GradedGuard Nano.</p><div class="pd-qresult__cta"><button type="button" class="pd-cta pd-cta--view" data-fitpop-close>Close</button></div></div></div>';
      ev("standfit_result_view", { key: key });
      var h = mount.querySelector(".pd-quiz__q"); if (h && isOpen) h.focus();
    }
    function result() {
      back.hidden = false;
      renderDots();
      var key = ans.display + "|" + ans.size + "|" + ans.auth;
      var r = MAP[key];
      ev("standfit_complete", { key: key, found: !!r, available: !!(r && r.av) });
      var disp = ans.display === "GradedGuard" ? "Phantom x GradedGuard" : ans.display === "Slabmags" ? "Phantom x Slabmags" : ans.display;
      if (!r) {
        mount.innerHTML = '<div class="pd-quiz__step"><div class="pd-qresult"><p class="pd-qresult__tag">No match</p><h2 class="pd-quiz__q" tabindex="-1">We could not match a stand</h2><p class="pd-qresult__why">Contact us and we will point you to the right stand for your setup.</p><div class="pd-qresult__cta"><button type="button" class="pd-cta pd-cta--view" data-fitpop-close>Close</button></div></div></div>';
      } else {
        var line = disp + " · " + ans.size + " · " + ans.auth;
        if (r.av) {
          // Matched + in stock (Simon-approved 2026-07-18): primary "Add to cart · [price]" adds the
          // exact matched variant (qty 1) and opens the cart drawer; the price now lives IN the button
          // so the .pd-qresult__price line is intentionally dropped for this state. Close becomes the
          // quiet secondary action and KEEPS data-fitpop-close + data-fitpop-scroll so it still scrolls
          // to the pickers. tag + match line are unchanged.
          mount.innerHTML = '<div class="pd-quiz__step"><div class="pd-qresult"><p class="pd-qresult__tag">Your stand</p><h2 class="pd-quiz__q" tabindex="-1">' + line + '</h2><div class="pd-qresult__cta"><button type="button" class="pd-cta pd-cta--view" data-fitpop-atc>Add to cart · ' + r.price + '</button><button type="button" class="pd-quiz__restart" data-fitpop-close data-fitpop-scroll>Close</button></div></div></div>';
          var atc = mount.querySelector("[data-fitpop-atc]");
          if (atc) atc.addEventListener("click", function () {
            if (atc.disabled) return;
            atc.disabled = true;                 // in-flight guard: block a double add
            ev("standfit_atc", { key: key });    // NEW standfit funnel event (Simon-approved 2026-07-18)
            // Shared cart-add path the PDP ATC + quick-add use: POST -> refetch -> real drawer open, with
            // 422/404 rejection. PhantomCart.add does NOT fire add_to_cart itself, so this caller owns the
            // analytics, mirroring the quick_add event shape with source "standfit".
            window.PhantomCart.add({ id: r.vid, qty: 1 }, true)
              .then(function () {
                if (window.pdEvent) window.pdEvent("add_to_cart", { handle: HANDLE, variantId: Number(r.vid), price: parseFloat(String(r.price).replace(/[^0-9.]/g, "")) || 0, qty: 1, source: "standfit" });
                _ret = null;         // the drawer now owns focus; do not pull it back to the page behind
                closePop(false);     // no scroll: the cart drawer is already open over the page
              })
              .catch(function () { atc.disabled = false; });   // PhantomCart.add reverted its optimistic open; allow a retry
          });
        } else {
          // OOS matched card: unchanged (no ATC). Keeps the price line, the notify note, and the Done
          // button whose data-fitpop-close + data-fitpop-scroll behavior is untouched.
          mount.innerHTML = '<div class="pd-quiz__step"><div class="pd-qresult"><p class="pd-qresult__tag">Out of stock</p><h2 class="pd-quiz__q" tabindex="-1">' + line + '</h2><p class="pd-qresult__price">' + r.price + '</p><p class="pd-qresult__why">Currently out of stock. Use the notify form on this page and we will email you when it is back.</p><div class="pd-qresult__cta"><button type="button" class="pd-cta pd-cta--view" data-fitpop-close data-fitpop-scroll>Done</button></div></div></div>';
        }
      }
      ev("standfit_result_view", { key: key });
      var h = mount.querySelector(".pd-quiz__q"); if (h && isOpen) h.focus();
    }
    var _fitHideT = null;
    function openPop() {
      if (isOpen) return; isOpen = true;
      clearTimeout(_fitHideT);
      root.classList.remove("is-in");
      root.inert = false;
      root.hidden = false; root.setAttribute("aria-hidden", "false");
      void root.offsetHeight;   /* AUDIT-24 W2 F42: commit display:none -> block before the class flips (a single rAF can batch past it and hard-cut the entrance) */
      root.classList.add("is-in");
      lockScroll(true); _ret = document.activeElement; _untrap = trapFocus(panel);
      ans = {}; history = [];
      ev("standfit_open", {});
      step(0);
    }
    function closePop(toPickers) {
      if (!isOpen) return; isOpen = false;
      // AUDIT-24 W2 F42: play the exit (class off now, hidden deferred until the motion lands); the CSS
      // pointer-events guard keeps the fading overlay from swallowing the next tap. aria/focus/scroll
      // teardown stays synchronous below.
      root.classList.remove("is-in"); root.inert = true; root.setAttribute("aria-hidden", "true");   /* AUDIT-24 W2: inert through the deferred-hidden exit window */
      clearTimeout(_fitHideT);
      var _fd = (window.PD_MEDIA && window.PD_MEDIA.REDUCE) ? 150 : (matchMedia("(max-width: 760px)").matches ? 300 : 200);
      _fitHideT = setTimeout(function () { if (!isOpen) root.hidden = true; }, _fd);
      // lockScroll(false) synchronously restores the pre-open scroll position, so the
      // smooth scroll below must run AFTER it to animate from the restored position.
      lockScroll(false); if (_untrap) { _untrap(); _untrap = null; }
      if (toPickers) {
        // Focus the trigger without a scroll jump, then smooth-scroll the buy box (wizard
        // arm .pd-wiz first so it wins over its nested .pd-bbox; stacked arm = .pd-bbox).
        if (trig) { try { trig.focus({ preventScroll: true }); } catch (e) { trig.focus(); } }
        var box = document.querySelector(".pd-wiz, .pd-bbox");
        if (box) box.scrollIntoView({ behavior: "smooth", block: "start" });
      } else if (_ret && _ret.focus) { _ret.focus(); }
      _ret = null;
    }
    trig.addEventListener("click", openPop);
    root.addEventListener("click", function (e) {
      var c = e.target && e.target.closest ? e.target.closest("[data-fitpop-close]") : null;
      if (c) closePop(c.hasAttribute("data-fitpop-scroll"));
    });
    back.addEventListener("click", function () {
      if (!history.length) return;
      var prev = history.pop();
      for (var j = prev; j < STEPS.length; j++) delete ans[STEPS[j].id];
      step(prev);
    });
    document.addEventListener("keydown", function (e) { if (e.key === "Escape" && isOpen) closePop(); });
  })();

  /* ---------------- desktop Fit Guide chart in the Sizing Guide (Simon 2026-10-27) ----------------
     sections/main-product.liquid renders [data-fitg] as the first block of the "Sizing Guide" dropdown (filled and
     shown while the page parses; it sat under the grader picker as its own dropdown for a few hours, Simon: "merge fit
     guide back into sizing guide drop down"). Here it keeps in step with the buy box: same #pdla-config map, grader
     PSA or BGS from the grader picker, plus "thick" when a Standard/Thick option sits on Thick; SGC has no chart, so it
     hides. Phones too since 2026-10-27 (Simon: "merge it into the sizing guide under the CTA"): the phone layout
     (snippets/pd-layout-a-js.liquid) drives the same buy-box controls, so pd:variant keeps this chart in step there,
     and its sheets no longer carry a Fit Guide button. The open event says which layout it came from. */
  (function () {
    var cfgEl = document.getElementById("pdla-config");
    var fits = [].slice.call(document.querySelectorAll("[data-fitg]"));
    if (!cfgEl || !fits.length) return;
    var fit, fam = ""; try { var cfg = JSON.parse(cfgEl.textContent); fit = cfg.fit || {}; fam = cfg.fam || ""; } catch (e) { return; }
    var ev = function (n, p) { if (window.pdEvent) window.pdEvent(n, p); };
    function picked(sel) { var b = sel && sel.querySelector('[data-opt][aria-checked="true"]'); return b ? b.getAttribute("data-val") || "" : ""; }
    function sizeKey() {
      var sels = [].slice.call(document.querySelectorAll(".pd-bbox .pd-sel"));
      for (var i = 0; i < sels.length; i++) {
        if (sels[i].querySelector('[data-opt][data-val^="Thick"]')) return /^Thick/.test(picked(sels[i])) ? "thick" : "";
      }
      return "";
    }
    function graderSel() {
      var sels = [].slice.call(document.querySelectorAll(".pd-bbox .pd-sel"));
      for (var i = 0; i < sels.length; i++) {
        if (sels[i].querySelector('[data-opt][data-val^="PSA"],[data-opt][data-val^="BGS"],[data-opt][data-val^="SGC"]')) return sels[i];
      }
      return null;
    }
    function chartFor(d) {
      var gv = picked(graderSel()), gk = /^PSA/.test(gv) ? "PSA" : (/^BGS/.test(gv) ? "BGS" : "");
      if (!gk) return null;
      var sk = sizeKey();
      return { src: fit[gk + "|" + sk] || (sk ? null : fit[gk + "|"]) || null, grader: gv, gk: gk, sk: sk };
    }
    function sync() {
      fits.forEach(function (d) {
        var c = chartFor(d), img = d.querySelector(".pd-gdd__chart");
        d.hidden = !(c && c.src);
        if (!c || !c.src || !img) return;
        /* drop the old chart first: a new src keeps the previous image on screen until it loads */
        if (img.getAttribute("src") !== c.src) { img.removeAttribute("src"); img.src = c.src; }
        img.alt = "Fit guide for " + c.grader + " graded cards";
      });
    }
    fits.forEach(function (d) {
      var host = d.closest("details"); if (!host) return;
      host.addEventListener("toggle", function () {
        if (!host.open || d.hidden) return;
        var c = chartFor(d);
        ev("fit_guide_open", { via: document.documentElement.classList.contains("pdla-on") ? "phone" : "desktop", grader: c ? c.gk : "", size: (c && c.sk) || (/thick$/.test(fam) ? "thick" : "standard"), fam: fam });
      });
    });
    document.addEventListener("pd:variant", sync);
    sync();
  })();

  /* ---------------- blog email-capture popup v10 (Simon's reader-offer redesign, 2026-08-24) ----------------
     Spec history: docs/superpowers/specs/2026-08-15-blog-popup-design.md. Markup in
     snippets/pd-blogpop.liquid, rendered only on blog/article templates. ONE centered modal on
     every viewport: the v2-v9 desktop split / bottom sheet / swipe / teaser machinery is retired.
     Triggers (Simon's clickable 2026-08-24, first pageview): 8s dwell OR 60% consumed scroll
     settled by a ~400ms idle, whichever first; never over the consent banner, cart drawer,
     search drawer or mobile nav (3s re-check, ~60s budget); email-UTM arrivals and subscribed
     browsers never see it; a scroll-locked body is never read for depth. Suppression: submit or
     any newsletter signup on this browser = never again (re-checked at fire time); dismissal =
     30 days elapsed-time. Steps: OFFER then CODE. CODE opens code-neutral ("Check your inbox.",
     true for everyone incl. the no-code returning-customer welcome branch); when the vend
     CODE state is now ALWAYS neutral: the vend was retired 2026-09-19 (Simon), the n8n
     webhook is deactivated, and no code is ever revealed on screen. The 15% still reaches
     blog subscribers by email, via Klaviyo's own welcome_promo_15_blog coupon keyed on the
     signup_source property set below, which is how it always worked. No dead UI, no false
     promise (the v9 lesson, twice).
     Klaviyo pipe + invariants unchanged (ARCH 7.2): client subscribe, list WbLVYp,
     custom_source + signup_source "blog-popup-v1", success UI only on 2xx, honeypot. */
  (function () {
    var root = document.getElementById("pd-blogpop"); if (!root) return;
    var SRC = "blog-popup-v1";
    var K_DONE = "pd_bpop_done", K_SUB = "pd_nl_sub", K_X = "pd_bpop_x";
    // storage access throws in some private/embedded contexts; a popup is never worth an exception
    function lsGet(k) { try { return localStorage.getItem(k); } catch (e) { return null; } }
    function lsSet(k, v) { try { localStorage.setItem(k, v); } catch (e) {} }
    var ev = function (n, p) { if (window.pdEvent) window.pdEvent(n, p); };
    if (lsGet(K_DONE) || lsGet(K_SUB)) return;                    // submitted / subscribed on this browser: never again
    if (/[?&]utm_medium=email/.test(location.search)) return;     // email-click arrivals are already on the list
    var xAt = parseInt(lsGet(K_X) || "0", 10) || 0;
    if (xAt && (Date.now() - xAt) < 2592000000) return;           // 30-day dismissal window (elapsed-time; no teaser in v10)
    // Re-home to <body>: main{z-index:3} creates a stacking context that would paint the fixed
    // overlay under the header and cart drawer (same trap + fix as the stand-fit popup above).
    if (root.parentElement !== document.body) document.body.appendChild(root);
    var offer = $("pd-bpop-offer"), done = $("pd-bpop-done");
    var form = $("pd-bpop-form"), email = $("pd-bpop-email"), btn = $("pd-bpop-btn"), err = $("pd-bpop-msg");
    var voucher = $("pd-bpop-voucher"), codeEl = $("pd-bpop-code"), copyBtn = $("pd-bpop-copy");
    var tap = $("pd-bpop-tap"), fine = $("pd-bpop-fine"), doneline = $("pd-bpop-doneline");
    if (!offer || !done || !form || !email || !btn || !voucher || !codeEl || !copyBtn || !tap || !doneline) return;
    var MQ = window.matchMedia ? matchMedia("(max-width: 760px)") : { matches: false };
    var isOpen = false, _untrap = null, _ret = null, _hideT = null, _submitted = false;
    function subscribedNow() { return !!(lsGet(K_DONE) || lsGet(K_SUB)); }
    function overlayBlocked() {
      // hard blockers first, in their own query: a comma list returns the FIRST match in DOCUMENT
      // order, so a lingering display:none consent node must never shadow an open drawer (review fix)
      if (document.querySelector("#pd-cartwrap.open, #pd-search.open, #pd-mobnav.open")) return true;
      // consent banners: require BOTH a real box and visible computed style (2026-08-17 hardening)
      var nodes = document.querySelectorAll("#shopify-pc__banner, .pandectes-root, #pandectes-banner, .cc-window, #CybotCookiebotDialog");
      for (var i = 0; i < nodes.length; i++) {
        var cs = window.getComputedStyle(nodes[i]);
        if (cs.display === "none" || cs.visibility === "hidden" || parseFloat(cs.opacity) < 0.05) continue;
        var r = nodes[i].getBoundingClientRect();
        if (r.width > 0 && r.height > 0) return true;
      }
      return false;
    }
    function visibleCard() { return done.hidden ? offer : done; }
    function focusX(card) {
      var xb = card.querySelector(".pd-bpop__x");
      if (xb) { try { xb.focus({ preventScroll: true }); } catch (e) { xb.focus(); } }
    }
    function openPop() {
      if (isOpen || subscribedNow()) return;
      isOpen = true; clearTimeout(_hideT);
      _ret = document.activeElement;
      root.hidden = false; root.setAttribute("aria-hidden", "false");
      document.body.classList.add("pd-bpop-open");
      lockScroll(true);
      // double rAF: let the [hidden] -> shown style land before the transition class, so the entrance runs
      requestAnimationFrame(function () { requestAnimationFrame(function () { root.classList.add("is-open"); }); });
      _untrap = trapFocus(visibleCard());
      // desktop: straight into the field; mobile: the X (raising the keyboard uninvited is hostile)
      if (!MQ.matches && done.hidden) { try { email.focus({ preventScroll: true }); } catch (e) {} }
      else focusX(visibleCard());
      ev("popup_view", { surface: "modal", src: SRC + "|modal" });
    }
    function closePop(method) {
      if (!isOpen) return; isOpen = false;
      root.classList.remove("is-open");
      document.body.classList.remove("pd-bpop-open");
      lockScroll(false);
      if (_untrap) { _untrap(); _untrap = null; }
      _hideT = setTimeout(function () { root.hidden = true; root.setAttribute("aria-hidden", "true"); }, REDUCE ? 0 : 220);
      if (!_submitted) {
        lsSet(K_X, String(Date.now()));
        ev("popup_dismiss", { method: method, src: SRC + "|modal|" + method });
      }
      // restore focus where the reader was; a hidden or detached target just no-ops
      if (_ret && _ret.focus) { try { _ret.focus({ preventScroll: true }); } catch (e) {} }
      _ret = null;
    }
    root.addEventListener("click", function (e) {
      if (e.target === root) { closePop("backdrop"); return; }   // the flex overlay itself is the backdrop
      var c = e.target && e.target.closest ? e.target.closest("[data-bpop-close]") : null;
      if (c) closePop(c.getAttribute("data-bpop-m") || "x");
    });
    document.addEventListener("keydown", function (e) { if (e.key === "Escape" && isOpen) closePop("esc"); });
    copyBtn.addEventListener("click", function () {
      var code = (codeEl.textContent || "").trim(); if (!code) return;
      try { navigator.clipboard.writeText(code); } catch (e) {}   // a failed copy is not worth an exception
      copyBtn.textContent = "Copied";
      setTimeout(function () { copyBtn.textContent = "Copy"; }, 1800);
      ev("popup_code_copy", { src: SRC + "|modal" });
    });
    tap.addEventListener("click", function () {
      if (tap.getAttribute("data-armed")) ev("popup_tap_apply", { src: SRC + "|modal" });
    });
    form.addEventListener("submit", function (e) {
      e.preventDefault();
      if (!email.checkValidity()) { email.reportValidity(); email.setAttribute("aria-invalid", "true"); return; }
      email.removeAttribute("aria-invalid");
      function showDone() {
        offer.hidden = true; done.hidden = false;
        if (_untrap) _untrap();
        _untrap = trapFocus(done);
        focusX(done);
      }
      var hp = form.querySelector('input[name="pd-bpop-extra"]');
      if (hp && hp.value) { showDone(); return; }                 // honeypot: swallow bots, send nothing
      btn.disabled = true; btn.textContent = "Sending...";
      if (err) err.hidden = true;
      fetch("https://a.klaviyo.com/client/subscriptions/?company_id=XLk4Bd", {
        method: "POST",
        headers: { "Content-Type": "application/json", "revision": "2026-07-15" },
        body: JSON.stringify({ data: { type: "subscription",
          attributes: { custom_source: SRC,
            // signup_source PROFILE property is what the Welcome Flow's 15% logic keys on:
            // custom_source only reaches consent records, which flows cannot filter
            profile: { data: { type: "profile", attributes: { email: email.value,
              properties: { signup_source: SRC },
              subscriptions: { email: { marketing: { consent: "SUBSCRIBED" } } } } } } },
          relationships: { list: { data: { type: "list", id: "WbLVYp" } } } } })
      }).then(function (r) {
        if (!r.ok) throw new Error("klaviyo " + r.status);        // success UI ONLY on 2xx (07-02 postmortem rule)
        _submitted = true; lsSet(K_DONE, "1"); lsSet(K_SUB, "1");
        showDone();
        ev("newsletter_signup", { source: SRC });
        ev("popup_submit", { surface: "modal", src: SRC + "|modal" });
        // 2026-09-19 VEND RETIRED (Simon). The blog popup no longer calls the n8n
        // popup-vend webhook; the n8n workflow is deactivated and the endpoint 404s.
        // WHY: the webhook was public and unauthenticated, and its only gate was a CORS
        // allowedOrigins setting, which constrains browsers and nothing else. Two
        // unauthenticated POSTs (the public Klaviyo subscribe above, then the vend) minted
        // a single-use 15% code for any address the caller did not own, and the on-screen
        // reveal is what made that worth doing. appliesOncePerCustomer bounds each CODE,
        // never the COUNT of codes, which is where the 2026-08-24 reveal ruling went wrong.
        // NOTHING IS LOST: verified 2026-09-19 that the welcome email NEVER carried the
        // minted code. Welcome E1 renders Klaviyo's own coupon_code tag for
        // welcome_promo_15_blog, selected by the signup_source property the subscribe call
        // above already sets, so the 15% still reaches subscribers by email exactly as
        // before. popup_code on the Klaviyo profile was written but read by nothing.
        // The done card keeps its neutral "Check your inbox." state, true for everyone.
        // Retired with this: data-vend, popup_vend_ok / popup_vend_fail, popup_tap_offer.
        // FOLLOW-UP: the voucher / code / tap markup in the section is now permanently
        // hidden dead DOM and can come out in a separate, DOM-only push.
      }).catch(function () {
        btn.disabled = false; btn.textContent = "Get the code";
        if (err) err.hidden = false;
      });
    });
    /* ---- triggers: first pageview; 8s dwell OR 60% consumed scroll settled ~400ms; guarded ---- */
    var fired = false, tries = 0, dwellOK = false, depthOK = false, _idleT = null;
    function tryFire() {
      if (fired || isOpen || !(dwellOK || depthOK)) return;
      if (subscribedNow()) { fired = true; return; }              // a footer signup this pageview wins
      if (document.hidden) return;                                // wait for the tab to be visible again
      if (overlayBlocked()) { if (++tries <= 20) setTimeout(tryFire, 3000); return; }
      fired = true;
      window.removeEventListener("scroll", onScroll);
      openPop();
    }
    var _preloaded = false;
    function preloadImgs() {
      // the hero is lazy so blog pageviews stay light; the first gate latch means an open is
      // likely within seconds, so flip it eager and the card never paints empty
      if (_preloaded) return; _preloaded = true;
      var imgs = root.querySelectorAll('img[loading="lazy"]');
      for (var i = 0; i < imgs.length; i++) imgs[i].loading = "eager";
    }
    var onScroll = function () {
      // a scroll-locked body clamps pageYOffset and collapses scrollHeight, and the clamp itself
      // fires a scroll event: never read depth through a lock (review-era fix, kept)
      if (document.body.style.position === "fixed") return;
      clearTimeout(_idleT);
      _idleT = setTimeout(function () {                           // fire on scroll IDLE, never mid-scroll (mockup rule)
        var span = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
        if (window.pageYOffset > 0 && window.pageYOffset / span >= 0.6) { depthOK = true; preloadImgs(); tryFire(); }
      }, 400);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    setTimeout(function () { dwellOK = true; preloadImgs(); tryFire(); }, 8000);
    document.addEventListener("visibilitychange", function () { if (!document.hidden) tryFire(); });
  })();
})();

/* ---------------- product-page interactive 360 Prism hero (drag to spin) ---------------- */
(function () {
  var root = document.getElementById("pd-prismhero");
  if (!root) return;
  var cv = root.querySelector(".pd-prismhero__cv"), poster = root.querySelector(".pd-prismhero__poster");
  var hint = root.querySelector(".pd-prismhero__hint");
  // Tap-to-Load (Simon 2026-07-02): on mobile the heavy frame set (~2.3MB) is deferred behind an
  // explicit tap instead of eager-loading on view. Desktop keeps the eager auto-spin (the signature
  // wow, affordable there). The poster is already painted, so deferring the frames is CLS-safe.
  var tapBtn = root.querySelector(".pd-prism__tap"), retryBtn = root.querySelector(".pd-prism__retry");
  var liveEl = root.querySelector(".pd-prismhero__live");
  // Gate only where the pill exists (the PDP hero). The About-page / other prisms carry no pill, so
  // TAP_GATE is false there and they keep their existing in-view load, unchanged.
  var TAP_GATE = !!tapBtn && !!(window.matchMedia && window.matchMedia("(max-width: 760px)").matches);
  var loadTimer = 0, activated = false;
  function setLoadState(s) { root.setAttribute("data-360-state", s); }
  function announce(msg) { if (liveEl) liveEl.textContent = msg; }
  // R2: Shopify assets are flat + per-file hashed, so the frames can't be addressed as
  // data-base + zero-padded index. The template emits data-frame-urls = a JSON array of the
  // (240) hashed asset URLs instead; scrub through that array (poster = frame 000).
  // Variant-aware (Simon 2026-06-21): data-frame-sets = {"style|cap": [240 urls]} holds every spin
  // set this product offers; data-frame-default names the set for the variant shown on load. The buy
  // engine fires "pd:variant" with the selected option values; applyVariant() swaps the active set.
  // Values with no 3D model (Nano style, Color tints) call showFallback() -> the product photo
  // (data-fallback-img).
  // Simon 2026-06-25 (360 Tier-2): frames now arrive as a compact base URL per set (the _c000 frame) + a count,
  // not 240 full URLs each (that baked ~140KB of JSON into every PDP). Rebuild each set's array by swapping the
  // zero-padded index into the base. Falls back to the legacy data-frame-sets/urls arrays so the About-page
  // prism (still emitting the old format) keeps working.
  var FRAME_COUNT = parseInt(root.getAttribute("data-frame-count"), 10) || 0;
  // Simon 2026-10-29 (carry case): "reverse the direction both of the single and 6 display case spins when you drag".
  // The case sets were rendered turning the other way from every other set, so drag, fling, arrows AND autoplay all
  // ran opposite to the rest of the site. data-frame-dir="-1" (main-product.liquid, is_case360) reverses the frame
  // ORDER of each set after decimate (frame 0, the poster, stays first; the loop wraps, so 0 -> last is one step).
  // No motion maths changes: the 10-25 jolt fix, DRAG_SENS, fling and the arrow step are untouched.
  var DIR = root.getAttribute("data-frame-dir") === "-1" ? -1 : 1;
  function orient(u) { return (DIR < 0 && u && u.length > 1) ? [u[0]].concat(u.slice(1).reverse()) : u; }   // applied to BOTH the initial set and every setFrames() swap
  function pad3(n) { n = String(n); return n.length >= 3 ? n : ("000" + n).slice(-3); }
  function expand(base) { if (!base || !FRAME_COUNT) return []; var o = []; for (var i = 0; i < FRAME_COUNT; i++) o.push(base.replace(/_c000\./, "_c" + pad3(i) + ".")); return o; }
  var SETS = {};
  var SETBASES = {}; try { SETBASES = JSON.parse(root.getAttribute("data-frame-setbases")) || {}; } catch (e) {}
  if (Object.keys(SETBASES).length) { for (var _k in SETBASES) { if (SETBASES.hasOwnProperty(_k)) SETS[_k] = expand(SETBASES[_k]); } }
  else { try { SETS = JSON.parse(root.getAttribute("data-frame-sets")) || {}; } catch (e) { SETS = {}; } }
  var FALLBACK = root.getAttribute("data-fallback-img") || "";
  var DEFAULT_KEY = root.getAttribute("data-frame-default") || "";
  var URLS = [];
  var _base = root.getAttribute("data-frame-base");
  if (_base && FRAME_COUNT) { URLS = expand(_base); }
  else { try { URLS = JSON.parse(root.getAttribute("data-frame-urls")) || []; } catch (e) { URLS = []; } }
  var curKey = null;
  if (SETS[DEFAULT_KEY] && SETS[DEFAULT_KEY].length) { URLS = SETS[DEFAULT_KEY]; curKey = DEFAULT_KEY; }
  // Alex 2026-06-24 (mobile = black screen, desktop = 30-40s to load): the spin set is 240 frames @
  // 800x1000 (~9.7MB, and ~770MB if all decode at once). That overruns phone memory (the canvas blanks),
  // and on a fast 5G connection the connection-based LITE gate never fires so it eager-loads the lot.
  // Cap how many frames we actually load - a light subset on phones / low-memory devices, a fuller set on
  // desktop - sampled evenly so the spin still covers a full 360. The poster (frame 0) is the never-black
  // fallback. Drag/fling/autoplay pace is rescaled to the kept count by tune() so the feel is unchanged.
  var FRAME_CAP = ((window.matchMedia && window.matchMedia("(max-width: 760px)").matches) || (navigator.deviceMemory && navigator.deviceMemory <= 4)) ? 120 : 240;   // Simon 2026-06-25 (Tier-2): WebP frames (~17KB) make a denser set affordable -> ~3deg steps mobile / ~1.5deg desktop (was 6deg/3deg), far smoother. ~2MB mobile / ~4.2MB desktop, still lighter than the old 60/120 JPG set.
  function decimate(urls) {
    if (!urls) return [];
    if (urls.length <= FRAME_CAP) return urls.slice();
    // Simon 2026-10-28 (review): keep an integer stride so every kept step is the same size. The carry case's 432-frame
    // sets take 216 on desktop and 108 on phones (the rounded sampling mixed one- and two-frame steps); the 240-frame
    // sets keep 240 and 120 exactly as before. A count with no useful divisor falls back to the rounded sampling.
    var keep = FRAME_CAP; while (keep > 1 && urls.length % keep) keep--;
    if (keep * 2 < FRAME_CAP) { keep = FRAME_CAP; var stepR = urls.length / keep, outR = []; for (var r = 0; r < keep; r++) outR.push(urls[Math.min(urls.length - 1, Math.round(r * stepR))]); return outR; }
    var step = urls.length / keep, out = [];
    for (var i = 0; i < keep; i++) out.push(urls[i * step]);
    return out;
  }
  URLS = orient(decimate(URLS));
  var N = URLS.length;
  if (!cv || !N) return;
  // Shared connection-aware flags (PERF-002/006). On slow nets or mobile we keep the
  // already-rendered poster and load the heavy frame set lazily, off the LCP path.
  var media = window.PD_MEDIA || {};
  var SLOW = !!media.SLOW, REDUCE = !!media.REDUCE;
  var LITE = SLOW;  // gate the heavy frame preload on save-data / slow-connection only. A viewport-based
  // gate (poster-only on all mobile) destabilized the frame-hero height under throttled measurement
  // (CLS regression), so normal mobile keeps the baseline-stable eager load; the full mobile-payload
  // win needs a CLS-safe reserved-height hero (deferred).
  var ctx = cv.getContext("2d");
  var imgs = new Array(N), edgeCache = new Array(N), ready = 0, started = false, loadStarted = false, noModel = false;
  // Simon 2026-06-22: zoom the render to fill the frame ONLY on the PDP gallery (.pd-gal__hero--prism).
  // The About-page prism (#pd-prismhero in a storyrow) keeps 1:1 so it is unaffected.
  // Simon 2026-06-28: lowered 1.18 -> 1.10. At 1.18 the visible window was ~85% of the frame, which could
  // not show both the Rounded base (sits flush at the frame's bottom edge, ~0.2% margin, measured) AND the
  // taller Prism display top (13.2% from top, measured) at any single crop. 1.10 widens the window to ~91%
  // so both fit; the crop is then biased to the bottom (VBIAS) so the empty top headroom absorbs it.
  var ZOOM = (root.closest && root.closest(".pd-gal__hero--prism")) ? 1.10 : 1;
  // Vertical crop bias: 0.5 = centred. On the zoomed PDP, bias near the bottom (0.98) so the cropped portion
  // comes off the empty top headroom and the display base stays in frame. Must match the poster's CSS
  // transform-origin Y% (mockup-pdp.css): for any ZOOM, origin-Y% == VBIAS keeps poster + canvas aligned.
  // ZOOM==1 (About page) makes h==cssH so VBIAS has no effect there.
  // Simon 2026-10-03: a page can move the anchor with data-frame-vbias (0..1). The Magneto renders lift
  // the lid above the 98% crop line while their base leaves room below, so those two PDPs anchor at
  // 0.75. main-product.liquid writes the same figure into the poster's inline transform-origin.
  var VB = parseFloat(root.getAttribute("data-frame-vbias"));
  var VBIAS = (ZOOM > 1) ? ((VB >= 0 && VB <= 1) ? VB : 0.98) : 0.5;
  // Simon 2026-10-20 ("Use them as rendered"): the BGS slab sets sit about 95 px higher in the frame than the PSA
  // ones and their lid peaks at rows 56 to 63 of 1000, above the 75% crop line (row 68), so a "|bgs" key anchors at
  // data-frame-vbias-bgs (0.55: crop rows 50 to 959) and every other key keeps the page anchor. setFrames() writes the
  // same figure into the poster's transform-origin so poster and canvas stay aligned; Liquid seeds the landing one.
  var VB_PAGE = VBIAS, VB_BGS = parseFloat(root.getAttribute("data-frame-vbias-bgs"));
  function vbiasFor(key) { return (ZOOM > 1 && VB_BGS >= 0 && VB_BGS <= 1 && /\|bgs$/.test(key || "")) ? VB_BGS : VB_PAGE; }
  VBIAS = vbiasFor(curKey);

  // PERF-002/006: do NOT eager-load the full frame set on slow/mobile. On fast desktop
  // keep current behavior but mark the fetches low priority so they yield to the LCP.
  function loadFrames() {
    if (loadStarted) return; loadStarted = true;
    var errCount = 0;
    for (var c = 0; c < N; c++) {
      (function (c) {
        var im = new Image();
        if ("fetchPriority" in im) im.fetchPriority = "low";
        im.onload = function () { if (++ready === 1) { begin(); onFirstFrame(); } };
        im.onerror = function () { if (++errCount === N && ready === 0) onLoadError(); };   // every frame errored, nothing decoded -> fail
        im.src = URLS[c];
        imgs[c] = im;
      })(c);
    }
  }
  // Reduced-motion: paint a single static frame, never autoplay (PERF-005).
  function loadStatic() {
    if (loadStarted) return; loadStarted = true;
    var im = new Image();
    if ("fetchPriority" in im) im.fetchPriority = "low";
    im.onload = function () { if (++ready === 1) { begin(); onFirstFrame(); } };   // close out the tap state so the pill never lingers under reduced motion
    im.src = URLS[0];
    imgs[0] = im;
  }
  function trigger() { if (noModel || loadStarted) return; if (REDUCE) loadStatic(); else loadFrames(); }
  // Tap-to-Load state machine (mobile). activate() is the only entry that loads frames under the gate;
  // onFirstFrame / onLoadError close the loading state. Desktop never calls these (state attr stays cosmetic).
  function activate() {
    if (activated || noModel) return; activated = true;
    if (loadStarted) { setLoadState("ready"); return; }   // already loaded via another path
    setLoadState("loading"); announce("Loading 360 view");
    clearTimeout(loadTimer);
    loadTimer = setTimeout(function () {
      if (root.getAttribute("data-360-state") === "loading") { activated = false; setLoadState("failed"); announce("360 view failed to load"); }
    }, 8000);
    trigger();
  }
  function onFirstFrame() { clearTimeout(loadTimer); if (TAP_GATE) { setLoadState("ready"); if (!REDUCE) announce("360 view ready. Drag or use arrow keys to rotate."); } }
  function onLoadError() { clearTimeout(loadTimer); activated = false; if (TAP_GATE) { setLoadState("failed"); announce("360 view failed to load"); } }

  var pos = 0, vel = 0, dragging = false, autoplay = true, lastX = 0, lastT = 0, resumeT = 0, last = 0, phist = [];   /* AUDIT-24 W2 F10: release-velocity sample ring */
  // S2/2.2.2: a deliberate pause must stay paused. userPaused gates the auto-resume branches below.
  var userPaused = false, pauseBtn = root.querySelector(".pd-prism__pause"), pauseTxt = pauseBtn ? pauseBtn.querySelector(".pd-prism__pause-txt") : null;
  var AUTO_FPS = 14, DRAG_SENS = 0.34, VEL_MAX = 110, FRICTION = 1.8, cssW = 0, cssH = 0;   // Simon 2026-06-25: softer friction -> longer eased glide on fling
  // keep the drag-to-degrees ratio, fling cap and autoplay pace consistent regardless of how many frames
  // we kept (fewer frames = each frame is more degrees), referenced to the original 240-frame tuning.
  // Simon 2026-06-28: the triple sets render a display ~2.5-3x wider than the single, so at the SAME angular
  // speed (all sets are 240 frames -> 27deg/s on desktop) the triple's outer edges sweep far more pixels per
  // second and read as faster + steppier than the single. Slow the triple auto-rotate to ~0.7x so the
  // perceived edge-speed and stepping come down to match the single. Single sets are unchanged. curKey is set
  // before tune() runs (at init and inside each setFrames() swap), so this tracks the live Single<->Triple
  // toggle on multi-capacity products (e.g. Ultra). Drag/fling stay 1:1 so manual spinning stays responsive.
  function tune() { var f = N / 240; var triScale = (curKey && curKey.indexOf("triple") !== -1) ? 0.7 : 1;
    DRAG_SENS = 0.34 * f; VEL_MAX = 110 * f; AUTO_FPS = Math.max(14, 18 * f) * triScale; }   // Simon 2026-06-25: higher floor so the auto-rotate reads smooth, not stepped
  tune();
  var acv = document.createElement("canvas"); acv.width = 1; acv.height = 1;
  var actx = acv.getContext("2d", { willReadFrequently: true });
  function edge(im, sx, sy, sw, sh) { actx.clearRect(0, 0, 1, 1); actx.drawImage(im, sx, sy, sw, sh, 0, 0, 1, 1);
    var d = actx.getImageData(0, 0, 1, 1).data; return "rgb(" + d[0] + "," + d[1] + "," + d[2] + ")"; }
  // PERF-015: the 4 edge colors are constant per frame; compute once per index and reuse.
  function edges(im, i) {
    var c = edgeCache[i]; if (c) return c;
    var iw = im.width, ih = im.height, band = Math.max(2, Math.round(ih * 0.02));
    c = edgeCache[i] = { l: edge(im, 0, 0, band, ih), r: edge(im, iw - band, 0, band, ih),
      t: edge(im, 0, 0, iw, band), b: edge(im, 0, ih - band, iw, band) };
    return c;
  }
  function resize() { var r = root.getBoundingClientRect(), dpr = Math.min(window.devicePixelRatio || 1, 2);
    cssW = r.width; cssH = r.height; readPosterFit();
    cv.width = Math.max(1, Math.round(cssW * dpr)); cv.height = Math.max(1, Math.round(cssH * dpr));
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0); draw(); }
  // Simon 2026-06-25 (Tier-2): never show a blank/stuck frame while the set is still loading. If the target
  // frame is not decoded yet, draw the nearest already-loaded frame instead (kills the pop-in stutter).
  // Simon 2026-10-20 (staging harness, magneto-color / ultra-color under load): a frame the browser marked BROKEN
  // (request dropped under load, decode failure) reports complete=true with naturalWidth 0. drawImage() on it throws
  // InvalidStateError inside loop() before the next requestAnimationFrame is scheduled, freezing the spin on that
  // frame until the next variant pick or resize. Treat a broken frame exactly like an unloaded one.
  function loaded(im) { return !!im && im.complete && im.naturalWidth > 0; }
  function nearestLoaded(i) { if (loaded(imgs[i])) return imgs[i]; for (var d = 1; d < N; d++) { var a = imgs[(i - d + N) % N]; if (loaded(a)) return a; var b = imgs[(i + d) % N]; if (loaded(b)) return b; } return null; }
  // Simon 2026-10-25 ("the first few frames ... looks quite jolty", desk stand; every spin page had it): the frames are
  // requested all at once and arrive in any order, but the loop starts on the FIRST one in and autoplay kept advancing,
  // so draw() filled each gap with the nearest loaded frame and the spin leapt about and ran backwards (phone, desk
  // stand: 0 120 114 58 62 64 106 102 ...) until the set finished. Autoplay now never steps onto a frame that has not
  // arrived (it waits on the current one), and while it waits it keeps showing the last frame it drew (none yet: the
  // poster, which is this set's frame 0, shows through). A frame that failed is complete, so it is passed over, never
  // waited on. A frame the shopper put on screen (drag, fling, arrow keys) keeps the nearest-frame stand-in, as before.
  function pending(i) { var im = imgs[((i % N) + N) % N]; return !im || !im.complete; }
  function autoStep(p, step) { var np = p + step; for (var j = Math.round(p) + 1; j <= Math.round(np); j++) if (pending(j)) return p; return np; }
  var lastIm = null, lastI = 0;   // the last frame draw() put on the canvas for the current set (setFrames clears it)
  // Simon 2026-10-25: the first canvas frame must land exactly where the still poster's image sits, or the display jumps
  // the moment the spin takes over. The phone grid layout (pd-layout-a.css, 2026-10-13) shows the poster object-fit: cover
  // at 50% 55% while draw() always contain-fitted, so on every display page on a phone frame 0 came in 3.9% smaller and
  // 8 px / 11 px off (live magneto, Pixel 7). draw() now copies the poster's object-fit and object-position (read on each
  // resize) and scales by ZOOM about the poster's transform-origin (centre, VBIAS). Contain at 50% 50% in a 4:5 box
  // (desktop, and accessory pages on phones) is the same maths as before.
  var FIT_COVER = false, POS_X = 0.5, POS_Y = 0.5;
  function readPosterFit() {
    if (!poster || !window.getComputedStyle) return;
    try { var cs = getComputedStyle(poster), op = String(cs.objectPosition || "").split(" ");
      FIT_COVER = cs.objectFit === "cover";
      POS_X = /%$/.test(op[0] || "") ? parseFloat(op[0]) / 100 : 0.5; POS_Y = /%$/.test(op[1] || "") ? parseFloat(op[1]) / 100 : 0.5;
      if (!isFinite(POS_X)) POS_X = 0.5; if (!isFinite(POS_Y)) POS_Y = 0.5;   // e.g. calc(50% + 4px) splits into "calc(50%": never draw at NaN
    } catch (e) {}
  }
  function draw() { var i = ((Math.round(pos) % N) + N) % N, im;
    if (pending(i) && autoplay && !dragging && Math.abs(vel) <= 0.02) { im = lastIm; i = lastI; }   // autoplay waits on a frame still loading
    else im = nearestLoaded(i);
    if (!cssW) return;
    if (!im) { ctx.clearRect(0, 0, cssW, cssH); return; }   // nothing of this set to show yet: the poster (its frame 0) shows through
    lastIm = im; lastI = i;
    ctx.clearRect(0, 0, cssW, cssH);
    var s0 = (FIT_COVER ? Math.max : Math.min)(cssW / im.width, cssH / im.height), w0 = im.width * s0, h0 = im.height * s0;
    var ox = cssW / 2, oy = cssH * VBIAS, w = w0 * ZOOM, h = h0 * ZOOM;   // Simon 2026-06-22: ZOOM (1.10 on the PDP) fills the frame like the cropped product photos; the poster matches via its CSS scale about the same origin; .pd-prismhero overflow:hidden clips the spill
    var ec = edges(im, i);
    var dx = ox + ((cssW - w0) * POS_X - ox) * ZOOM, dy = oy + ((cssH - h0) * POS_Y - oy) * ZOOM, by = dy + h, rx = dx + w;
    if (dx > 0.5) { ctx.fillStyle = ec.l; ctx.fillRect(0, 0, Math.ceil(dx) + 1, cssH); }
    if (rx < cssW - 0.5) { ctx.fillStyle = ec.r; ctx.fillRect(Math.floor(rx) - 1, 0, cssW - rx + 2, cssH); }
    if (dy > 0.5) { ctx.fillStyle = ec.t; ctx.fillRect(dx - 1, 0, w + 2, Math.ceil(dy) + 1); }
    if (by < cssH - 0.5) { ctx.fillStyle = ec.b; ctx.fillRect(dx - 1, Math.floor(by) - 1, w + 2, cssH - by + 2); }
    ctx.drawImage(im, dx, dy, w, h);
    // Alex 2026-06-24: the poster (frame 0) stays VISIBLE behind the canvas (was faded to 0 after the first
    // draw). A fully-drawn frame is opaque and fully covers it, so it is invisible while the spin runs, but
    // if a frame is still loading / was evicted under memory pressure the product photo shows instead of black.
  }
  var raf = 0, inView = false;
  function loop(t) {
    raf = 0;
    if (noModel) return;
    var dt = last ? Math.min((t - last) / 1000, 0.05) : 0; last = t;
    if (!dragging) {
      if (Math.abs(vel) > 0.02) { pos += vel * dt; vel -= vel * Math.min(1, FRICTION * dt); }
      else if (autoplay) { pos = autoStep(pos, AUTO_FPS * dt); }
      else if (!userPaused && resumeT && t > resumeT) { autoplay = true; }
    }
    pos = ((pos % N) + N) % N; draw();
    if (inView) raf = requestAnimationFrame(loop);  // PERF-005: stop when off-screen
  }
  // PERF-005: reduced-motion paints one static frame and never spins.
  function startLoop() { if (REDUCE) { draw(); return; } if (!raf && inView) { last = 0; raf = requestAnimationFrame(loop); } }
  function begin() { if (started) return; started = true; if (poster) poster.style.transition = "opacity .25s"; resize(); startLoop(); }
  window.addEventListener("resize", function () { if (started) resize(); }, { passive: true });
  root.addEventListener("pointerdown", function (e) { if (TAP_GATE && !activated) return; trigger(); dragging = true; autoplay = false; vel = 0; lastX = e.clientX; lastT = performance.now(); phist = [{ t: lastT, x: e.clientX }]; resumeT = 0; root.classList.add("is-spun"); if (root.setPointerCapture) { try { root.setPointerCapture(e.pointerId); } catch (_) {} } });
  root.addEventListener("pointerenter", function () { if (!TAP_GATE) trigger(); }, { passive: true });
  root.addEventListener("pointermove", function (e) { if (!dragging) return; var now = performance.now(), dx = e.clientX - lastX; lastX = e.clientX; lastT = now; pos -= dx * DRAG_SENS; phist.push({ t: now, x: e.clientX }); if (phist.length > 6) phist.shift(); }, { passive: true });
  function rel() {
    if (!dragging) return; dragging = false;
    // AUDIT-24 W2 F10: velocity over the last ~100ms window, zeroed if the pointer sat still >80ms -
    // the old single-sample dx/dt threw the model ~70-90 degrees on a hold-then-release. Tracking
    // (pos -= dx) is untouched; FRICTION/VEL_MAX/DRAG_SENS unchanged - only the handed-off value.
    var tNow = performance.now(), b = phist[phist.length - 1] || null, a = null;
    for (var i = phist.length - 1; i >= 0; i--) { if (tNow - phist[i].t > 100) break; a = phist[i]; }
    vel = (a && b && b.t > a.t && (tNow - b.t) < 80) ? -((b.x - a.x) / ((b.t - a.t) / 1000)) * DRAG_SENS : 0;
    vel = Math.max(-VEL_MAX, Math.min(VEL_MAX, vel));
    resumeT = userPaused ? 0 : performance.now() + 2200; setValueNow();
  }
  root.addEventListener("pointerup", rel, { passive: true });
  root.addEventListener("pointercancel", rel, { passive: true });
  // W8 code-audit L6: keyboard rotation (the 360 is the only product image).
  root.tabIndex = 0;
  root.addEventListener("keydown", function (e) {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();
    if (TAP_GATE && !activated) { activate(); return; }   // first arrow key loads; subsequent keys rotate
    trigger(); begin(); autoplay = false; resumeT = 0;
    var kstep = Math.max(1, Math.round(N / 24));
    pos = ((pos + (e.key === "ArrowLeft" ? -kstep : kstep)) % N + N) % N;
    root.classList.add("is-spun"); draw(); setValueNow();
  });
  // Tap-to-Load controls (mobile). The pill loads the set; retry re-arms after a failed load.
  if (tapBtn) tapBtn.addEventListener("click", function (e) { e.stopPropagation(); activate(); });
  if (retryBtn) retryBtn.addEventListener("click", function (e) {
    e.stopPropagation();
    loadStarted = false; ready = 0; activated = false;
    imgs = new Array(N); edgeCache = new Array(N);   // drop the broken Image handles so nearestLoaded stays clean
    activate();
  });

  // M1/4.1.2: reflect the current rotation as a slider value (degrees 0..359) for assistive tech.
  // Updated on key/drag steps only (the primary AT path), never every animation frame.
  function setValueNow() {
    if (!N) return;
    var i = ((Math.round(pos) % N) + N) % N;
    root.setAttribute("aria-valuenow", Math.round((i / N) * 359));
  }
  // S2/2.2.2: a persistent Pause/Play toggle. The pause is sticky (guards the auto-resume branches).
  if (pauseBtn) {
    // keep a pause click from engaging the hero's drag/spin pointer logic
    pauseBtn.addEventListener("pointerdown", function (e) { e.stopPropagation(); });
    pauseBtn.addEventListener("click", function (e) {
      e.stopPropagation();
      userPaused = !userPaused;
      if (userPaused) {
        autoplay = false; resumeT = 0; vel = 0;
        pauseBtn.setAttribute("aria-pressed", "true");
        if (pauseTxt) pauseTxt.textContent = "Play spin";
      } else {
        trigger(); begin(); autoplay = true; resumeT = 0;
        pauseBtn.setAttribute("aria-pressed", "false");
        if (pauseTxt) pauseTxt.textContent = "Pause spin";
        startLoop();
      }
    });
  }

  // PERF-005 + PERF-002: one IntersectionObserver gates both the rAF loop (stop off-screen,
  // resume on re-entry) and the lazy frame load (load when the hero scrolls into view).
  function watch() {
    if ("IntersectionObserver" in window) {
      // Simon 2026-10-25 ("if i'm looking at one of the lifestyle images of a single stand and then switch to the double ... the
      // double 360 seems stuck"): setFrames() re-measures the hero, and while a photo slide is showing the hero is display:none,
      // so it measured 0 x 0, the canvas went to 1 x 1 and draw() returned on !cssW for good (live, phone and desktop, every
      // spin page on any variant change made on a photo). Measure again whenever the hero comes into view, and on any size
      // change where ResizeObserver exists. Offline test: ops/test-360-viewer.cjs ("stuck spin").
      new IntersectionObserver(function (es) {
        inView = es[0].isIntersecting;
        if (inView) { if (!TAP_GATE || activated) trigger(); if (started) { resize(); startLoop(); } }
      }, { threshold: 0 }).observe(root);
      if (window.ResizeObserver) { try { new ResizeObserver(function () { if (started) resize(); }).observe(root); } catch (e) { /* old browsers: the observer above still re-measures */ } }
    } else { inView = true; trigger(); }
  }
  // --- Variant-aware set swapping (Simon 2026-06-21) ---
  // Swap the active frame set in place: reset the frame buffers, repaint from the new poster, and
  // reload per the same connection gate. Restores the spin UI if we were in the photo fallback.
  function setFrames(urls) {
    noModel = false;
    // Simon 2026-07-26: recover the 360 when leaving a no-model variant (Nano -> Prism/Rounded, Single or
    // Triple). showFallback() writes data-360-state="fallback" and mockup-pdp.css hides .mgal__thumb--360
    // off that attribute. On MOBILE the branch below sets "idle" so it self-heals, but on DESKTOP the only
    // other writers (activate/onFirstFrame) are gated behind TAP_GATE, so the attribute stayed "fallback"
    // for the rest of the session and the 360 thumbnail never came back. Clear it here, recovery-only.
    if (root.getAttribute("data-360-state") === "fallback") setLoadState("idle");
    URLS = orient(decimate(urls)); N = URLS.length; tune();
    VBIAS = vbiasFor(curKey);   // Simon 2026-10-20: per-grader crop anchor (see vbiasFor)
    if (poster && ZOOM > 1) poster.style.transformOrigin = "center " + Math.round(VBIAS * 100) + "%";
    // Simon 2026-07-26: the 360 RAIL THUMBNAIL must preview the actual 360, not a product photo.
    // Liquid seeds it from `default_poster`, which falls back to product.featured_image whenever the
    // default variant has no 3D model (Nano), so the thumb rendered a stock photo wearing a "360" badge.
    // pd.js had ZERO references to .mgal__thumb--360, so it also never tracked a style change on ANY
    // product (e.g. thumb stuck on the Rounded render while the viewer spun Prism). URLS[0] is always
    // the _c000 frame (expand() builds from it; decimate keeps index 0) and is already being fetched
    // for the viewer, so this costs no extra request.
    var th360 = document.querySelector(".mgal__thumb--360 img");
    if (th360 && URLS[0]) th360.setAttribute("src", URLS[0]);
    // Simon 2026-10-03: drop the outgoing set's unfinished frame requests. Magneto (Color) has twelve
    // sets of 240, so a shopper clicking through the colours was queueing every abandoned set behind
    // the one they landed on. Handlers are cleared first so a cancelled frame cannot count toward the
    // new set's ready/error totals.
    for (var z = 0; z < imgs.length; z++) {
      var old = imgs[z];
      if (old && !old.complete) { old.onload = old.onerror = null; old.src = ""; }
    }
    imgs = new Array(N); edgeCache = new Array(N); lastIm = null; lastI = 0;
    ready = 0; loadStarted = false; pos = 0; vel = 0;
    if (!userPaused) autoplay = true;
    if (cv) cv.style.display = "";
    if (hint) hint.style.display = "";
    if (pauseBtn) pauseBtn.style.display = "";
    // Simon 2026-10-03: the poster alt says "360 degree view" only while the poster is a spin frame, so it is
    // written next to BOTH poster.src writers (here and showFallback). The strings are server-rendered data attributes.
    // Simon 2026-10-20: on the six slab pages the alt names the slab the render shows, so it follows the grader in the
    // key (data-alt-spin-psa / -bgs); every other page keeps its one data-alt-spin.
    if (poster) { var altA = poster.getAttribute(/\|bgs$/.test(curKey || "") ? "data-alt-spin-bgs" : "data-alt-spin-psa") || poster.getAttribute("data-alt-spin"); if (altA) poster.alt = altA; }
    if (poster) { poster.style.transition = "none"; poster.src = URLS[0] || ""; poster.style.opacity = "1";
      requestAnimationFrame(function () { if (poster) poster.style.transition = "opacity .25s"; }); }
    if (!TAP_GATE) { trigger(); }              // desktop: (re)load the new set per the connection gate
    else if (activated) { activated = false; activate(); }   // mobile, already engaged: reload + stay engaged
    else { setLoadState("idle"); }             // mobile, not yet tapped: keep poster + pill for the new variant
    if (started) { resize(); startLoop(); }    // already initialised once: repaint + resume the spin
  }
  // Photo fallback for a variant with no 3D model (Nano style, Color tints): hide the canvas/controls
  // and show the product photo (data-fallback-img) on the poster.
  function showFallback() {
    noModel = true; curKey = null; vel = 0; autoplay = false;
    clearTimeout(loadTimer); setLoadState("fallback");   // no 3D model for this variant -> hide the tap pill

    if (raf) { cancelAnimationFrame(raf); raf = 0; }
    if (cv && ctx && cssW) ctx.clearRect(0, 0, cssW, cssH);
    if (cv) cv.style.display = "none";
    if (hint) hint.style.display = "none";
    if (pauseBtn) pauseBtn.style.display = "none";
    if (poster && poster.getAttribute("data-alt-photo")) poster.alt = poster.getAttribute("data-alt-photo");
    if (poster) { poster.style.transition = "none"; poster.src = FALLBACK || poster.src; poster.style.opacity = "1"; }
    // Simon 2026-10-20: a photo after a BGS spin would otherwise keep the 55% anchor (see vbiasFor); back to the page's.
    VBIAS = VB_PAGE;
    if (poster && ZOOM > 1) poster.style.transformOrigin = "center " + Math.round(VB_PAGE * 100) + "%";
    // Simon 2026-10-25: a shopper parked on the spin who picks a variant with no spin (the Desk Stand Double until its
    // renders land) was left looking at the product's featured photo, which shows two SINGLE stands. Move the stage to
    // that variant's own photo: the gallery sync ran first and put its thumb straight after the (now hidden) 360 thumb.
    // No other spin page has a variant without a spin (checked 2026-10-25).
    var hr = document.getElementById("mgp0");
    if (hr && hr.checked && root.closest && root.closest('.mgal__slide[data-i="0"]')) {
      var nt = document.querySelector(".mgal__rail .mgal__thumb--360 + .mgal__thumb"), nr = nt && document.getElementById(nt.getAttribute("for"));
      if (nr && nt.style.display !== "none") nr.checked = true;
    }
  }
  // Map the selected option values -> a "style|cap" set key. Style ignores order; capacity defaults
  // to single. No edge style -> null (no model -> fallback).
  // Simon 2026-10-01: Nano is a modeled style now (nano-sf2, single only). The key is returned for any
  // style and applyVariant() decides off SETS, so "nano|triple", which has no set, still falls back to
  // the photo. A product with no Style option at all (the Nano PDP) names its one style in
  // data-frame-style, so every one of its variants maps to the same set.
  var FIXED_STYLE = (root.getAttribute("data-frame-style") || "").toLowerCase();
  // Simon 2026-10-03: Magneto (Color) carries one spin set per colourway, keyed "style|cap|colour".
  // data-frame-color-opt is the position of the Color option in the selection; absent on every other
  // product (NaN), so their keys stay "style|cap". A colour with no rendered set has no SETS entry and
  // applyVariant() falls back to the photo.
  var COLOR_I = parseInt(root.getAttribute("data-frame-color-opt"), 10);
  // Simon 2026-10-20: the October slab sets exist per grader. data-frame-grader-opt is the position of the
  // Authenticator option on the six slab pages (phantom-ultra, nano, phantom-x-gradedguard, magneto, magneto-color,
  // ultra-color); the key then ends in "|bgs" when that value contains BGS, else "|psa" (PSA/CGC/TAG and SGC share
  // the PSA render). Absent everywhere else (NaN), so those pages keep their keys exactly as before.
  var GRADER_I = parseInt(root.getAttribute("data-frame-grader-opt"), 10);
  // Simon 2026-10-23: Ungraded spins a different card per Size (r17 one-touch sets), so its keys carry the Size value too.
  var SIZE_I = parseInt(root.getAttribute("data-frame-size-opt"), 10);
  function keyFromSel(sel) {
    var st = "", cap = "single";
    for (var i = 0; i < sel.length; i++) {
      var v = (sel[i] || "").toLowerCase();
      if (v === "prism" || v === "rounded" || v === "nano") st = v;
      else if (v === "single" || v === "triple") cap = v;
    }
    if (!st) st = FIXED_STYLE;
    if (!st) return null;                                  // no edge style -> no 3D model
    var key = st + "|" + cap;
    if (COLOR_I >= 0 && sel[COLOR_I]) key += "|" + String(sel[COLOR_I]).toLowerCase();
    if (SIZE_I >= 0 && sel[SIZE_I]) key += "|" + String(sel[SIZE_I]).toLowerCase();
    if (GRADER_I >= 0 && sel[GRADER_I]) key += "|" + (/bgs/i.test(String(sel[GRADER_I])) ? "bgs" : "psa");
    return key;
  }
  // Simon 2026-10-20 (review finding): the pack-row test arm draws its Single / Triple option thumbnails from the
  // suffix-free alias entries of data-frame-setbases ("prism|single" ...), which Liquid points at the PSA sets. So a
  // BGS shopper in that arm would see PSA slabs on the Type cards while the hero spins BGS. On every variant change,
  // point each alias at the picked grader's set (an alias with no set for that grader keeps PSA) and write the map
  // back to the attribute; the arm re-parses it on every sheet open. SETS (this viewer's own expanded arrays) is
  // untouched: the alias stays this viewer's PSA fallback for a missing grader render.
  function syncAliases(g) {
    var changed = false;
    for (var k in SETBASES) {
      if (!SETBASES.hasOwnProperty(k) || !/\|(psa|bgs)$/.test(k)) continue;
      var al = k.replace(/\|(psa|bgs)$/, "");
      if (!SETBASES.hasOwnProperty(al)) continue;
      var want = SETBASES[al + "|" + g] || SETBASES[al + "|psa"];
      if (want && SETBASES[al] !== want) { SETBASES[al] = want; changed = true; }
    }
    if (changed) { try { root.setAttribute("data-frame-setbases", JSON.stringify(SETBASES)); } catch (e) {} }
  }
  function applyVariant(sel) {
    var key = keyFromSel(sel || []);
    // Simon 2026-10-20: a grader key with no set (a BGS render not uploaded) takes the suffix-free alias, which
    // Liquid emits for every PSA entry, before giving up on the photo.
    if (key && !(SETS[key] && SETS[key].length)) { var k2 = key.replace(/\|(psa|bgs)$/, ""); if (k2 !== key && SETS[k2] && SETS[k2].length) key = k2; }
    if (GRADER_I >= 0 && sel && sel[GRADER_I]) syncAliases(/bgs/i.test(String(sel[GRADER_I])) ? "bgs" : "psa");
    if (key && SETS[key] && SETS[key].length) {
      if (key === curKey && !noModel) return;              // already showing this set
      curKey = key; setFrames(SETS[key]);
    } else {
      showFallback();
    }
  }
  document.addEventListener("pd:variant", function (e) { if (e && e.detail && e.detail.sel) applyVariant(e.detail.sel); });

  // PERF-027: defer setup to idle so it never competes with the LCP paint.
  function init() {
    watch();
    if (!curKey && Object.keys(SETS).length) { showFallback(); return; }   // variant-aware viewer whose default variant has no 3D model -> static photo. A single-set viewer (e.g. the About-page prism, no data-frame-sets) has no SETS, so it keeps spinning.
    if (REDUCE) loadStatic();                     // static frame regardless of viewport
    else if (!LITE && !TAP_GATE) loadFrames();    // fast desktop keeps eager load (low priority)
    // TAP_GATE (mobile): wait for the pill tap / arrow key. LITE (slow): wait for in-view via watch().
  }
  (window.requestIdleCallback || function (f) { setTimeout(f, 1); })(init);
})();

/* a11y S3 / 1.4.13 Content on Hover or Focus: ESC dismisses a focused jargon tooltip.
   The .pd-term__tip shows via :focus, so blurring the trigger hides it without moving focus elsewhere. */
(function () {
  document.addEventListener("keydown", function (e) {
    if (e.key !== "Escape" && e.key !== "Esc") return;
    var el = document.activeElement;
    if (el && el.classList && el.classList.contains("pd-term") && typeof el.blur === "function") el.blur();
  });
})();

/* ---------------- PDP photo-slide swipe (Simon 2026-07-02, parity with the Tap-to-Load prototype) ----------------
   Finger-following swipe between the product PHOTO slides, with flick velocity. The 360 (slide data-i="0")
   keeps its own drag-to-rotate and is never peeked/swiped into (reach it via its thumbnail). Mobile/tablet only
   (<=980px, where the rail replaces the desktop hover model). Drives the same radios the CSS switcher uses, so
   the committed slide shows through :checked exactly as a thumbnail tap would. */
(function () {
  if (!window.PointerEvent || !window.matchMedia || !matchMedia("(max-width: 980px)").matches) return;
  var stage = document.querySelector(".pd-pdp .mgal--multi .mgal__stage");
  if (!stage) return;
  var gal = stage.closest(".mgal");
  function radios() { return gal.querySelectorAll(".mgal__r"); }
  function slideAt(i) { return stage.querySelector('.mgal__slide[data-i="' + i + '"]'); }
  function isPhoto(i) { var s = slideAt(i); return !!s && !s.querySelector(".pd-prismhero"); }
  var FIRST = isPhoto(0) ? 0 : 1;   // photos start at 0, or at 1 when slide 0 is the 360
  function activeIdx() { var r = radios(); for (var i = 0; i < r.length; i++) if (r[i].checked) return i; return FIRST; }
  function setIdx(i) { var r = radios(); i = Math.max(FIRST, Math.min(r.length - 1, i)); if (r[i]) { r[i].checked = true; r[i].dispatchEvent(new Event("change", { bubbles: true })); } }
  // Simon 2026-10-25 ("user should only be able to scroll through either single or double images only depending on which
  // type they selected in the variant picker"): the swipe took the next slide in page order whatever the variant filter
  // (filterGallery360, filterGalleryByVariant, the Liquid first-paint pre-filter) had hidden, and clearTrack() wiped every
  // slide's inline display, so one swipe un-hid them all (live phone, desk stand Obsidian Black / Single: 18 -> 19 -> 17
  // silver -> 20 silver -> 24 Double, dots 4 -> 10). Neighbours are now the previous and next SHOWN photos, and clearTrack()
  // resets only the slides this gesture moved, back to the display they had. Offline test: ops/test-pdp-swipe.cjs.
  function shownPhoto(i) { var s = slideAt(i); return !!s && !s.querySelector(".pd-prismhero") && s.style.display !== "none"; }
  function neighbour(i, step) { var n = radios().length; for (var j = i + step; j >= FIRST && j < n; j += step) if (shownPhoto(j)) return j; return -1; }
  var d = null, W = 0, placed = [];
  function place(s, x, z) {
    if (!s) return;
    var known = false; for (var k = 0; k < placed.length; k++) if (placed[k][0] === s) known = true;
    if (!known) placed.push([s, s.style.display]);
    s.style.cssText += ";display:block;position:absolute;top:0;left:0;width:100%;transition:none;z-index:" + z + ";transform:translateX(" + x + "px)";
  }
  function clearTrack() {
    stage.style.height = ""; stage.style.overflow = "";
    for (var i = 0; i < placed.length; i++) { var s = placed[i][0]; s.style.display = placed[i][1]; s.style.position = ""; s.style.top = ""; s.style.left = ""; s.style.width = ""; s.style.transform = ""; s.style.transition = ""; s.style.zIndex = ""; }
    placed = [];
  }
  var _pendingFin = null;   // round-1 blocker fix: the in-flight settle's finalizer (idempotent)
  // round-2 fix: a THUMBNAIL tap during the settle also needs the finalize-first treatment (the rail is
  // outside the stage; without this the stale fin() reverted the radio the tap just set). Capture phase
  // so it runs before the label's activation behavior; idempotent so the stage handler overlapping is free.
  document.addEventListener("pointerdown", function (e) {
    if (_pendingFin && e.target && e.target.closest && e.target.closest(".mgal")) _pendingFin();
  }, true);
  stage.addEventListener("pointerdown", function (e) {
    if (e.target.closest("#pd-prismhero")) return;        // the 360 owns its own gesture
    if (_pendingFin) _pendingFin();                        // finalize any in-flight settle NOW so the new gesture starts from clean, current state
    var cur = activeIdx();
    if (!isPhoto(cur)) return;                             // active slide is the 360 -> ignore
    var rect = stage.getBoundingClientRect(); W = rect.width || 358;
    stage.style.height = rect.height + "px"; stage.style.overflow = "hidden";   // pin height so absolute slides don't collapse the stage
    d = { x0: e.clientX, lastX: e.clientX, lastT: performance.now(), vel: 0, cur: cur, raf: 0, pending: e.clientX, moved: false, pi: neighbour(cur, -1), ni: neighbour(cur, 1) };
    if (stage.setPointerCapture) { try { stage.setPointerCapture(e.pointerId); } catch (_) {} }
  });
  stage.addEventListener("pointermove", function (e) {
    if (!d) return; d.pending = e.clientX; if (d.raf) return;
    d.raf = requestAnimationFrame(function () {
      d.raf = 0; var now = performance.now(), dt = now - d.lastT, dx = d.pending - d.lastX;
      if (dt > 0) d.vel = dx / dt; d.lastX = d.pending; d.lastT = now;
      var off = d.pending - d.x0;
      if (!d.moved && Math.abs(off) < 6) return;
      d.moved = true;
      var prev = d.pi >= 0 ? slideAt(d.pi) : null;   // the previous SHOWN photo; never peeks into the 360
      var next = d.ni >= 0 ? slideAt(d.ni) : null;
      // AUDIT-24 W2 F9: Apple rubber-band when the drag direction has no neighbour (algebraically identical
      // to Apple's (1 - 1/((x*c/d)+1))*d). With W~358 and c=.55 a full-width overdrag travels ~127px and the
      // curve asymptotes to W - resistance is felt immediately and the stage never empties. The commit test
      // in end() keeps using the RAW off; the settle springs back through this same damped value (d.lim).
      var lim = off;
      if ((off > 0 && !prev) || (off < 0 && !next)) { lim = (off * W * 0.55) / (W + 0.55 * Math.abs(off)); d.lim = lim; }
      else { d.lim = null; }
      place(slideAt(d.cur), lim, 2); place(prev, -W + lim, 1); place(next, W + lim, 1);
    });
  });
  function end(commit) {
    // AUDIT-24 W2 F8: the release no longer teleports - the slides animate to rest, inheriting the
    // finger's velocity (hard flick lands fast, lazy drag settles gently; 180-320ms on --ease-out).
    // Re-entry guard is load-bearing: pointerup's implicit capture release also fires pointerleave,
    // whose end(false) used to no-op on d===null; with d alive through the settle it would otherwise
    // start a competing settle that can land on the wrong slide.
    if (!d || d.settling) return;
    d.settling = true;
    if (d.raf) cancelAnimationFrame(d.raf);
    var off = d.pending - d.x0, thr = Math.max(32, W * 0.15), fast = Math.abs(d.vel) > 0.5, target = d.cur;
    if (commit && d.moved) {
      if ((off < -thr || (fast && d.vel < 0)) && d.ni >= 0) target = d.ni;
      else if ((off > thr || (fast && d.vel > 0)) && d.pi >= 0) target = d.pi;
    }
    var curIdx = d.cur, eff = (d.lim != null) ? d.lim : off;   // F9: spring back through the damped path
    var shift = target === d.ni ? -W : (target === d.pi ? W : 0), dist = Math.abs(shift - eff);   // one width either way, however far apart the indices
    var dur = (window.PD_MEDIA && window.PD_MEDIA.REDUCE) ? 0 : Math.max(180, Math.min(320, dist / Math.max(0.4, Math.abs(d.vel))));
    var curS = slideAt(curIdx),
        pv = d.pi >= 0 ? slideAt(d.pi) : null,
        nx = d.ni >= 0 ? slideAt(d.ni) : null;
    // Round-1 blocker fix: a NEW pointerdown during the settle must be able to finalize this settle
    // instantly and take over - fin is idempotent, cancels its own timer, is exposed via _pendingFin,
    // and only nulls `d` if it still owns it (a fresh gesture may have replaced it).
    var dd = d, moved = d.moved, done = false, tid = 0;
    var fin = function () {
      if (done) return; done = true;
      clearTimeout(tid); _pendingFin = null;
      clearTrack();                                  // MUST run before setIdx: it also releases the pinned stage height/overflow
      if (target !== curIdx) setIdx(target);
      if (d === dd) d = null;
    };
    if (!dur || !moved) { fin(); return; }
    _pendingFin = fin;
    [[curS, shift], [pv, -W + shift], [nx, W + shift]].forEach(function (p) {
      if (!p[0]) return;
      p[0].style.transition = "transform " + Math.round(dur) + "ms var(--ease-out)";
      p[0].style.transform = "translateX(" + p[1] + "px)";
    });
    if (curS) curS.addEventListener("transitionend", fin, { once: true });
    tid = setTimeout(fin, Math.round(dur) + 60);     // fallback if transitionend is swallowed
  }
  stage.addEventListener("pointerup", function () { end(true); });
  stage.addEventListener("pointercancel", function () { end(false); });
  stage.addEventListener("pointerleave", function () { end(false); });
})();


/* ---------------- PDP gallery photo warmer (AUDIT-24 F7, 2026-08-17) ----------------
   Every non-default gallery slide is loading="lazy" inside a display:none subtree
   (.mgal--multi .mgal__slide, mockup-pdp.css), so the browser NEVER fetches it: the first
   thumbnail tap / swipe on every PDP showed a bare grey frame while the photo fetched on
   demand (verified live: zero bytes until the tap). Warm the first THREE hidden slides
   after the load event, off the critical path - capped, because img_cap is lifted to the
   full gallery on non-360 displays (main-product.liquid:441; Magneto/Nano/colour PDPs
   carry 5-12 images) and an uncapped warm would also make every later variant-swap src
   rewrite download hidden photos (eager images fetch on src change even in display:none).
   Three covers the first swipes/taps, which is where the grey flash lived. Flipping
   loading to "eager" runs the lazy-load resumption steps and fires the fetch even inside
   display:none, AND lets the browser resolve srcset itself (450w on mobile) - do NOT
   preload via new Image().src, which would fetch the 900w candidate and double-download.
   No markup change: no-JS keeps native lazy-loading. Honors the save-data / slow gate. */
addEventListener("load", function () {
  if (window.PD_MEDIA && window.PD_MEDIA.SLOW) return;
  var warm = function () {
    [].slice.call(document.querySelectorAll('.mgal__slide .mgal__img[loading="lazy"]'), 0, 3).forEach(function (im) {
      im.loading = "eager";
      im.decoding = "async";
    });
  };
  (window.requestIdleCallback || function (f) { setTimeout(f, 1); })(warm);
});
